import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  DataTable,
  FormLayout,
  InlineGrid,
  InlineStack,
  Layout,
  List,
  Page,
  Select,
  Spinner,
  Tabs,
  Text,
  TextField
} from "@shopify/polaris";
import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from "react";

import {
  apiFetch,
  AdminContext,
  AdminProduct,
  AnalyticsFunnelResponse,
  ArtworkPolicy,
  AuditEvent,
  AuditResponse,
  CatalogDriftEvent,
  CatalogDriftResponse,
  ChannelExportFormat,
  ChannelFeedExport,
  ChannelFeedPreview,
  ChannelReadinessResponse,
  ConfigurationTemplate,
  ConfigurationTemplatePreview,
  FulfillmentRulesResponse,
  Kpis,
  LaunchReadinessResponse,
  OperationsHealth,
  OrderLifecycleItem,
  OrderLifecycleResponse,
  PriceListRow,
  ProductParityConfig,
  RetentionStatus,
  ReviewItem,
  ReviewQueueResponse,
  SetupConfig,
  SetupStatus,
  ShopifyCollection,
  StoreSummary,
  SupplierCatalogProduct,
  SupplierConnection,
  SyncJob,
  SyncJobsResponse
} from "./api";
import { MetricCard } from "./components/MetricCard";
import { StatusBadge } from "./components/StatusBadge";
import {
  customInstallShopDomain,
  isCustomDistributionMode,
  setupConfigForDistributionSave
} from "./distributionMode";
import {
  buildProductConfiguratorThemeEditorUrl,
  configuredShopifyPublicApiKey
} from "./themeEditor";

type NavItem = { label: string; to: string; type?: never } | { type: "divider"; label?: never; to?: never };

const navItems: NavItem[] = [
  { label: "Dashboard", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Orders", to: "/orders" },
  { label: "Channels", to: "/channels" },
  { type: "divider" as const },
  { label: "Jobs", to: "/jobs" },
  { label: "Operations", to: "/operations" },
  { label: "Analytics", to: "/analytics" },
  { label: "Audit", to: "/audit" },
  { label: "Settings", to: "/settings" }
];

const legacyAdminRoutes: Record<string, string> = {
  "/stores": "/settings?tab=setup&section=stores",
  "/suppliers": "/settings?tab=setup&section=suppliers",
  "/launch": "/settings?tab=setup&section=launch",
  "/pricing": "/settings?tab=pricing",
  "/delivery": "/settings?tab=delivery",
  "/artwork": "/settings?tab=artwork",
  "/setup": "/settings?tab=setup"
};

function browserLocationSnapshot() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function subscribeToBrowserLocation(onChange: () => void) {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
}

function useBrowserLocation() {
  const href = useSyncExternalStore(
    subscribeToBrowserLocation,
    browserLocationSnapshot,
    () => "/"
  );
  return useMemo(() => new URL(href, window.location.origin), [href]);
}

function navigateWithinAdmin(
  event: ReactMouseEvent<HTMLAnchorElement>,
  destination: string
) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();
  window.history.pushState(null, "", destination);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function navigateToAdmin(destination: string) {
  window.history.pushState(null, "", destination);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const defaultParityConfig = {
  delivery: {
    enabled_service_levels: ["Saver", "Standard", "Express"],
    date_padding_days: 1,
    same_day_cutoff_hour: 11,
    same_day_cutoff_timezone: "Europe/London",
    allowed_modes: ["ship_to_customer"]
  },
  artwork: {
    required: true,
    allow_upload_later: true,
    artwork_service: "Just Print",
    allowed_mime_types: ["application/pdf"],
    max_file_size_mb: 100
  },
  branding: {
    accent_color: "#166534",
    grid_header_color: "#111827",
    button_color: "#166534",
    border_radius_px: 6,
    hide_tradeprint_branding: true
  }
};

export function App() {
  const location = useBrowserLocation();
  const legacyDestination = legacyAdminRoutes[location.pathname];
  const activeLocation = useMemo(
    () => new URL(legacyDestination || location.href, window.location.origin),
    [legacyDestination, location.href]
  );
  useEffect(() => {
    if (!legacyDestination) return;
    window.history.replaceState(null, "", legacyDestination);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [legacyDestination]);
  useEffect(() => {
    const main = document.getElementById("admin-content");
    if (!main) return;
    const updateScrollableTables = () => {
      main.querySelectorAll<HTMLElement>(".Polaris-DataTable__ScrollContainer").forEach((container) => {
        const scrollable =
          container.scrollWidth > container.clientWidth ||
          container.scrollHeight > container.clientHeight;
        if (scrollable) {
          container.tabIndex = 0;
          container.dataset.adminScrollable = "true";
        } else if (container.dataset.adminScrollable === "true") {
          container.removeAttribute("tabindex");
          delete container.dataset.adminScrollable;
        }
      });
    };
    const frame = window.requestAnimationFrame(updateScrollableTables);
    const observer = new MutationObserver(updateScrollableTables);
    observer.observe(main, { childList: true, subtree: true });
    window.addEventListener("resize", updateScrollableTables);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", updateScrollableTables);
    };
  }, [activeLocation.pathname]);
  const { data: context } = useApiData<AdminContext>("/shopify/admin/context", {
    organization: {
      id: "pending",
      name: "Organisation",
      billing_mode: "free",
      billing_status: "not_configured"
    },
    shop: {
      id: "pending",
      organization_id: "pending",
      shop_domain: "Loading store",
      store_key: "pending",
      install_status: "unknown",
      app_proxy_status: "unknown",
      theme_extension_status: "unknown",
      billing_status: "not_configured",
      protected_customer_data_status: "needs_review"
    },
    suppliers: []
  });

  return (
    <div className="admin-shell">
      <a className="admin-skip-link" href="#admin-content">
        Skip to main content
      </a>
      <aside className="admin-sidebar" aria-label="Print connector navigation">
        <div className="admin-sidebar__brand">
          <Text as="h1" variant="headingMd">
            Print Connector
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Catalog, channels, orders
          </Text>
        </div>
        <nav>
          {navItems.map((item) => {
            if (item.type === "divider") {
              return (
              <div key="primary-divider" className="admin-sidebar__divider" />
              );
            }
            const current =
              item.to === "/"
                ? activeLocation.pathname === "/"
                : activeLocation.pathname.startsWith(item.to);
            return (
              <a
                key={item.to}
                href={item.to}
                aria-current={current ? "page" : undefined}
                onClick={(event) => navigateWithinAdmin(event, item.to)}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
      </aside>
      <main id="admin-content" className="admin-main" tabIndex={-1}>
        <ContextBar context={context} />
        {activeLocation.pathname === "/" ? <DashboardView /> : null}
        {activeLocation.pathname === "/products" ? <ProductsView /> : null}
        {activeLocation.pathname === "/channels" ? <ChannelsView /> : null}
        {activeLocation.pathname === "/settings" ? (
          <SettingsView context={context} />
        ) : null}
        {activeLocation.pathname === "/orders" ? <OrdersView /> : null}
        {activeLocation.pathname === "/jobs" ? <JobsView /> : null}
        {activeLocation.pathname === "/operations" ? <OperationsView /> : null}
        {activeLocation.pathname === "/analytics" ? <AnalyticsView /> : null}
        {activeLocation.pathname === "/audit" ? <AuditView /> : null}
      </main>
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="admin-loading-state" role="status" aria-live="polite">
      <Spinner accessibilityLabel={label} size="small" />
      <Text as="span" tone="subdued">
        {label}
      </Text>
    </div>
  );
}

function ContextBar({ context }: { context: AdminContext }) {
  const supplierSummary =
    context.shop.id === "pending"
      ? "Loading"
      : context.suppliers.length
        ? `${context.suppliers.length} configured`
        : "None configured";
  const contextItems = [
    { label: "Organisation", value: context.organization.name },
    { label: "Active store", value: context.shop.shop_domain },
    { label: "Suppliers", value: supplierSummary }
  ];
  return (
    <div className="context-bar">
      {contextItems.map((item) => (
        <BlockStack key={item.label} gap="100">
          <Text as="span" variant="bodySm" tone="subdued">
            {item.label}
          </Text>
          <Text as="span" variant="bodyMd" fontWeight="semibold">
            {item.value}
          </Text>
        </BlockStack>
      ))}
    </div>
  );
}

function DashboardView() {
  const {
    data: operations,
    loading: operationsLoading,
    error: operationsError,
    reload: reloadOperations
  } = useApiData<OperationsHealth>("/shopify/admin/operations/health", {
    generated_at: "",
    store_key: "pending",
    status: "healthy",
    score: 100,
    recommendations: [],
    supplier_controls: {
      tradeprint: {
        supplier_key: "tradeprint",
        enabled: true,
        submit_only_manual: false,
        degraded: false
      }
    },
    suppliers: [],
    queues: [],
    recent_jobs: [],
    manual_reviews: [],
    risks: [],
    risk_summary: {
      action_required: 0,
      items: 0,
      by_severity: {},
      by_category: {},
      margin_threshold_percent: 20,
      quote_recovery_warning_count: 5
    },
    kpis: {},
    retention: {
      store_key: "pending",
      dry_run: true,
      generated_at: "",
      policy: {},
      cutoffs: {},
      counts: {},
      total: 0
    },
    audit_events: [],
    alerting: {
      configured: false,
      destination: "platform_dashboard",
      external_webhook_configured: false
    }
  });
  const {
    data: setup,
    loading: setupLoading,
    error: setupError,
    reload: reloadSetup
  } = useApiData<SetupStatus>("/shopify/admin/setup/status", {
    backend: "error",
    shopify_session: "not_verified",
    shopify_admin_api: "missing",
    supplier_credentials: "missing",
    app_proxy: "missing",
    webhook_secret: "missing",
    embedded_app_url: null
  });

  const setupChecks = [
    setup.backend === "ready",
    setup.shopify_session === "verified",
    setup.shopify_admin_api === "connected",
    setup.supplier_credentials === "configured",
    setup.app_proxy === "configured",
    setup.webhook_secret === "configured",
    setup.billing?.active === true || setup.billing_mode === "free",
    setup.protected_customer_data === "acknowledged"
  ];
  const setupIssues = setupChecks.filter((complete) => !complete).length;
  const failedJobs = operations.recent_jobs.filter((job) => job.status === "failed").length;
  const failedQueueJobs = operations.queues.reduce(
    (total, queue) => total + Number(queue.failed_job_registry_count || 0),
    0
  );
  const openReviews = operations.manual_reviews.length;
  const staleOrders = Number(operations.kpis.stale_order_count || 0);
  const fulfilmentGaps = Number(operations.kpis.fulfillment_writeback_pending_count || 0);
  const actionCount = setupIssues + operations.risk_summary.action_required;
  const refresh = () => {
    void reloadOperations();
    void reloadSetup();
  };

  return (
    <Page
      title="Dashboard"
      subtitle="What needs attention across products, orders, channels, and operations"
      primaryAction={{ content: "Refresh", onAction: refresh }}
    >
      <BlockStack gap="400">
        {operationsLoading || setupLoading ? <LoadingState label="Loading dashboard" /> : null}
        <DismissibleNotice
          message={operationsError ? `Operational information could not be loaded: ${operationsError}` : null}
        />
        <DismissibleNotice
          message={setupError ? `Setup information could not be loaded: ${setupError}` : null}
        />
        {!setupLoading && setupIssues ? (
          <Banner tone="warning">
            <BlockStack gap="200">
              <Text as="p">
                {setupIssues} setup {setupIssues === 1 ? "check needs" : "checks need"} attention
                before PrintConnect is fully ready.
              </Text>
              <InlineStack>
                <Button onClick={() => navigateToAdmin("/settings?tab=setup")}>
                  Review setup
                </Button>
              </InlineStack>
            </BlockStack>
          </Banner>
        ) : null}
        {!operationsLoading ? (
          <Banner
            tone={
              operations.status === "critical"
                ? "critical"
                : operations.status === "warning"
                  ? "warning"
                  : "success"
            }
          >
            {actionCount
              ? `${actionCount} ${actionCount === 1 ? "item requires" : "items require"} attention.`
              : "No immediate administrator action is required."}
          </Banner>
        ) : null}
        <InlineGrid columns={{ xs: 1, md: 4 }} gap="400">
          <MetricCard
            label="Orders needing attention"
            value={String(openReviews + staleOrders)}
            trend={openReviews + staleOrders ? "Review" : "Clear"}
          />
          <MetricCard
            label="Failed jobs"
            value={String(failedJobs + failedQueueJobs)}
            trend={failedJobs + failedQueueJobs ? "Retry" : "Clear"}
          />
          <MetricCard
            label="Fulfilment gaps"
            value={String(fulfilmentGaps)}
            trend={fulfilmentGaps ? "Check tracking" : "Clear"}
          />
          <MetricCard
            label="Setup checks"
            value={`${setupChecks.length - setupIssues}/${setupChecks.length}`}
            trend={setupIssues ? "Incomplete" : "Complete"}
          />
        </InlineGrid>
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Next actions
            </Text>
            <DataTable
              columnContentTypes={["text", "numeric", "text"]}
              headings={["Area", "Items", "Action"]}
              rows={[
                [
                  "Orders and manual review",
                  openReviews + staleOrders,
                  <Button onClick={() => navigateToAdmin("/orders")}>Review orders</Button>
                ],
                [
                  "Product and background jobs",
                  failedJobs + failedQueueJobs,
                  <Button onClick={() => navigateToAdmin("/jobs")}>Review jobs</Button>
                ],
                [
                  "Operational health",
                  fulfilmentGaps,
                  <Button onClick={() => navigateToAdmin("/operations")}>
                    Review operations
                  </Button>
                ],
                [
                  "Store setup",
                  setupIssues,
                  <Button onClick={() => navigateToAdmin("/settings?tab=setup")}>
                    Review settings
                  </Button>
                ]
              ]}
            />
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingMd">
                Recent jobs
              </Text>
              <Button variant="plain" onClick={() => navigateToAdmin("/jobs")}>
                View all jobs
              </Button>
            </InlineStack>
            <DataTable
              columnContentTypes={["text", "text", "text"]}
              headings={["Job", "Status", "Updated"]}
              rows={
                operations.recent_jobs.length
                  ? operations.recent_jobs.slice(0, 5).map((job) => [
                      statusLabel(job.job_type),
                      <StatusBadge status={job.status} />,
                      job.updated_at || "Not recorded"
                    ])
                  : [["No recent jobs", "", ""]]
              }
            />
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function useApiData<T>(path: string, fallback: T) {
  const [data, setData] = useState<T>(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSequence = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    const result = await apiFetch<T>(path, { signal: controller.signal });
    if (controller.signal.aborted || sequence !== requestSequence.current) return;
    setLoading(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (result.data) {
      setData(result.data);
      setError(null);
    }
  }, [path]);

  useEffect(() => {
    void load();
    return () => {
      requestSequence.current += 1;
      controllerRef.current?.abort();
    };
  }, [load]);

  return { data, setData, loading, error, reload: load };
}

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);

  return debounced;
}

function bannerToneFor(message: string | null): "success" | "warning" | "critical" {
  if (!message) return "success";
  const lower = message.toLowerCase();
  if (
    lower.includes("failed") ||
    lower.includes("error") ||
    lower.includes("unavailable") ||
    lower.includes("could not") ||
    lower.includes("blocked") ||
    lower.includes("returned 0") ||
    lower.includes("no products")
  ) {
    return "critical";
  }
  if (
    lower.includes("manual") ||
    lower.includes("review") ||
    lower.includes("not submitted") ||
    lower.includes("mismatch") ||
    lower.includes("skipped")
  ) {
    return "warning";
  }
  return "success";
}

function AdminMessageBanner({
  message,
  onDismiss
}: {
  message: string | null;
  onDismiss: () => void;
}) {
  if (!message) return null;
  return (
    <Banner tone={bannerToneFor(message)} onDismiss={onDismiss}>
      {message}
    </Banner>
  );
}

function DismissibleNotice({
  message,
  tone = "warning",
  actionLabel,
  onAction
}: {
  message: string | null | undefined;
  tone?: "info" | "warning" | "critical" | "success";
  actionLabel?: string;
  onAction?: () => void;
}) {
  const [dismissedMessage, setDismissedMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!message) setDismissedMessage(null);
  }, [message]);
  if (!message || dismissedMessage === message) return null;
  return (
    <Banner tone={tone} onDismiss={() => setDismissedMessage(message)}>
      {message}
      {actionLabel && onAction ? (
        <>
          {" "}
          <Button variant="plain" onClick={onAction}>
            {actionLabel}
          </Button>
        </>
      ) : null}
    </Banner>
  );
}

function statusLabel(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function setupConfigForSave(config: SetupConfig, distributionMode: unknown): SetupConfig {
  const embeddedUrl = (config.shopify_embedded_app_url || "").trim();
  const next = setupConfigForDistributionSave(
    {
      ...config,
      shopify_embedded_app_url:
        embeddedUrl && !/^https?:\/\//i.test(embeddedUrl) ? `https://${embeddedUrl}` : embeddedUrl
    },
    distributionMode
  );
  (["shopify_api_secret", "shopify_webhook_secret", "tradeprint_api_key", "tradeprint_password", "pencarrie_api_token"] as const).forEach(
    (key) => {
      const value = String(next[key] || "").trim();
      if (!value || value === "configured" || value.includes("...")) delete next[key];
    }
  );
  return next;
}

function cleanCollectionList(collections: string[]): string[] {
  return collections
    .map((collection) => String(collection || "").trim())
    .filter(Boolean)
    .filter((collection, index, all) => all.findIndex((item) => item.toLowerCase() === collection.toLowerCase()) === index);
}

type ImpactSizeOption = { label: string; width_mm: number; height_mm: number };
type MultiSelectOption = { label: string; value: string; description?: string };
type ImpactAssignmentDraft = {
  sizes?: ImpactSizeOption[];
  substrates?: string[];
  minimumPricingEnabled?: string;
  logoCustomisationEnabled?: boolean;
  antiGraffitiOverlaminateEnabled?: boolean;
};
type ImpactOptionTemplate = {
  name: string;
  sizes?: ImpactSizeOption[];
  substrates?: string[];
};

const DEFAULT_IMPACT_SIZES: ImpactSizeOption[] = [
  { label: "A5", width_mm: 148, height_mm: 210 },
  { label: "A4", width_mm: 210, height_mm: 297 },
  { label: "A3", width_mm: 297, height_mm: 420 },
  { label: "A2", width_mm: 420, height_mm: 594 }
];

const DEFAULT_IMPACT_SUBSTRATES = [
  "Dibond",
  "Correx",
  "Foamex",
  "Self Adhesive Vinyl",
  "Photoluminescent Vinyl",
  "Retroreflective RA1 Vinyl"
];

function impactSizeFromLabel(label: string): ImpactSizeOption {
  const match = DEFAULT_IMPACT_SIZES.find((size) => size.label.toLowerCase() === label.trim().toLowerCase());
  return match || { label: label.trim(), width_mm: 210, height_mm: 297 };
}

function parseImpactSizes(value: unknown): ImpactSizeOption[] {
  let source = value;
  if (typeof source === "string") {
    const raw = source.trim();
    if (!raw) return [];
    try {
      source = JSON.parse(raw);
    } catch {
      const hasSizeRows = /(^|\r?\n)\s*[^,\n]+,\s*\d+(?:\.\d+)?,\s*\d+(?:\.\d+)?/.test(raw);
      if (raw.includes("\n") || hasSizeRows) {
        return impactSizesFromText(raw);
      }
      source = raw.split(/\r?\n|,/).map((line) => line.trim()).filter(Boolean);
    }
  }
  if (!Array.isArray(source)) return [];
  const sizes = source
    .map((item) => {
      if (item && typeof item === "object") {
        const record = item as Record<string, unknown>;
        const label = String(record.label || record.name || "").trim();
        const width = Number(record.width_mm || 0);
        const height = Number(record.height_mm || 0);
        return label && width > 0 && height > 0 ? { label, width_mm: width, height_mm: height } : null;
      }
      return impactSizeFromLabel(String(item || ""));
    })
    .filter((item): item is ImpactSizeOption => Boolean(item?.label));
  return [...new Map(sizes.map((size) => [size.label, size])).values()];
}

function parseImpactSubstrates(value: unknown): string[] {
  let source = value;
  if (typeof source === "string") {
    const raw = source.trim();
    if (!raw) return [];
    try {
      source = JSON.parse(raw);
    } catch {
      source = raw.split(/\r?\n|,/).map((line) => line.trim()).filter(Boolean);
    }
  }
  if (!Array.isArray(source)) return [];
  return [...new Set(source.map((item) => String(item || "").trim()).filter(Boolean))];
}

function MultiSelectDropdown({
  label,
  summary,
  options,
  selectedValues,
  disabled,
  filterPlaceholder,
  onChange
}: {
  label: string;
  summary: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  disabled?: boolean;
  filterPlaceholder?: string;
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const panelId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const selectedSet = new Set(selectedValues);
  const filteredOptions = options.filter((option) => {
    const query = filter.trim().toLowerCase();
    if (!query) return true;
    return [option.label, option.value, option.description]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query);
  });
  const toggleValue = (value: string, checked: boolean) => {
    const next = checked
      ? [...selectedValues, value]
      : selectedValues.filter((item) => item !== value);
    onChange([...new Set(next)]);
  };
  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    setFilter("");
    if (restoreFocus) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const focusFrame = window.requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    });
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      close(true);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, open]);

  return (
    <div ref={containerRef} className="admin-multiselect">
      <Text as="p" variant="bodySm" fontWeight="semibold">
        {label}
      </Text>
      <Button
        disclosure={open ? "up" : "down"}
        disabled={disabled}
        ariaExpanded={open}
        ariaControls={panelId}
        onClick={() => {
          triggerRef.current =
            containerRef.current?.querySelector<HTMLButtonElement>("button") ||
            (document.activeElement instanceof HTMLElement ? document.activeElement : null);
          if (open) {
            close(false);
            return;
          }
          setOpen(true);
        }}
      >
        {summary}
      </Button>
      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          className="admin-multiselect__panel"
          role="group"
          aria-label={`${label} options`}
        >
          <TextField
            label={`${label} filter`}
            labelHidden
            value={filter}
            autoComplete="off"
            placeholder={filterPlaceholder || "Filter options"}
            disabled={disabled}
            onChange={setFilter}
          />
          <div className="admin-multiselect__options">
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={
                    option.description
                      ? `${option.label} (${option.description})`
                      : option.label
                  }
                  checked={selectedSet.has(option.value)}
                  disabled={disabled}
                  onChange={(checked) => toggleValue(option.value, checked)}
                />
              ))
            ) : (
              <Text as="p" tone="subdued">
                No options match this filter.
              </Text>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function truthyConfigValue(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return ["1", "true", "yes", "on", "enabled"].includes(value.trim().toLowerCase());
  return Boolean(value);
}

function impactSizesText(sizes: unknown): string {
  return parseImpactSizes(sizes).map((size) => `${size.label},${size.width_mm},${size.height_mm}`).join("\n");
}

function impactSubstratesText(substrates: unknown): string {
  return parseImpactSubstrates(substrates).join("\n");
}

function impactSizeLineToOption(line: string): ImpactSizeOption | null {
  const parts = line.split(",").map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return null;
  if (parts.length >= 3) {
    const width = Number(parts[1]);
    const height = Number(parts[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { label: parts[0], width_mm: width, height_mm: height };
    }
  }
  return impactSizeFromLabel(parts[0]);
}

function impactSizesFromText(raw: string): ImpactSizeOption[] {
  return [...new Map(raw.split(/\r?\n/).map(impactSizeLineToOption).filter((item): item is ImpactSizeOption => Boolean(item)).map((size) => [size.label, size])).values()];
}

function parseImpactOptionTemplates(value: unknown): ImpactOptionTemplate[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item): ImpactOptionTemplate | null => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const name = String(record.name || record.label || record.title || "").trim();
      const sizes = parseImpactSizes(record.sizes);
      const substrates = parseImpactSubstrates(record.substrates || record.materials);
      if (!name || (!sizes.length && !substrates.length)) return null;
      return { name, sizes, substrates };
    })
    .filter((item): item is ImpactOptionTemplate => Boolean(item));
}

function cleanImpactOptionTemplates(templates: ImpactOptionTemplate[]): ImpactOptionTemplate[] {
  return templates
    .map((template): ImpactOptionTemplate | null => {
      const name = String(template.name || "").trim();
      const sizes = parseImpactSizes(template.sizes);
      const substrates = parseImpactSubstrates(template.substrates);
      if (!name || (!sizes.length && !substrates.length)) return null;
      return { name, sizes, substrates };
    })
    .filter((template): template is ImpactOptionTemplate => Boolean(template));
}

function parseInHouseOptionSchema(raw: string): Array<Record<string, unknown>> {
  const schema: Array<Record<string, unknown>> = [];
  raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const [name, valuesRaw = ""] = line.split(":");
      const label = String(name || "").trim();
      const values = valuesRaw
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      if (label && values.length) {
        schema.push({ id: label, label, values, required: true });
      }
    });
  return schema;
}

type InHouseOptionDraft = {
  id: string;
  name: string;
  values: string;
};

type InHousePriceDraft = {
  id: string;
  optionValues: Record<string, string>;
  quantity: string;
  saver: string;
  standard: string;
  express: string;
};

const IN_HOUSE_CREATION_STEPS = [
  { label: "Product details", shortLabel: "Details" },
  { label: "Print options", shortLabel: "Options" },
  { label: "Quantities and prices", shortLabel: "Pricing" },
  { label: "Review and create", shortLabel: "Review" }
];

let inHouseDraftSequence = 0;

function nextInHouseDraftId(prefix: string): string {
  inHouseDraftSequence += 1;
  return `${prefix}-${inHouseDraftSequence}`;
}

function initialInHouseOptionDrafts(): InHouseOptionDraft[] {
  return [
    {
      id: nextInHouseDraftId("option"),
      name: "",
      values: ""
    }
  ];
}

function emptyInHousePriceDraft(
  values: Partial<Omit<InHousePriceDraft, "id">> = {}
): InHousePriceDraft {
  return {
    id: nextInHouseDraftId("price"),
    optionValues: {},
    quantity: "",
    saver: "",
    standard: "",
    express: "",
    ...values
  };
}

function initialInHousePriceDrafts(): InHousePriceDraft[] {
  return [emptyInHousePriceDraft()];
}

function inHouseOptionSchemaFromDrafts(
  drafts: InHouseOptionDraft[]
): Array<Record<string, unknown>> {
  return drafts.flatMap((draft) => {
    const name = draft.name.trim();
    const values = cleanStringValues(draft.values.split(","));
    return name && values.length
      ? [{ id: name, label: name, values, required: true }]
      : [];
  });
}

function inHouseOptionSchemaText(drafts: InHouseOptionDraft[]): string {
  return drafts
    .map((draft) => `${draft.name.trim()}: ${cleanStringValues(draft.values.split(",")).join(", ")}`)
    .filter((line) => !line.startsWith(":"))
    .join("\n");
}

function inHousePriceRowsFromDrafts(
  drafts: InHousePriceDraft[]
): Array<Record<string, unknown>> {
  return drafts.flatMap((draft) => {
    const quantity = Number(draft.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) return [];
    const row: Record<string, unknown> = { quantity };
    const productionData = Object.fromEntries(
      Object.entries(draft.optionValues)
        .map(([key, value]) => [key.trim(), value.trim()])
        .filter(([key, value]) => Boolean(key && value))
    );
    if (Object.keys(productionData).length) row.production_data = productionData;
    (
      [
        ["Saver", draft.saver],
        ["Standard", draft.standard],
        ["Express", draft.express]
      ] as const
    ).forEach(([service, rawValue]) => {
      const price = Number(rawValue);
      if (Number.isFinite(price) && price > 0) row[service] = price;
    });
    return Object.keys(row).length > 1 ? [row] : [];
  });
}

function inHousePriceDraftsFromRows(
  rows: Array<Record<string, unknown>>
): InHousePriceDraft[] {
  return rows.map((row) =>
    emptyInHousePriceDraft({
      optionValues:
        row.production_data && typeof row.production_data === "object"
          ? Object.fromEntries(
              Object.entries(row.production_data as Record<string, unknown>).map(
                ([key, value]) => [key, String(value)]
              )
            )
          : {},
      quantity: String(row.quantity || ""),
      saver: row.Saver == null ? "" : String(row.Saver),
      standard: row.Standard == null ? "" : String(row.Standard),
      express: row.Express == null ? "" : String(row.Express)
    })
  );
}

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

function parseInHousePriceCsv(raw: string): Array<Record<string, unknown>> {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]).map((header) => header.trim());
  const headerKeys = headers.map((header) => header.toLowerCase());
  const quantityIndex = headerKeys.findIndex((header) => ["quantity", "qty"].includes(header));
  const serviceIndexes = {
    Saver: headerKeys.findIndex((header) => ["saver", "saver price", "saver_price"].includes(header)),
    Standard: headerKeys.findIndex((header) => ["standard", "standard price", "standard_price"].includes(header)),
    Express: headerKeys.findIndex((header) => ["express", "express price", "express_price"].includes(header))
  };
  const optionIndexes = headers
    .map((header, index) => {
      const match = header.match(/^option:\s*(.+)$/i) || header.match(/^production[_ ]data:\s*(.+)$/i);
      return match ? { index, label: match[1].trim() } : null;
    })
    .filter((item): item is { index: number; label: string } => Boolean(item?.label));
  if (quantityIndex < 0) return [];
  return lines
    .slice(1)
    .map((line) => {
      const cells = splitCsvLine(line);
      const quantity = Number(cells[quantityIndex]);
      if (!Number.isFinite(quantity) || quantity <= 0) return null;
      const row: Record<string, unknown> = { quantity };
      const productionData = optionIndexes.reduce<Record<string, string>>((data, option) => {
        const value = String(cells[option.index] || "").trim();
        if (value) data[option.label] = value;
        return data;
      }, {});
      if (Object.keys(productionData).length) row.production_data = productionData;
      Object.entries(serviceIndexes).forEach(([service, index]) => {
        if (index < 0) return;
        const value = Number(cells[index]);
        if (Number.isFinite(value) && value > 0) row[service] = value;
      });
      return Object.keys(row).length > 1 ? row : null;
    })
    .filter((item): item is Record<string, unknown> => Boolean(item));
}

function inHouseSampleCsv(optionSchemaRaw: string): string {
  const optionLabels = parseInHouseOptionSchema(optionSchemaRaw)
    .map((option) => String(option.label || option.id || "").trim())
    .filter(Boolean);
  const headers = ["Quantity", "Saver", "Standard", "Express", ...optionLabels.map((label) => `Option: ${label}`)];
  const firstValues = optionLabels.map((label) => (label.toLowerCase().includes("size") ? "A6" : label.toLowerCase().includes("paper") ? "170gsm Silk" : "Default"));
  const rows = [
    headers.join(","),
    ["25", "12.00", "15.00", "20.00", ...firstValues].join(","),
    ["50", "18.00", "21.00", "26.00", ...firstValues].join(",")
  ];
  return rows.join("\n");
}

function optionSchemaTextFromRules(rules: unknown): string {
  if (!Array.isArray(rules)) return "";
  return rules
    .map((rule) => {
      if (!rule || typeof rule !== "object") return "";
      const item = rule as Record<string, unknown>;
      const label = String(item.label || item.id || item.attribute || "").trim();
      const values = Array.isArray(item.values) ? item.values.map((value) => String(value)).filter(Boolean) : [];
      return label && values.length ? `${label}: ${values.join(", ")}` : "";
    })
    .filter(Boolean)
    .join("\n");
}

type VisibleProductOptionRule = {
  key: string;
  attribute: string;
  label: string;
  values: string[];
};

function cleanStringValues(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .map((item) => {
      if (item && typeof item === "object") {
        const record = item as Record<string, unknown>;
        return String(record.value ?? record.label ?? record.name ?? record.id ?? "").trim();
      }
      return String(item ?? "").trim();
    })
    .filter((item) => {
      if (!item || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

function visibleProductOptionRules(config: ProductParityConfig | undefined): VisibleProductOptionRule[] {
  if (!Array.isArray(config?.rules)) return [];
  return config.rules.flatMap((rule, index) => {
    if (!rule || typeof rule !== "object") return [];
    const attribute = String(rule.attribute || rule.name || rule.id || rule.label || "").trim();
    const label = String(rule.label || attribute).trim();
    const hiddenValues = new Set(cleanStringValues(rule.hidden_values));
    const values = cleanStringValues(rule.values).filter((value) => !hiddenValues.has(value));
    if (!attribute || !values.length) return [];
    return [
      {
        key: `${index}:${attribute}`,
        attribute,
        label: label || attribute,
        values
      }
    ];
  });
}

function productEnabledServiceLevels(product: AdminProduct): string[] {
  const configured = cleanStringValues(product.selection?.parity_config?.delivery?.enabled_service_levels);
  if (configured.length) return configured;
  return productSupplierTab(product) === "impactconnect" ? ["Standard"] : ["Saver", "Standard", "Express"];
}

function csvDataUrl(value: string): string {
  return `data:text/csv;charset=utf-8,${encodeURIComponent(value)}`;
}

function productSourceLabel(source: string | undefined): string {
  if (source === "supplier_api") return "Supplier API";
  if (source === "in_house") return "In-house print";
  return "Imported Products v2";
}

function nestedStringValue(source: Record<string, unknown> | undefined, path: string[]): string {
  let current: unknown = source;
  for (const key of path) {
    if (!current || typeof current !== "object") return "";
    current = (current as Record<string, unknown>)[key];
  }
  if (typeof current === "boolean") return current ? "true" : "false";
  return current == null ? "" : String(current);
}

const productSupplierTabs = [
  { id: "tradeprint", content: "Tradeprint", panelID: "products-tradeprint" },
  { id: "impactconnect", content: "Impact signs", panelID: "products-impactconnect" },
  { id: "in_house", content: "In-house print", panelID: "products-in-house" }
];

type SupplierImportResult = {
  imported: number;
  variant_count?: number;
  run?: SupplierImportRun;
};

type CatalogQualityIssue = {
  count?: number;
  product_ids?: string[];
};

type ImpactCatalogQuality = {
  status?: "ready" | "needs_review" | "blocked";
  issue_count?: number;
  missing_image?: CatalogQualityIssue;
  missing_seo?: CatalogQualityIssue;
  missing_category?: CatalogQualityIssue;
  incomplete_material_pricing?: CatalogQualityIssue;
};

type SupplierImportRun = {
  imported_count?: number;
  variant_count?: number;
  archived_count?: number;
  source_summary?: {
    source?: string;
    raw_product_count?: number;
    product_count?: number;
    imported_product_count?: number;
    variant_count?: number;
    store_key?: string;
    error?: string;
    duplicate_supplier_product_id_count?: number;
    duplicate_supplier_product_ids?: string[];
    skipped_count?: number;
    catalog_quality?: ImpactCatalogQuality;
  };
  completed_at?: string | null;
};

function supplierImportMessage(
  supplierKey: string,
  displayName: string,
  result: { data: SupplierImportResult | null; error: string | null }
): string {
  if (result.error) return result.error;
  const imported = result.data?.imported ?? 0;
  const summary = result.data?.run?.source_summary;
  if (summary?.error) return summary.error;
  const sourceDetail = summary?.source ? ` from ${summary.source}` : "";
  const countDetail =
    summary?.product_count !== undefined && summary.product_count !== imported
      ? ` (${summary.product_count} generated catalogue products)`
      : "";
  if (
    supplierKey === "impactconnect" &&
    summary?.catalog_quality?.status &&
    summary.catalog_quality.status !== "ready"
  ) {
    const issueCount = summary.catalog_quality.issue_count ?? 0;
    const qualityStatus = summary.catalog_quality.status === "blocked" ? "blocked" : "needs review";
    return `Imported ${imported} ${displayName} products${countDetail}${sourceDetail}; catalogue quality ${qualityStatus} (${issueCount} issues)`;
  }
  if (supplierKey === "impactconnect" && imported === 0) {
    return `Impact catalogue import returned 0 products${sourceDetail || " from the configured static catalogue"}`;
  }
  return `Imported ${imported} ${displayName} products${countDetail}${sourceDetail}`;
}

function normaliseSupplierTabKey(value: string | null | undefined): string {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!key) return "";
  if (["impact", "impact_sign", "impact_signs", "impact_connect", "impactconnect"].includes(key)) return "impactconnect";
  if (["in_house", "inhouse", "in_house_print", "custom_print"].includes(key)) return "in_house";
  if (["pen_carrie", "pencarrie"].includes(key)) return "pencarrie";
  if (["trade_print", "tradeprint"].includes(key)) return "tradeprint";
  return key;
}

function productSupplierTab(product: AdminProduct): string {
  const explicitSupplier = normaliseSupplierTabKey(product.supplier_key);
  if (explicitSupplier) return explicitSupplier;
  const selection = product.selection;
  const productId = String(product.id || selection?.product_id || "");
  const parityConfig = selection?.parity_config || {};
  const configuredSource =
    nestedStringValue(parityConfig, ["source"]) ||
    nestedStringValue(parityConfig, ["custom_print", "source"]) ||
    nestedStringValue(parityConfig, ["supplier", "key"]) ||
    nestedStringValue(parityConfig, ["supplier_key"]);
  const searchText = [
    productId,
    product.name,
    product.title,
    product.catalog_source,
    selection?.category,
    selection?.product_type,
    selection?.product_group_title,
    selection?.canonical_handle,
    configuredSource
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (normaliseSupplierTabKey(product.catalog_source) === "in_house" || searchText.includes("in_house") || searchText.includes("in-house")) {
    return "in_house";
  }
  if (productId.toUpperCase().startsWith("VO-HS-") || searchText.includes("impactconnect") || searchText.includes("impact")) {
    return "impactconnect";
  }
  if (searchText.includes("pencarrie")) {
    return "pencarrie";
  }
  return "tradeprint";
}

const CHANNEL_FIELD_PATHS: Record<string, string[]> = {
  google_product_category: ["channel_metadata", "google_merchant", "google_product_category"],
  identifier_policy: ["channel_metadata", "google_merchant", "identifier_policy"],
  amazon_category: ["channel_metadata", "amazon", "marketplace_category"],
  etsy_category: ["channel_metadata", "etsy", "marketplace_category"],
  ebay_category: ["channel_metadata", "ebay", "marketplace_category"],
  meta_category: ["channel_metadata", "meta", "marketplace_category"],
  tiktok_category: ["channel_metadata", "tiktok", "marketplace_category"],
  shopify_taxonomy_category_id: ["channel_metadata", "shopify", "shopify_taxonomy_category_id"],
  taxable: ["commerce", "taxable"],
  image_url: ["content", "media", "image_url"],
  asset_policy: ["content", "media", "asset_policy"],
  fulfillment_route: ["fulfillment", "route"]
};

const CHANNEL_CATEGORY_OPTIONS: Record<string, Array<{ label: string; value: string }>> = {
  amazon_category: [
    { label: "Office Products > Office Paper Products > Cards & Card Stock", value: "Office Products > Office Paper Products > Cards & Card Stock" },
    { label: "Office Products > Office Paper Products > Stationery", value: "Office Products > Office Paper Products > Stationery" },
    { label: "Office Products > Office Paper Products > Envelopes", value: "Office Products > Office Paper Products > Envelopes" },
    { label: "Office Products > Office Paper Products > Labels, Indexes & Stamps", value: "Office Products > Office Paper Products > Labels, Indexes & Stamps" },
    { label: "Office Products > Presentation Products > Brochure Holders & Displays", value: "Office Products > Presentation Products > Brochure Holders & Displays" },
    { label: "Industrial & Scientific > Retail Store Fixtures & Equipment > Signs & Displays", value: "Industrial & Scientific > Retail Store Fixtures & Equipment > Signs & Displays" },
    { label: "Industrial & Scientific > Professional Medical Supplies > Forms & Recordkeeping", value: "Industrial & Scientific > Professional Medical Supplies > Forms & Recordkeeping" },
    { label: "Home & Kitchen > Home Decor Products > Posters & Prints", value: "Home & Kitchen > Home Decor Products > Posters & Prints" },
    { label: "Home & Kitchen > Home Decor Products > Decorative Signs & Plaques", value: "Home & Kitchen > Home Decor Products > Decorative Signs & Plaques" },
    { label: "Patio, Lawn & Garden > Outdoor Decor > Flags & Banners", value: "Patio, Lawn & Garden > Outdoor Decor > Flags & Banners" },
    { label: "Toys & Games > Party Supplies > Banners & Decorations", value: "Toys & Games > Party Supplies > Banners & Decorations" },
    { label: "Arts, Crafts & Sewing > Craft Supplies > Paper & Card Stock", value: "Arts, Crafts & Sewing > Craft Supplies > Paper & Card Stock" },
    { label: "Clothing, Shoes & Jewelry > Novelty & More > Clothing > Novelty Apparel", value: "Clothing, Shoes & Jewelry > Novelty & More > Clothing > Novelty Apparel" },
    { label: "Clothing, Shoes & Jewelry > Luggage & Travel Gear > Backpacks", value: "Clothing, Shoes & Jewelry > Luggage & Travel Gear > Backpacks" },
    { label: "Sports & Outdoors > Fan Shop > Flags & Banners", value: "Sports & Outdoors > Fan Shop > Flags & Banners" },
    { label: "Kitchen & Dining > Dining & Entertaining > Drinkware", value: "Kitchen & Dining > Dining & Entertaining > Drinkware" },
    { label: "Electronics > Accessories & Supplies > Blank Media > Media Storage & Organization", value: "Electronics > Accessories & Supplies > Blank Media > Media Storage & Organization" }
  ],
  etsy_category: [
    { label: "Paper & Party Supplies > Paper > Stationery > Business Cards", value: "Paper & Party Supplies > Paper > Stationery > Business Cards" },
    { label: "Paper & Party Supplies > Paper > Stationery > Letterhead", value: "Paper & Party Supplies > Paper > Stationery > Letterhead" },
    { label: "Paper & Party Supplies > Paper > Cards > Greeting Cards", value: "Paper & Party Supplies > Paper > Cards > Greeting Cards" },
    { label: "Paper & Party Supplies > Invitations & Announcements", value: "Paper & Party Supplies > Invitations & Announcements" },
    { label: "Paper & Party Supplies > Paper > Stickers, Labels & Tags", value: "Paper & Party Supplies > Paper > Stickers, Labels & Tags" },
    { label: "Paper & Party Supplies > Party Supplies > Banners & Signs", value: "Paper & Party Supplies > Party Supplies > Banners & Signs" },
    { label: "Paper & Party Supplies > Paper > Planners & Notebooks", value: "Paper & Party Supplies > Paper > Planners & Notebooks" },
    { label: "Craft Supplies & Tools > Paper, Party & Kids > Paper", value: "Craft Supplies & Tools > Paper, Party & Kids > Paper" },
    { label: "Craft Supplies & Tools > Blanks > Signs", value: "Craft Supplies & Tools > Blanks > Signs" },
    { label: "Craft Supplies & Tools > Raw Materials > Vinyl", value: "Craft Supplies & Tools > Raw Materials > Vinyl" },
    { label: "Home & Living > Home Decor > Wall Decor > Prints", value: "Home & Living > Home Decor > Wall Decor > Prints" },
    { label: "Home & Living > Home Decor > Signs", value: "Home & Living > Home Decor > Signs" },
    { label: "Home & Living > Kitchen & Dining > Drinkware", value: "Home & Living > Kitchen & Dining > Drinkware" },
    { label: "Art & Collectibles > Prints > Digital Prints", value: "Art & Collectibles > Prints > Digital Prints" },
    { label: "Art & Collectibles > Prints > Giclée", value: "Art & Collectibles > Prints > Giclée" },
    { label: "Weddings > Invitations & Paper", value: "Weddings > Invitations & Paper" },
    { label: "Weddings > Decorations > Signs", value: "Weddings > Decorations > Signs" },
    { label: "Bags & Purses > Totes", value: "Bags & Purses > Totes" },
    { label: "Accessories > Keychains & Lanyards", value: "Accessories > Keychains & Lanyards" },
    { label: "Clothing > Unisex Adult Clothing > Tops & Tees", value: "Clothing > Unisex Adult Clothing > Tops & Tees" }
  ],
  google_product_category: [
    { label: "Business & Industrial > Advertising & Marketing", value: "Business & Industrial > Advertising & Marketing" },
    { label: "Office Supplies", value: "Office Supplies" },
    { label: "Arts & Entertainment > Party & Celebration", value: "Arts & Entertainment > Party & Celebration" },
    { label: "Home & Garden > Decor", value: "Home & Garden > Decor" },
    { label: "Apparel & Accessories", value: "Apparel & Accessories" }
  ],
  ebay_category: [
    { label: "Business, Office & Industrial > Printing & Graphic Arts", value: "Business, Office & Industrial > Printing & Graphic Arts" },
    { label: "Crafts > Multi-Purpose Craft Supplies", value: "Crafts > Multi-Purpose Craft Supplies" },
    { label: "Home, Furniture & DIY > Home Decor", value: "Home, Furniture & DIY > Home Decor" },
    { label: "Clothes, Shoes & Accessories > Specialty", value: "Clothes, Shoes & Accessories > Specialty" }
  ],
  meta_category: [
    { label: "Business & Industrial > Advertising & Marketing", value: "Business & Industrial > Advertising & Marketing" },
    { label: "Office Supplies", value: "Office Supplies" },
    { label: "Home & Garden > Decor", value: "Home & Garden > Decor" },
    { label: "Arts & Entertainment > Party & Celebration", value: "Arts & Entertainment > Party & Celebration" },
    { label: "Apparel & Accessories", value: "Apparel & Accessories" }
  ],
  tiktok_category: [
    { label: "Office Supplies", value: "Office Supplies" },
    { label: "Home Supplies > Home Decor", value: "Home Supplies > Home Decor" },
    { label: "Womenswear & Underwear", value: "Womenswear & Underwear" },
    { label: "Sports & Outdoor > Fan Merchandise", value: "Sports & Outdoor > Fan Merchandise" },
    { label: "Collectibles", value: "Collectibles" }
  ],
  asset_policy: [
    { label: "Use Shopify product media", value: "shopify_product_media" },
    { label: "Use configured channel image URL", value: "configured_channel_image" },
    { label: "Require manual image review", value: "manual_review" },
    { label: "Generate from product mockup", value: "generated_mockup" }
  ]
};

function optionsWithCurrent(
  options: Array<{ label: string; value: string }>,
  currentValue: string
): Array<{ label: string; value: string }> {
  const base = [{ label: "Not set", value: "" }, ...options];
  if (!currentValue || base.some((option) => option.value === currentValue)) {
    return base;
  }
  return [...base, { label: currentValue, value: currentValue }];
}

function StoresView({ embedded = false }: { embedded?: boolean }) {
  const { data, loading, error, reload } = useApiData<{ stores: StoreSummary[] }>("/shopify/admin/stores", { stores: [] });
  const content = (
    <BlockStack gap="400">
        <DismissibleNotice message={error ? `Stores could not be loaded: ${error}` : null} />
        {loading ? <LoadingState label="Loading stores" /> : null}
        <Card>
          <DataTable
            columnContentTypes={["text", "text", "text", "text", "text", "text"]}
            headings={["Store", "Install", "App proxy", "Theme extension", "Billing", "Customer data"]}
            rows={
              data.stores.length
                ? data.stores.map((store) => [
                    store.shop_domain,
                    <StatusBadge status={store.install_status} />,
                    <StatusBadge status={store.app_proxy_status} />,
                    <StatusBadge status={store.theme_extension_status} />,
                    <StatusBadge status={store.billing_status} />,
                    <StatusBadge status={store.protected_customer_data_status} />
                  ])
                : [[loading ? "Loading stores..." : "No stores found", "", "", "", "", ""]]
            }
          />
        </Card>
      </BlockStack>
  );
  if (embedded) return content;
  return (
    <Page title="Stores" subtitle="Organisation-managed Shopify stores" primaryAction={{ content: "Refresh", onAction: reload }}>
      {content}
    </Page>
  );
}

function SuppliersView({ embedded = false }: { embedded?: boolean }) {
  const { data, loading, error, reload } = useApiData<{ suppliers: SupplierConnection[] }>("/shopify/admin/suppliers", { suppliers: [] });
  const {
    data: setupStatus,
    loading: setupLoading,
    error: setupError,
    reload: reloadSetupStatus
  } = useApiData<SetupStatus>("/shopify/admin/setup/status", {
    backend: "error",
    shopify_session: "not_verified",
    shopify_admin_api: "missing",
    supplier_credentials: "missing",
    app_proxy: "missing",
    webhook_secret: "missing",
    embedded_app_url: null,
    distribution_mode: "public"
  });
  const [message, setMessage] = useState<string | null>(null);
  const [catalogs, setCatalogs] = useState<Record<string, SupplierCatalogProduct[]>>({});
  const [busySupplier, setBusySupplier] = useState<string | null>(null);
  const [supplierConfigDrafts, setSupplierConfigDrafts] = useState<Record<string, Record<string, string>>>({});
  const [impactOptionTemplateListDrafts, setImpactOptionTemplateListDrafts] = useState<Record<string, ImpactOptionTemplate[]>>({});
  const [impactOptionTemplateEditorDrafts, setImpactOptionTemplateEditorDrafts] = useState<
    Record<string, { selectedName: string; name: string; sizes: string[]; substrates: string[] }>
  >({});
  const [supplierCredentialDrafts, setSupplierCredentialDrafts] = useState<Record<string, Record<string, string>>>({});
  const [selectedSupplierTab, setSelectedSupplierTab] = useState(0);
  const hasDeferredSuppliers = data.suppliers.some((supplier) => supplier.is_beta);
  const setupConfig = setupStatus.config || {};
  const supplierTabs = data.suppliers.map((supplier) => ({
    id: supplier.supplier_key,
    content: supplier.display_name,
    panelID: `supplier-${supplier.supplier_key}`
  }));

  const loadCatalog = async (supplierKey: string) => {
    setBusySupplier(`${supplierKey}:catalog`);
    const result = await apiFetch<{ products: SupplierCatalogProduct[] }>(
      `/shopify/admin/suppliers/${supplierKey}/catalog/products`
    );
    setBusySupplier(null);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    setCatalogs((current) => ({ ...current, [supplierKey]: result.data?.products || [] }));
  };

  const supplierCredentialDraft = (supplierKey: string, key: keyof SetupConfig, fallback = "") =>
    supplierCredentialDrafts[supplierKey]?.[key] ?? String(setupConfig[key] ?? fallback);

  const setSupplierCredentialDraft = (supplierKey: string, key: keyof SetupConfig, value: string) =>
    setSupplierCredentialDrafts((current) => ({
      ...current,
      [supplierKey]: {
        ...(current[supplierKey] || {}),
        [key]: value
      }
    }));

  const saveSupplierCredentials = async (supplierKey: string, payload: SetupConfig, successMessage: string) => {
    setBusySupplier(`${supplierKey}:credentials`);
    const result = await apiFetch<{ saved: boolean; config?: SetupConfig }>("/shopify/admin/setup/config", {
      method: "PUT",
      body: JSON.stringify(setupConfigForSave(payload, setupStatus.distribution_mode))
    });
    setBusySupplier(null);
    setMessage(result.error || successMessage);
    if (!result.error) {
      setSupplierCredentialDrafts((current) => {
        const next = { ...current };
        delete next[supplierKey];
        return next;
      });
      void reloadSetupStatus();
      void reload();
    }
  };

  const content = (
    <BlockStack gap="400">
        {hasDeferredSuppliers ? (
          <Banner tone="warning">
            Beta suppliers are visible in development only. Production launch scope should keep deferred suppliers disabled unless explicitly approved.
          </Banner>
        ) : null}
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        <DismissibleNotice message={error ? `Suppliers could not be loaded: ${error}` : null} />
        <DismissibleNotice
          message={setupError ? `Supplier credentials could not be loaded: ${setupError}` : null}
        />
        {loading || setupLoading ? <LoadingState label="Loading supplier connections" /> : null}
        {!loading && !data.suppliers.length && !error ? <Banner tone="info">No supplier connections are configured for this store yet.</Banner> : null}
        {data.suppliers.length ? (
          <BlockStack gap="300">
            <Tabs tabs={supplierTabs} selected={Math.min(selectedSupplierTab, supplierTabs.length - 1)} onSelect={setSelectedSupplierTab} fitted />
            <InlineGrid columns={{ xs: 1, md: 1 }} gap="400">
              {data.suppliers
                .filter((_, index) => index === Math.min(selectedSupplierTab, supplierTabs.length - 1))
                .map((supplier) => {
            const isImpact = supplier.supplier_key === "impactconnect";
            const publicConfig = supplier.public_config || {};
            const configDraft = supplierConfigDrafts[supplier.supplier_key] || {};
            const impactSource = configDraft.impact_library_source ?? String(publicConfig.impact_library_source || "apps/backend/data/impact_health_safety_catalog.json");
            const materialPricingMatrix = configDraft.material_pricing_matrix ?? JSON.stringify(publicConfig.material_pricing_matrix || {}, null, 2);
            const impactAvailableSizes = configDraft.impact_available_sizes ?? impactSizesText(publicConfig.impact_available_sizes || DEFAULT_IMPACT_SIZES);
            const impactAvailableSubstrates = configDraft.impact_available_substrates ?? impactSubstratesText(publicConfig.impact_available_substrates || DEFAULT_IMPACT_SUBSTRATES);
            const impactOptionTemplates = impactOptionTemplateListDrafts[supplier.supplier_key] ?? parseImpactOptionTemplates(publicConfig.impact_option_templates);
            const impactTemplateEditor = impactOptionTemplateEditorDrafts[supplier.supplier_key] || {
              selectedName: "",
              name: "",
              sizes: [],
              substrates: []
            };
            const parsedAvailableSizesForTemplates = impactSizesFromText(impactAvailableSizes);
            const parsedAvailableSubstratesForTemplates = parseImpactSubstrates(impactAvailableSubstrates);
            const impactTemplateOptions = [
              { label: "New template", value: "" },
              ...impactOptionTemplates.map((template) => ({
                label: template.name,
                value: template.name
              }))
            ];
            const setImpactTemplateEditor = (draft: Partial<typeof impactTemplateEditor>) =>
              setImpactOptionTemplateEditorDrafts((current) => ({
                ...current,
                [supplier.supplier_key]: {
                  ...(current[supplier.supplier_key] || {
                    selectedName: "",
                    name: "",
                    sizes: [],
                    substrates: []
                  }),
                  ...draft
                }
              }));
            const upsertImpactTemplate = () => {
              const name = impactTemplateEditor.name.trim();
              if (!name) {
                setMessage("Template name is required");
                return;
              }
              if (!impactTemplateEditor.sizes.length && !impactTemplateEditor.substrates.length) {
                setMessage("Choose at least one size or substrate for the option template");
                return;
              }
              const sizes = impactTemplateEditor.sizes
                .map((label) => parsedAvailableSizesForTemplates.find((size) => size.label === label) || impactSizeFromLabel(label))
                .filter((size): size is ImpactSizeOption => Boolean(size));
              const nextTemplate: ImpactOptionTemplate = {
                name,
                sizes,
                substrates: parseImpactSubstrates(impactTemplateEditor.substrates)
              };
              setImpactOptionTemplateListDrafts((current) => {
                const existing = current[supplier.supplier_key] ?? impactOptionTemplates;
                const replaceName = impactTemplateEditor.selectedName || name;
                const next = existing.filter((template) => template.name !== replaceName && template.name !== name);
                return {
                  ...current,
                  [supplier.supplier_key]: [...next, nextTemplate].sort((first, second) => first.name.localeCompare(second.name))
                };
              });
              setImpactTemplateEditor({
                selectedName: name,
                name,
                sizes: nextTemplate.sizes?.map((size) => size.label) || [],
                substrates: nextTemplate.substrates || []
              });
            };
            const deleteImpactTemplate = () => {
              const deleteName = impactTemplateEditor.selectedName || impactTemplateEditor.name.trim();
              if (!deleteName) {
                setMessage("Choose an option template to delete");
                return;
              }
              setImpactOptionTemplateListDrafts((current) => {
                const existing = current[supplier.supplier_key] ?? impactOptionTemplates;
                return {
                  ...current,
                  [supplier.supplier_key]: existing.filter((template) => template.name !== deleteName)
                };
              });
              setImpactTemplateEditor({ selectedName: "", name: "", sizes: [], substrates: [] });
            };
            const impactGlobalMarginPercent = configDraft.impact_global_margin_percent ?? String(publicConfig.impact_global_margin_percent ?? "");
            const impactMinimumPricingEnabled =
              configDraft.impact_minimum_pricing_enabled ??
              (publicConfig.impact_minimum_pricing_enabled === undefined
                ? true
                : Boolean(publicConfig.impact_minimum_pricing_enabled));
            const impactLogoCustomisationEnabled = configDraft.impact_logo_customisation_enabled ?? Boolean(publicConfig.impact_logo_customisation_enabled);
            const impactLogoCustomisationLabel = configDraft.impact_logo_customisation_label ?? String(publicConfig.impact_logo_customisation_label || "Add customised logo");
            const impactLogoCustomisationPrice = configDraft.impact_logo_customisation_price ?? String(publicConfig.impact_logo_customisation_price ?? "");
            const impactLogoCustomisationTooltip =
              configDraft.impact_logo_customisation_tooltip ??
              String(publicConfig.impact_logo_customisation_tooltip || "Adds your supplied logo to the sign artwork.");
            const impactAntiGraffitiEnabled =
              configDraft.impact_anti_graffiti_overlaminate_enabled ??
              (publicConfig.impact_anti_graffiti_overlaminate_enabled === undefined
                ? true
                : Boolean(publicConfig.impact_anti_graffiti_overlaminate_enabled));
            const impactAntiGraffitiLabel =
              configDraft.impact_anti_graffiti_overlaminate_label ??
              String(publicConfig.impact_anti_graffiti_overlaminate_label || "Anti-graffiti overlaminate");
            const impactAntiGraffitiTooltip =
              configDraft.impact_anti_graffiti_overlaminate_tooltip ??
              String(publicConfig.impact_anti_graffiti_overlaminate_tooltip || "Adds a protective anti-graffiti overlaminate to the sign face.");
            const impactAntiGraffitiPricePerSqm =
              configDraft.impact_anti_graffiti_overlaminate_price_per_sqm ??
              String(publicConfig.impact_anti_graffiti_overlaminate_price_per_sqm ?? 18);
            const impactAntiGraffitiMinimumPrice =
              configDraft.impact_anti_graffiti_overlaminate_minimum_price ??
              String(publicConfig.impact_anti_graffiti_overlaminate_minimum_price ?? 3);
            const impactQuantityMarginCompression = configDraft.impact_quantity_margin_compression ?? JSON.stringify(
              publicConfig.impact_quantity_margin_compression || [
                { min_quantity: 1, margin_multiplier: 1 },
                { min_quantity: 2, margin_multiplier: 0.85 },
                { min_quantity: 5, margin_multiplier: 0.75 },
                { min_quantity: 10, margin_multiplier: 0.65 },
                { min_quantity: 25, margin_multiplier: 0.55 },
                { min_quantity: 50, margin_multiplier: 0.45 },
                { min_quantity: 100, margin_multiplier: 0.35 }
              ],
              null,
              2
            );
            const impactProductSizeOverrides = configDraft.impact_product_size_overrides ?? JSON.stringify(publicConfig.impact_product_size_overrides || {}, null, 2);
            const defaultPublishStatus = configDraft.default_publish_status ?? String(publicConfig.default_publish_status || "DRAFT");
            const betaActionBlocked = supplier.is_beta && !isImpact;
            return (
            <Card key={supplier.supplier_key}>
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">
                    {supplier.display_name}
                  </Text>
                  <StatusBadge status={supplier.is_beta ? "Beta" : "Stable"} />
                </InlineStack>
                <StatusBadge status={supplier.status} />
                {supplier.is_beta ? (
                  <Banner tone="warning">
                    {isImpact
                      ? "Impact signs are in launch scope. Confirm catalogue quality, pricing, and local fulfilment before publishing at scale."
                      : "Deferred supplier. Keep disabled unless this supplier is explicitly approved for launch."}
                  </Banner>
                ) : null}
                <Text as="p" variant="bodyMd">
                  {supplier.metadata.product_model}
                </Text>
                <DismissibleNotice
                  tone="critical"
                  message={supplier.last_error ? `Last supplier error: ${supplier.last_error}` : null}
                />
                <Text as="p" variant="bodySm" tone="subdued">
                  Last tested: {supplier.last_tested_at || "Not tested"} · Last sync: {supplier.last_successful_sync_at || "No successful sync recorded"}
                </Text>
                <Select
                  label="Environment"
                  value={supplier.environment}
                  disabled={Boolean(busySupplier)}
                  options={[
                    { label: "Sandbox", value: "sandbox" },
                    { label: "Production", value: "production" }
                  ]}
                  onChange={async (environment) => {
                    setBusySupplier(`${supplier.supplier_key}:environment`);
                    const result = await apiFetch(`/shopify/admin/suppliers/${supplier.supplier_key}`, {
                      method: "PUT",
                      body: JSON.stringify({ environment })
                    });
                    setBusySupplier(null);
                    setMessage(result.error || `${supplier.display_name} environment updated`);
                    void reload();
                  }}
                />
                <Checkbox
                  label={`Enable ${supplier.display_name} for this store`}
                  checked={supplier.is_enabled}
                  disabled={Boolean(busySupplier)}
                  onChange={async (is_enabled) => {
                    setBusySupplier(`${supplier.supplier_key}:enabled`);
                    const result = await apiFetch(`/shopify/admin/suppliers/${supplier.supplier_key}`, {
                      method: "PUT",
                      body: JSON.stringify({ is_enabled })
                    });
                    setBusySupplier(null);
                    setMessage(result.error || `${supplier.display_name} updated`);
                    void reload();
                  }}
                />
                <BlockStack gap="100">
                  {Object.entries(supplier.capabilities).map(([capability, enabled]) => (
                    <InlineStack key={capability} align="space-between">
                      <Text as="span" variant="bodySm">
                        {capability.replace(/_/g, " ")}
                      </Text>
                      <StatusBadge status={enabled ? "Ready" : "Unsupported"} />
                    </InlineStack>
                  ))}
                </BlockStack>
                {supplier.supplier_key === "tradeprint" ? (
                  <details className="admin-accordion" open>
                    <summary>Tradeprint API credentials</summary>
                    <div className="admin-accordion__body">
                      <FormLayout>
                        <TextField
                          label="API key"
                          value={supplierCredentialDraft(supplier.supplier_key, "tradeprint_api_key")}
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "tradeprint_api_key", value)}
                        />
                        <TextField
                          label="API prefix"
                          value={supplierCredentialDraft(supplier.supplier_key, "tradeprint_api_prefix", "/v2")}
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "tradeprint_api_prefix", value)}
                        />
                        <TextField
                          label="Username"
                          value={supplierCredentialDraft(supplier.supplier_key, "tradeprint_username")}
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "tradeprint_username", value)}
                        />
                        <TextField
                          label="Password"
                          type="password"
                          value={supplierCredentialDraft(supplier.supplier_key, "tradeprint_password")}
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "tradeprint_password", value)}
                        />
                        <Select
                          label="API environment"
                          options={[
                            { label: "Sandbox (SDK stage)", value: "sandbox" },
                            { label: "Sandbox docs host", value: "sandbox_docs" },
                            { label: "Production", value: "production" }
                          ]}
                          value={supplierCredentialDraft(supplier.supplier_key, "tradeprint_environment", "sandbox")}
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "tradeprint_environment", value)}
                        />
                        <Button
                          variant="primary"
                          loading={busySupplier === `${supplier.supplier_key}:credentials`}
                          disabled={Boolean(busySupplier)}
                          onClick={() =>
                            void saveSupplierCredentials(
                              supplier.supplier_key,
                              {
                                tradeprint_api_key: supplierCredentialDraft(supplier.supplier_key, "tradeprint_api_key"),
                                tradeprint_api_prefix: supplierCredentialDraft(supplier.supplier_key, "tradeprint_api_prefix", "/v2"),
                                tradeprint_username: supplierCredentialDraft(supplier.supplier_key, "tradeprint_username"),
                                tradeprint_password: supplierCredentialDraft(supplier.supplier_key, "tradeprint_password"),
                                tradeprint_environment: supplierCredentialDraft(
                                  supplier.supplier_key,
                                  "tradeprint_environment",
                                  "sandbox"
                                ) as "sandbox" | "sandbox_docs" | "production"
                              },
                              "Tradeprint API credentials saved"
                            )
                          }
                        >
                          Save Tradeprint credentials
                        </Button>
                      </FormLayout>
                    </div>
                  </details>
                ) : null}
                {supplier.supplier_key === "tradeprint" ? <TradeprintControlsCard /> : null}
                {supplier.supplier_key === "pencarrie" ? (
                  <details className="admin-accordion" open>
                    <summary>PenCarrie API credentials</summary>
                    <div className="admin-accordion__body">
                      <FormLayout>
                        <TextField
                          label="API token"
                          type="password"
                          value={supplierCredentialDraft(supplier.supplier_key, "pencarrie_api_token")}
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "pencarrie_api_token", value)}
                        />
                        <TextField
                          label="Products ZIP URL"
                          value={supplierCredentialDraft(supplier.supplier_key, "pencarrie_products_url")}
                          placeholder="https://www.pencarrie.com/api/public/v1/export/products.zip"
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "pencarrie_products_url", value)}
                        />
                        <TextField
                          label="Stock CSV URL"
                          value={supplierCredentialDraft(supplier.supplier_key, "pencarrie_stock_url")}
                          placeholder="https://www.pencarrie.com/api/public/v1/stock.csv"
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setSupplierCredentialDraft(supplier.supplier_key, "pencarrie_stock_url", value)}
                        />
                        <Button
                          variant="primary"
                          loading={busySupplier === `${supplier.supplier_key}:credentials`}
                          disabled={Boolean(busySupplier)}
                          onClick={() =>
                            void saveSupplierCredentials(
                              supplier.supplier_key,
                              {
                                pencarrie_api_token: supplierCredentialDraft(supplier.supplier_key, "pencarrie_api_token"),
                                pencarrie_products_url: supplierCredentialDraft(supplier.supplier_key, "pencarrie_products_url"),
                                pencarrie_stock_url: supplierCredentialDraft(supplier.supplier_key, "pencarrie_stock_url")
                              },
                              "PenCarrie API credentials saved"
                            )
                          }
                        >
                          Save PenCarrie credentials
                        </Button>
                      </FormLayout>
                    </div>
                  </details>
                ) : null}
                {isImpact ? (
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Impact catalogue settings
                    </Text>
                    <TextField
                      label="Static catalogue source"
                      value={impactSource}
                      autoComplete="off"
                      disabled={Boolean(busySupplier)}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_library_source: value
                          }
                        }))
                      }
                    />
                    <TextField
                      label="Material, ink, and margin cost JSON"
                      value={materialPricingMatrix}
                      multiline={4}
                      autoComplete="off"
                      helpText="Global Impact substrate pricing. Configure each material with material_cost_per_sqm, ink_cost_per_sqm, minimum_price, margin_percent, production_method, and any plotter_application_cost_per_sqm."
                      disabled={Boolean(busySupplier)}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            material_pricing_matrix: value
                          }
                        }))
                      }
                    />
                    <TextField
                      label="Available sizes"
                      value={impactAvailableSizes}
                      multiline={4}
                      autoComplete="off"
                      helpText="One per line as Label,width_mm,height_mm. Example: A4,210,297"
                      disabled={Boolean(busySupplier)}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_available_sizes: value
                          }
                        }))
                      }
                    />
                    <TextField
                      label="Available substrates"
                      value={impactAvailableSubstrates}
                      multiline={4}
                      autoComplete="off"
                      helpText="One substrate per line. These become product assignment options."
                      disabled={Boolean(busySupplier)}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_available_substrates: value
                          }
                        }))
                      }
                    />
                    <BlockStack gap="200">
                      <Text as="h4" variant="headingSm">
                        Option templates
                      </Text>
                      <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                        <Select
                          label="Edit template"
                          value={impactTemplateEditor.selectedName}
                          disabled={Boolean(busySupplier)}
                          options={impactTemplateOptions}
                          onChange={(value) => {
                            const template = impactOptionTemplates.find((item) => item.name === value);
                            setImpactTemplateEditor({
                              selectedName: value,
                              name: template?.name || "",
                              sizes: parseImpactSizes(template?.sizes).map((size) => size.label),
                              substrates: parseImpactSubstrates(template?.substrates)
                            });
                          }}
                        />
                        <TextField
                          label="Template name"
                          value={impactTemplateEditor.name}
                          autoComplete="off"
                          disabled={Boolean(busySupplier)}
                          onChange={(value) => setImpactTemplateEditor({ name: value })}
                        />
                      </InlineGrid>
                      <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                        <MultiSelectDropdown
                          label="Template sizes"
                          summary={
                            impactTemplateEditor.sizes.length
                              ? `${impactTemplateEditor.sizes.length} sizes selected`
                              : "Select sizes"
                          }
                          options={parsedAvailableSizesForTemplates.map((size) => ({
                            label: size.label,
                            value: size.label,
                            description: `${size.width_mm} x ${size.height_mm}mm`
                          }))}
                          selectedValues={impactTemplateEditor.sizes}
                          filterPlaceholder="Filter sizes"
                          disabled={Boolean(busySupplier)}
                          onChange={(values) => setImpactTemplateEditor({ sizes: values })}
                        />
                        <MultiSelectDropdown
                          label="Template substrates"
                          summary={
                            impactTemplateEditor.substrates.length
                              ? `${impactTemplateEditor.substrates.length} substrates selected`
                              : "Select substrates"
                          }
                          options={parsedAvailableSubstratesForTemplates.map((substrate) => ({
                            label: substrate,
                            value: substrate
                          }))}
                          selectedValues={impactTemplateEditor.substrates}
                          filterPlaceholder="Filter substrates"
                          disabled={Boolean(busySupplier)}
                          onChange={(values) => setImpactTemplateEditor({ substrates: values })}
                        />
                      </InlineGrid>
                      <InlineStack gap="200">
                        <Button disabled={Boolean(busySupplier)} onClick={upsertImpactTemplate}>
                          {impactTemplateEditor.selectedName ? "Update template" : "Add template"}
                        </Button>
                        <Button
                          disabled={Boolean(busySupplier)}
                          onClick={() => setImpactTemplateEditor({ selectedName: "", name: "", sizes: [], substrates: [] })}
                        >
                          New template
                        </Button>
                        <Button
                          tone="critical"
                          disabled={Boolean(busySupplier) || !impactTemplateEditor.selectedName}
                          onClick={deleteImpactTemplate}
                        >
                          Delete template
                        </Button>
                      </InlineStack>
                      <Text as="p" tone="subdued">
                        {impactOptionTemplates.length
                          ? `${impactOptionTemplates.length} option templates configured. Use Save Impact settings to persist changes.`
                          : "Create templates here, then apply them from the Products page."}
                      </Text>
                    </BlockStack>
                    <TextField
                      label="Global Impact margin"
                      type="number"
                      suffix="%"
                      value={impactGlobalMarginPercent}
                      autoComplete="off"
                      helpText="Used for area-based Impact pricing unless a product margin is entered."
                      disabled={Boolean(busySupplier)}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_global_margin_percent: value
                          }
                        }))
                      }
                    />
                    <Checkbox
                      label="Enable minimum pricing by default"
                      checked={truthyConfigValue(impactMinimumPricingEnabled)}
                      helpText="Applies each material minimum_price as a per-order-line floor after quantity discounts."
                      disabled={Boolean(busySupplier)}
                      onChange={(checked) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_minimum_pricing_enabled: checked ? "true" : "false"
                          }
                        }))
                      }
                    />
                    <Checkbox
                      label="Offer customised logo add-on"
                      checked={truthyConfigValue(impactLogoCustomisationEnabled)}
                      disabled={Boolean(busySupplier)}
                      onChange={(checked) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_logo_customisation_enabled: checked ? "true" : "false"
                          }
                        }))
                      }
                    />
                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                      <TextField
                        label="Logo add-on label"
                        value={impactLogoCustomisationLabel}
                        autoComplete="off"
                        disabled={Boolean(busySupplier)}
                        onChange={(value) =>
                          setSupplierConfigDrafts((current) => ({
                            ...current,
                            [supplier.supplier_key]: {
                              ...(current[supplier.supplier_key] || {}),
                              impact_logo_customisation_label: value
                            }
                          }))
                        }
                      />
                      <TextField
                        label="Logo tooltip description"
                        value={impactLogoCustomisationTooltip}
                        autoComplete="off"
                        helpText="Shown as a customer tooltip on the storefront add-on."
                        disabled={Boolean(busySupplier)}
                        onChange={(value) =>
                          setSupplierConfigDrafts((current) => ({
                            ...current,
                            [supplier.supplier_key]: {
                              ...(current[supplier.supplier_key] || {}),
                              impact_logo_customisation_tooltip: value
                            }
                          }))
                        }
                      />
                      <TextField
                        label="Logo add-on fixed price"
                        type="number"
                        prefix="£"
                        value={impactLogoCustomisationPrice}
                        min={0}
                        autoComplete="off"
                        helpText="Applied once per sign order line, not per quantity."
                        disabled={Boolean(busySupplier)}
                        onChange={(value) =>
                          setSupplierConfigDrafts((current) => ({
                            ...current,
                            [supplier.supplier_key]: {
                              ...(current[supplier.supplier_key] || {}),
                              impact_logo_customisation_price: value
                            }
                          }))
                        }
                      />
                    </InlineGrid>
                    <Checkbox
                      label="Offer anti-graffiti overlaminate add-on by default"
                      checked={truthyConfigValue(impactAntiGraffitiEnabled)}
                      disabled={Boolean(busySupplier)}
                      onChange={(checked) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_anti_graffiti_overlaminate_enabled: checked ? "true" : "false"
                          }
                        }))
                      }
                    />
                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                      <TextField
                        label="Overlaminate add-on label"
                        value={impactAntiGraffitiLabel}
                        autoComplete="off"
                        disabled={Boolean(busySupplier)}
                        onChange={(value) =>
                          setSupplierConfigDrafts((current) => ({
                            ...current,
                            [supplier.supplier_key]: {
                              ...(current[supplier.supplier_key] || {}),
                              impact_anti_graffiti_overlaminate_label: value
                            }
                          }))
                        }
                      />
                      <TextField
                        label="Overlaminate tooltip description"
                        value={impactAntiGraffitiTooltip}
                        autoComplete="off"
                        helpText="Shown as a customer tooltip on the storefront add-on."
                        disabled={Boolean(busySupplier)}
                        onChange={(value) =>
                          setSupplierConfigDrafts((current) => ({
                            ...current,
                            [supplier.supplier_key]: {
                              ...(current[supplier.supplier_key] || {}),
                              impact_anti_graffiti_overlaminate_tooltip: value
                            }
                          }))
                        }
                      />
                    </InlineGrid>
                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                      <TextField
                        label="Overlaminate price per sqm"
                        type="number"
                        prefix="£"
                        value={impactAntiGraffitiPricePerSqm}
                        min={0}
                        autoComplete="off"
                        disabled={Boolean(busySupplier)}
                        onChange={(value) =>
                          setSupplierConfigDrafts((current) => ({
                            ...current,
                            [supplier.supplier_key]: {
                              ...(current[supplier.supplier_key] || {}),
                              impact_anti_graffiti_overlaminate_price_per_sqm: value
                            }
                          }))
                        }
                      />
                      <TextField
                        label="Overlaminate minimum per item"
                        type="number"
                        prefix="£"
                        value={impactAntiGraffitiMinimumPrice}
                        min={0}
                        autoComplete="off"
                        helpText="Used when area-based overlaminate cost is below this floor."
                        disabled={Boolean(busySupplier)}
                        onChange={(value) =>
                          setSupplierConfigDrafts((current) => ({
                            ...current,
                            [supplier.supplier_key]: {
                              ...(current[supplier.supplier_key] || {}),
                              impact_anti_graffiti_overlaminate_minimum_price: value
                            }
                          }))
                        }
                      />
                    </InlineGrid>
                    <TextField
                      label="Quantity margin compression JSON"
                      value={impactQuantityMarginCompression}
                      multiline={4}
                      autoComplete="off"
                      helpText="Global Impact quantity discounts. Each row needs min_quantity and margin_multiplier."
                      disabled={Boolean(busySupplier)}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_quantity_margin_compression: value
                          }
                        }))
                      }
                    />
                    <TextField
                      label="Per-product size overrides JSON"
                      value={impactProductSizeOverrides}
                      multiline={4}
                      autoComplete="off"
                      helpText='Map supplier product IDs to sizes, for example {"VO-HS-FIRST-AID":["A5","A4","A3"]} or use objects with label, width_mm, and height_mm.'
                      disabled={Boolean(busySupplier)}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            impact_product_size_overrides: value
                          }
                        }))
                      }
                    />
                    <Select
                      label="Default publish status"
                      value={defaultPublishStatus}
                      disabled={Boolean(busySupplier)}
                      options={[
                        { label: "Draft", value: "DRAFT" },
                        { label: "Active", value: "ACTIVE" }
                      ]}
                      onChange={(value) =>
                        setSupplierConfigDrafts((current) => ({
                          ...current,
                          [supplier.supplier_key]: {
                            ...(current[supplier.supplier_key] || {}),
                            default_publish_status: value
                          }
                        }))
                      }
                    />
                    <Button
                      disabled={Boolean(busySupplier)}
                      loading={busySupplier === `${supplier.supplier_key}:config`}
                      onClick={async () => {
                        let parsedPricing: Record<string, unknown> = {};
                        if (materialPricingMatrix.trim()) {
                          try {
                            parsedPricing = JSON.parse(materialPricingMatrix);
                          } catch {
                            setMessage("Impact material, ink, and margin cost config must be valid JSON");
                            return;
                          }
                        }
                        let parsedSizeOverrides: Record<string, unknown> = {};
                        const parsedAvailableSizes = impactSizesFromText(impactAvailableSizes);
                        const parsedAvailableSubstrates = parseImpactSubstrates(impactAvailableSubstrates);
                        const parsedOptionTemplates = cleanImpactOptionTemplates(impactOptionTemplates);
                        let parsedQuantityCompression: Array<Record<string, unknown>> = [];
                        if (impactQuantityMarginCompression.trim()) {
                          try {
                            const parsed = JSON.parse(impactQuantityMarginCompression);
                            if (Array.isArray(parsed)) parsedQuantityCompression = parsed;
                          } catch {
                            setMessage("Impact quantity margin compression must be valid JSON");
                            return;
                          }
                        }
                        if (impactProductSizeOverrides.trim()) {
                          try {
                            parsedSizeOverrides = JSON.parse(impactProductSizeOverrides);
                          } catch {
                            setMessage("Impact per-product size overrides must be valid JSON");
                            return;
                          }
                        }
                        setBusySupplier(`${supplier.supplier_key}:config`);
                        const result = await apiFetch(`/shopify/admin/suppliers/${supplier.supplier_key}`, {
                          method: "PUT",
                          body: JSON.stringify({
                            public_config: {
                              ...publicConfig,
                              impact_library_source: impactSource,
                              material_pricing_matrix: parsedPricing,
                              impact_available_sizes: parsedAvailableSizes,
                              impact_available_substrates: parsedAvailableSubstrates,
                              impact_option_templates: parsedOptionTemplates,
                              impact_global_margin_percent: Number(impactGlobalMarginPercent || 0),
                              impact_minimum_pricing_enabled: truthyConfigValue(impactMinimumPricingEnabled),
                              impact_quantity_margin_compression: parsedQuantityCompression,
                              impact_logo_customisation_enabled: truthyConfigValue(impactLogoCustomisationEnabled),
                              impact_logo_customisation_label: impactLogoCustomisationLabel,
                              impact_logo_customisation_price: Number(impactLogoCustomisationPrice || 0),
                              impact_logo_customisation_tooltip: impactLogoCustomisationTooltip,
                              impact_anti_graffiti_overlaminate_enabled: truthyConfigValue(impactAntiGraffitiEnabled),
                              impact_anti_graffiti_overlaminate_label: impactAntiGraffitiLabel,
                              impact_anti_graffiti_overlaminate_price_per_sqm: Number(impactAntiGraffitiPricePerSqm || 0),
                              impact_anti_graffiti_overlaminate_minimum_price: Number(impactAntiGraffitiMinimumPrice || 0),
                              impact_anti_graffiti_overlaminate_tooltip: impactAntiGraffitiTooltip,
                              impact_product_size_overrides: parsedSizeOverrides,
                              default_publish_status: defaultPublishStatus
                            }
                          })
                        });
                        setBusySupplier(null);
                        setMessage(result.error || "Impact catalogue settings saved");
                        void reload();
                      }}
                    >
                      Save Impact settings
                    </Button>
                  </BlockStack>
                ) : null}
                <Button
                  loading={busySupplier === `${supplier.supplier_key}:test`}
                  disabled={Boolean(busySupplier)}
                  onClick={async () => {
                    setBusySupplier(`${supplier.supplier_key}:test`);
                    const result = await apiFetch<{ result?: { ok?: boolean; message?: string } }>(
                      `/shopify/admin/suppliers/${supplier.supplier_key}/test`,
                      { method: "POST" }
                    );
                    setBusySupplier(null);
                    const testMessage = result.data?.result?.message || `${supplier.display_name} test completed`;
                    setMessage(result.error || testMessage);
                    void reload();
                  }}
                >
                  Test connection
                </Button>
                <InlineStack gap="200">
                  <Button
                    disabled={betaActionBlocked || Boolean(busySupplier)}
                    loading={busySupplier === `${supplier.supplier_key}:import`}
                    onClick={async () => {
                      let importPayload: Record<string, unknown> = {};
                      if (isImpact) {
                        let parsedPricing: Record<string, unknown> = {};
                        if (materialPricingMatrix.trim()) {
                          try {
                            parsedPricing = JSON.parse(materialPricingMatrix);
                          } catch {
                            setMessage("Impact material, ink, and margin cost config must be valid JSON");
                            return;
                          }
                        }
                        let parsedSizeOverrides: Record<string, unknown> = {};
                        const parsedAvailableSizes = impactSizesFromText(impactAvailableSizes);
                        const parsedAvailableSubstrates = parseImpactSubstrates(impactAvailableSubstrates);
                        const parsedOptionTemplates = cleanImpactOptionTemplates(impactOptionTemplates);
                        let parsedQuantityCompression: Array<Record<string, unknown>> = [];
                        if (impactQuantityMarginCompression.trim()) {
                          try {
                            const parsed = JSON.parse(impactQuantityMarginCompression);
                            if (Array.isArray(parsed)) parsedQuantityCompression = parsed;
                          } catch {
                            setMessage("Impact quantity margin compression must be valid JSON");
                            return;
                          }
                        }
                        if (impactProductSizeOverrides.trim()) {
                          try {
                            parsedSizeOverrides = JSON.parse(impactProductSizeOverrides);
                          } catch {
                            setMessage("Impact per-product size overrides must be valid JSON");
                            return;
                          }
                        }
                        importPayload = {
                          source_payload: {
                            impact_library_source: impactSource,
                            material_pricing_matrix: parsedPricing,
                            impact_available_sizes: parsedAvailableSizes,
                            impact_available_substrates: parsedAvailableSubstrates,
                            impact_option_templates: parsedOptionTemplates,
                            impact_global_margin_percent: Number(impactGlobalMarginPercent || 0),
                            impact_minimum_pricing_enabled: truthyConfigValue(impactMinimumPricingEnabled),
                            impact_quantity_margin_compression: parsedQuantityCompression,
                            impact_logo_customisation_enabled: truthyConfigValue(impactLogoCustomisationEnabled),
                            impact_logo_customisation_label: impactLogoCustomisationLabel,
                            impact_logo_customisation_price: Number(impactLogoCustomisationPrice || 0),
                            impact_logo_customisation_tooltip: impactLogoCustomisationTooltip,
                            impact_anti_graffiti_overlaminate_enabled: truthyConfigValue(impactAntiGraffitiEnabled),
                            impact_anti_graffiti_overlaminate_label: impactAntiGraffitiLabel,
                            impact_anti_graffiti_overlaminate_price_per_sqm: Number(impactAntiGraffitiPricePerSqm || 0),
                            impact_anti_graffiti_overlaminate_minimum_price: Number(impactAntiGraffitiMinimumPrice || 0),
                            impact_anti_graffiti_overlaminate_tooltip: impactAntiGraffitiTooltip,
                            impact_product_size_overrides: parsedSizeOverrides
                          }
                        };
                      }
                      setBusySupplier(`${supplier.supplier_key}:import`);
                      const result = await apiFetch<SupplierImportResult>(
                        `/shopify/admin/suppliers/${supplier.supplier_key}/catalog/import`,
                        { method: "POST", body: JSON.stringify(importPayload) }
                      );
                      setBusySupplier(null);
                      setMessage(supplierImportMessage(supplier.supplier_key, supplier.display_name, result));
                      await loadCatalog(supplier.supplier_key);
                    }}
                  >
                    Import catalog
                  </Button>
                  <Button
                    disabled={Boolean(busySupplier)}
                    loading={busySupplier === `${supplier.supplier_key}:catalog`}
                    onClick={() => loadCatalog(supplier.supplier_key)}
                  >
                    View catalog
                  </Button>
                </InlineStack>
                <InlineStack gap="200">
                  <Button
                    disabled={betaActionBlocked || Boolean(busySupplier)}
                    loading={busySupplier === `${supplier.supplier_key}:dry-run`}
                    onClick={async () => {
                      setBusySupplier(`${supplier.supplier_key}:dry-run`);
                      const result = await apiFetch<{ published: number }>(
                        `/shopify/admin/suppliers/${supplier.supplier_key}/products/publish`,
                        { method: "POST", body: JSON.stringify({ dry_run: true, publish_status: defaultPublishStatus }) }
                      );
                      setBusySupplier(null);
                      setMessage(result.error || `Dry-run built ${result.data?.published || 0} Shopify product payloads`);
                    }}
                  >
                    Dry-run publish
                  </Button>
                  <Button
                    variant="primary"
                    disabled={betaActionBlocked || Boolean(busySupplier)}
                    loading={busySupplier === `${supplier.supplier_key}:queue`}
                    onClick={async () => {
                      setBusySupplier(`${supplier.supplier_key}:queue`);
                      const result = await apiFetch<{ job_id: string; queue: string }>(
                        `/shopify/admin/suppliers/${supplier.supplier_key}/products/publish`,
                        { method: "POST", body: JSON.stringify({ dry_run: false, publish_status: defaultPublishStatus }) }
                      );
                      setBusySupplier(null);
                      setMessage(result.error || `Queued ${supplier.display_name} publish job ${result.data?.job_id || ""} on ${result.data?.queue || "product-sync"}`);
                    }}
                  >
                    Queue publish
                  </Button>
                </InlineStack>
                {catalogs[supplier.supplier_key]?.length ? (
                  <DataTable
                    columnContentTypes={["text", "text", "text", "text"]}
                    headings={["Product", "Supplier ID", "Variants", "Status"]}
                    rows={(catalogs[supplier.supplier_key] || []).slice(0, 5).map((product) => [
                      product.title,
                      product.supplier_product_id,
                      String(product.variants?.length || 0),
                      <StatusBadge status={product.status} />
                    ])}
                  />
                ) : null}
              </BlockStack>
            </Card>
          );
              })}
            </InlineGrid>
          </BlockStack>
        ) : null}
      </BlockStack>
  );
  if (embedded) return content;
  return (
    <Page title="Suppliers" subtitle="Tradeprint launch controls and supplier health" primaryAction={{ content: "Refresh", onAction: reload }}>
      {content}
    </Page>
  );
}

const certificationFlagLabels: Record<string, string> = {
  attributes_verified: "Attributes",
  quantities_verified: "Quantities",
  prices_verified: "Prices",
  preflight_verified: "Preflight",
  sandbox_order_verified: "Sandbox order",
  webhook_lifecycle_verified: "Webhook lifecycle",
  simulator_verified: "Order simulator"
};

function formatCertificationGap(flag: string) {
  return certificationFlagLabels[flag] || flag.replace(/_/g, " ");
}

function LaunchView({ embedded = false }: { embedded?: boolean }) {
  const { data, loading, error, reload } = useApiData<LaunchReadinessResponse>("/shopify/admin/launch-readiness", {
    ready: false,
    blockers: [],
    certifications: { total: 0, certified: 0, not_ready: 0, items: [] },
    recent_tradeprint_webhooks: [],
    manual_review_count: 0,
    production_gates: {
      tradeprint_require_launch_certification: false,
      tradeprint_order_submission_enabled: false
    }
  });
  const [certificationDraft, setCertificationDraft] = useState<Record<string, boolean | string>>({
    supplier_product_id: "",
    product_name: ""
  });
  const [simulatorDraft, setSimulatorDraft] = useState<Record<string, string>>({
    supplier_product_id: "",
    product_name: "",
    quantity: "250",
    service_level: "Standard",
    test_status: "Shipped",
    production_data_json: "{\n  \"Paper Type\": \"120gsm Premium Smooth White Paper\",\n  \"Sides Printed\": \"Single Sided\"\n}"
  });
  const [savingCertification, setSavingCertification] = useState(false);
  const [checkingCatalog, setCheckingCatalog] = useState(false);
  const [submittingSimulator, setSubmittingSimulator] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const content = (
    <BlockStack gap="400">
        <DismissibleNotice
          message={error ? `Launch readiness could not be loaded: ${error}` : null}
        />
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        {loading ? <LoadingState label="Loading launch readiness" /> : null}
        {!loading ? <Banner tone={data.ready ? "success" : "warning"}>
          {data.ready
            ? "Launch gates are clear for the currently certified Tradeprint products."
            : "Automatic Tradeprint fulfilment should remain gated until every blocker is cleared."}
        </Banner> : null}

        <InlineGrid columns={{ xs: 1, md: 4 }} gap="300">
          <MetricCard
            label="Launch status"
            value={data.ready ? "Ready" : "Blocked"}
            trend={data.ready ? "go-live" : "manual review"}
          />
          <MetricCard
            label="Certified products"
            value={`${data.certifications.certified}/${data.certifications.total}`}
            trend="Tradeprint"
          />
          <MetricCard
            label="Manual reviews"
            value={String(data.manual_review_count)}
            trend={data.manual_review_count ? "attention" : "clear"}
          />
          <MetricCard
            label="Supplier submission"
            value={data.production_gates.tradeprint_order_submission_enabled ? "Enabled" : "Paused"}
            trend={data.production_gates.tradeprint_require_launch_certification ? "certified only" : "config gate"}
          />
        </InlineGrid>

        <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Go-live blockers
              </Text>
              {data.blockers.length ? (
                <List>
                  {data.blockers.map((blocker) => (
                    <List.Item key={blocker}>{blocker.replace(/_/g, " ")}</List.Item>
                  ))}
                </List>
              ) : (
                <Text as="p" tone="subdued">
                  No launch blockers are currently reported by the backend.
                </Text>
              )}
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Production gates
              </Text>
              <InlineStack align="space-between">
                <Text as="span">Tradeprint order submission</Text>
                <StatusBadge status={data.production_gates.tradeprint_order_submission_enabled ? "Enabled" : "Paused"} />
              </InlineStack>
              <InlineStack align="space-between">
                <Text as="span">Require product certification</Text>
                <StatusBadge status={data.production_gates.tradeprint_require_launch_certification ? "Required" : "Optional"} />
              </InlineStack>
              {!data.production_gates.tradeprint_require_launch_certification ? (
                <Banner tone="warning">
                  Enable certification-required mode in Setup before live automatic fulfilment.
                </Banner>
              ) : null}
            </BlockStack>
          </Card>
        </InlineGrid>

        <details className="admin-accordion">
          <summary>Record certification evidence</summary>
          <div className="admin-accordion__body">
            <FormLayout>
              <TextField
                label="Tradeprint product ID"
                value={String(certificationDraft.supplier_product_id || "")}
                onChange={(value) => setCertificationDraft((current) => ({ ...current, supplier_product_id: value }))}
                autoComplete="off"
              />
              <TextField
                label="Product name"
                value={String(certificationDraft.product_name || "")}
                onChange={(value) => setCertificationDraft((current) => ({ ...current, product_name: value }))}
                autoComplete="off"
              />
              <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                {Object.entries(certificationFlagLabels).map(([flag, label]) => (
                  <Checkbox
                    key={flag}
                    label={label}
                    checked={Boolean(certificationDraft[flag])}
                    onChange={(value) => setCertificationDraft((current) => ({ ...current, [flag]: value }))}
                  />
                ))}
              </InlineGrid>
              <TextField
                label="Evidence note"
                value={String(certificationDraft.evidence_note || "")}
                onChange={(value) => setCertificationDraft((current) => ({ ...current, evidence_note: value }))}
                autoComplete="off"
                multiline={3}
              />
              <Button
                variant="primary"
                loading={savingCertification}
                disabled={savingCertification || !String(certificationDraft.supplier_product_id || "").trim()}
                onClick={async () => {
                  const productId = String(certificationDraft.supplier_product_id || "").trim();
                  setSavingCertification(true);
                  const result = await apiFetch(`/shopify/admin/launch-readiness/products/${encodeURIComponent(productId)}`, {
                    method: "PUT",
                    body: JSON.stringify({
                      product_name: String(certificationDraft.product_name || "").trim() || undefined,
                      attributes_verified: Boolean(certificationDraft.attributes_verified),
                      quantities_verified: Boolean(certificationDraft.quantities_verified),
                      prices_verified: Boolean(certificationDraft.prices_verified),
                      preflight_verified: Boolean(certificationDraft.preflight_verified),
                      sandbox_order_verified: Boolean(certificationDraft.sandbox_order_verified),
                      webhook_lifecycle_verified: Boolean(certificationDraft.webhook_lifecycle_verified),
                      simulator_verified: Boolean(certificationDraft.simulator_verified),
                      evidence: {
                        admin_note: String(certificationDraft.evidence_note || "").trim()
                      }
                    })
                  });
                  setSavingCertification(false);
                  if (result.error) {
                    setMessage(result.error);
                    return;
                  }
                  setMessage(`Certification evidence saved for ${productId}`);
                  void reload();
                }}
              >
                Save certification evidence
              </Button>
            </FormLayout>
          </div>
        </details>

        <details className="admin-accordion">
          <summary>Run sandbox order simulator</summary>
          <div className="admin-accordion__body">
            <FormLayout>
              <TextField
                label="Tradeprint product ID"
                value={simulatorDraft.supplier_product_id}
                onChange={(value) => setSimulatorDraft((current) => ({ ...current, supplier_product_id: value }))}
                autoComplete="off"
              />
              <TextField
                label="Product name"
                value={simulatorDraft.product_name}
                onChange={(value) => setSimulatorDraft((current) => ({ ...current, product_name: value }))}
                autoComplete="off"
              />
              <InlineGrid columns={{ xs: 1, md: 3 }} gap="200">
                <TextField
                  label="Quantity"
                  type="number"
                  value={simulatorDraft.quantity}
                  onChange={(value) => setSimulatorDraft((current) => ({ ...current, quantity: value }))}
                  autoComplete="off"
                />
                <Select
                  label="Service level"
                  options={["Same Day", "Express", "Standard", "Saver"]}
                  value={simulatorDraft.service_level}
                  onChange={(value) => setSimulatorDraft((current) => ({ ...current, service_level: value }))}
                />
                <Select
                  label="Simulator state"
                  options={["Shipped", "DocReviewRejected", "Cancelled"]}
                  value={simulatorDraft.test_status}
                  onChange={(value) => setSimulatorDraft((current) => ({ ...current, test_status: value }))}
                />
              </InlineGrid>
              <TextField
                label="Supplier productionData JSON"
                value={simulatorDraft.production_data_json}
                onChange={(value) => setSimulatorDraft((current) => ({ ...current, production_data_json: value }))}
                autoComplete="off"
                multiline={5}
              />
              <Button
                loading={checkingCatalog}
                disabled={checkingCatalog || submittingSimulator || !simulatorDraft.supplier_product_id.trim()}
                onClick={async () => {
                  const productId = simulatorDraft.supplier_product_id.trim();
                  let productionData: Record<string, string>;
                  try {
                    productionData = JSON.parse(simulatorDraft.production_data_json || "{}");
                  } catch {
                    setMessage("Supplier productionData JSON is not valid");
                    return;
                  }
                  setCheckingCatalog(true);
                  const result = await apiFetch(
                    `/shopify/admin/launch-readiness/products/${encodeURIComponent(productId)}/catalog-check`,
                    {
                      method: "POST",
                      body: JSON.stringify({
                        product_name: simulatorDraft.product_name.trim() || undefined,
                        quantity: Number(simulatorDraft.quantity || 0),
                        service_level: simulatorDraft.service_level,
                        production_data: productionData
                      })
                    }
                  );
                  setCheckingCatalog(false);
                  if (result.error) {
                    setMessage(result.error);
                    return;
                  }
                  setMessage(`Supplier catalogue evidence recorded for ${productId}`);
                  void reload();
                }}
              >
                Verify attributes, quantities and prices
              </Button>
              <Button
                variant="primary"
                loading={submittingSimulator}
                disabled={submittingSimulator || checkingCatalog || !simulatorDraft.supplier_product_id.trim()}
                onClick={async () => {
                  const productId = simulatorDraft.supplier_product_id.trim();
                  let productionData: Record<string, string>;
                  try {
                    productionData = JSON.parse(simulatorDraft.production_data_json || "{}");
                  } catch {
                    setMessage("Supplier productionData JSON is not valid");
                    return;
                  }
                  setSubmittingSimulator(true);
                  const result = await apiFetch(
                    `/shopify/admin/launch-readiness/products/${encodeURIComponent(productId)}/simulator`,
                    {
                      method: "POST",
                      body: JSON.stringify({
                        product_name: simulatorDraft.product_name.trim() || undefined,
                        quantity: Number(simulatorDraft.quantity || 0),
                        service_level: simulatorDraft.service_level,
                        test_status: simulatorDraft.test_status,
                        production_data: productionData
                      })
                    }
                  );
                  setSubmittingSimulator(false);
                  if (result.error) {
                    setMessage(result.error);
                    return;
                  }
                  setMessage(`Simulator evidence recorded for ${productId}/${simulatorDraft.test_status}`);
                  void reload();
                }}
              >
                Run simulator scenario
              </Button>
            </FormLayout>
          </div>
        </details>

        <details className="admin-accordion" open>
          <summary>Launch product certification</summary>
          <div className="admin-accordion__body">
            {data.certifications.items.length ? (
              <div className="admin-record-list">
                {data.certifications.items.map((item) => (
                  <article className="admin-record" key={item.supplier_product_id}>
                    <InlineStack align="space-between" gap="200">
                      <BlockStack gap="100">
                        <Text as="h3" variant="headingSm">
                          {item.product_name || item.supplier_product_id}
                        </Text>
                        <Text as="span" tone="subdued">
                          {item.supplier_product_id}
                        </Text>
                      </BlockStack>
                      <StatusBadge status={item.status} />
                    </InlineStack>
                    <div className="admin-record__meta">
                      <span>
                        <strong>Missing</strong>
                        {item.missing.length
                          ? item.missing.map(formatCertificationGap).join(", ")
                          : "All checks recorded"}
                      </span>
                      {item.missing_evidence.length ? (
                        <span>
                          <strong>Evidence required</strong>
                          {item.missing_evidence.map(formatCertificationGap).join(", ")}
                        </span>
                      ) : null}
                      <span>
                        <strong>Last checked</strong>
                        {item.last_checked_at || item.updated_at || "Not checked"}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <Text as="p" tone="subdued">
                No products certified yet.
              </Text>
            )}
          </div>
        </details>

        <details className="admin-accordion">
          <summary>Recent Tradeprint webhook events</summary>
          <div className="admin-accordion__body">
            {data.recent_tradeprint_webhooks.length ? (
              <div className="admin-record-list">
                {data.recent_tradeprint_webhooks.map((event) => (
                  <article className="admin-record" key={event.webhook_notification_id}>
                    <InlineStack align="space-between" gap="200">
                      <BlockStack gap="100">
                        <Text as="h3" variant="headingSm">
                          {event.order_reference || "Unknown order"}
                        </Text>
                        <Text as="span" tone="subdued">
                          {event.webhook_notification_id}
                        </Text>
                      </BlockStack>
                      <StatusBadge status={event.processed_status} />
                    </InlineStack>
                    <div className="admin-record__meta">
                      <span>
                        <strong>Item</strong>
                        {event.item_reference || "Unknown"}
                      </span>
                      <span>
                        <strong>Supplier status</strong>
                        {event.status || "Unknown"}
                      </span>
                      <span>
                        <strong>Received</strong>
                        {event.received_at || "Not recorded"}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <Text as="p" tone="subdued">
                No Tradeprint webhook events recorded.
              </Text>
            )}
          </div>
        </details>
      </BlockStack>
  );
  if (embedded) return content;
  return (
    <Page
      title="Launch"
      subtitle="Tradeprint production readiness and go-live gates"
      primaryAction={{ content: "Refresh", onAction: reload, loading }}
    >
      {content}
    </Page>
  );
}

function SetupView({
  context,
  embedded = false
}: {
  context: AdminContext;
  embedded?: boolean;
}) {
  const location = useBrowserLocation();
  const requestedSetupSection = location.searchParams.get("section");
  const setupSectionIds = ["checklist", "shopify", "stores", "suppliers", "launch"];
  const { data, loading, error, reload } = useApiData<SetupStatus>("/shopify/admin/setup/status", {
    backend: "error",
    shopify_session: "not_verified",
    shopify_admin_api: "missing",
    supplier_credentials: "missing",
    app_proxy: "missing",
    webhook_secret: "missing",
    embedded_app_url: null
  });
  const [config, setConfig] = useState<SetupConfig>({
    tradeprint_environment: "sandbox",
    printconnect_theme_template_name: "personalised-print"
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedSetupTab, setSelectedSetupTab] = useState(() => {
    const requestedIndex = setupSectionIds.indexOf(requestedSetupSection || "");
    return requestedIndex >= 0 ? requestedIndex : 0;
  });
  useEffect(() => {
    const requestedIndex = setupSectionIds.indexOf(requestedSetupSection || "");
    if (requestedIndex >= 0) setSelectedSetupTab(requestedIndex);
  }, [requestedSetupSection]);

  useEffect(() => {
    if (data.config) {
      setConfig((current) => ({ ...current, ...data.config }));
    }
  }, [data.config]);

  const setupItems = [
    { label: "Backend API reachable", done: data.backend === "ready" },
    { label: "Embedded Shopify session verified", done: data.shopify_session === "verified" },
    { label: "Shopify Admin API connected", done: data.shopify_admin_api === "connected" },
    { label: "Supplier sandbox/live credentials configured", done: data.supplier_credentials === "configured" },
    { label: "App proxy URL configured", done: data.app_proxy === "configured" },
    { label: "Webhook secret configured", done: data.webhook_secret === "configured" },
    { label: "Tradeprint launch certification gate enabled", done: Boolean(config.tradeprint_require_launch_certification) },
    { label: "Public/SaaS mode selected", done: !isCustomDistributionMode(data.distribution_mode) },
    {
      label: data.billing?.required ? "Shopify Billing subscription active" : "Billing configured",
      done: data.billing?.active === true || data.billing_mode === "free"
    },
    { label: "Protected customer data acknowledged", done: data.protected_customer_data === "acknowledged" },
    { label: "Privacy policy URL configured", done: Boolean(config.shopify_privacy_policy_url) },
    { label: "Terms URL configured", done: Boolean(config.shopify_terms_url) },
    { label: "Support URL configured", done: Boolean(config.shopify_support_url) },
    { label: "Support email configured", done: Boolean(config.support_email) }
  ];
  const customDistribution = isCustomDistributionMode(data.distribution_mode);
  const connectShop = customInstallShopDomain(
    data.distribution_mode,
    config.shopify_store_domain
  );
  const themeEditorUrl = buildProductConfiguratorThemeEditorUrl(
    context.shop.shop_domain,
    configuredShopifyPublicApiKey()
  );
  const allSetupItemsDone = setupItems.every((item) => item.done);
  const setupTabs = [
    { id: "checklist", content: "Checklist", panelID: "setup-checklist" },
    { id: "shopify", content: "Shopify", panelID: "setup-shopify" },
    { id: "stores", content: "Stores", panelID: "setup-stores" },
    { id: "suppliers", content: "Suppliers", panelID: "setup-suppliers" },
    { id: "launch", content: "Launch", panelID: "setup-launch" }
  ];

  const content = (
    <BlockStack gap="400">
        {loading ? <LoadingState label="Loading setup status" /> : null}
        <Tabs
          tabs={setupTabs}
          selected={selectedSetupTab}
          onSelect={(nextTab) => {
            setSelectedSetupTab(nextTab);
            const sectionId = setupTabs[nextTab]?.id || "checklist";
            window.history.replaceState(
              null,
              "",
              `/settings?tab=setup&section=${encodeURIComponent(sectionId)}`
            );
          }}
          fitted
        />
        {setupTabs[selectedSetupTab]?.id === "checklist" ? (
          <details className="admin-accordion" open={!allSetupItemsDone}>
            <summary>
              Launch checklist
              {allSetupItemsDone ? <StatusBadge status="Confirmed" /> : <StatusBadge status="Needs setup" />}
            </summary>
            <div className="admin-accordion__body">
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">
                    Launch checklist
                  </Text>
                  <Button onClick={reload} loading={loading}>
                    Refresh checks
                  </Button>
                </InlineStack>
                <DismissibleNotice
                  message={error ? `Using local fallback because setup status failed: ${error}` : null}
                />
                <BlockStack gap="300">
                  {setupItems.map((item) => (
                    <Checkbox key={item.label} label={item.label} checked={item.done} disabled />
                  ))}
                </BlockStack>
              </BlockStack>
            </div>
          </details>
        ) : setupTabs[selectedSetupTab]?.id === "stores" ? (
          <StoresView embedded />
        ) : setupTabs[selectedSetupTab]?.id === "suppliers" ? (
          <SuppliersView embedded />
        ) : setupTabs[selectedSetupTab]?.id === "launch" ? (
          <LaunchView embedded />
        ) : (
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Setup inputs
              </Text>
              <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
              <FormLayout>
                {!customDistribution ? (
                  <Banner tone="info">
                    Shopify manages installation, store identity, and access tokens for this public app.
                  </Banner>
                ) : (
                  <>
                    <TextField
                      label="Shopify API key"
                      value={config.shopify_api_key || ""}
                      onChange={(value) => setConfig((current) => ({ ...current, shopify_api_key: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Shopify API secret"
                      type="password"
                      value={config.shopify_api_secret || ""}
                      onChange={(value) => setConfig((current) => ({ ...current, shopify_api_secret: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Webhook secret"
                      type="password"
                      value={config.shopify_webhook_secret || ""}
                      onChange={(value) => setConfig((current) => ({ ...current, shopify_webhook_secret: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Shopify store domain"
                      value={config.shopify_store_domain || ""}
                      placeholder="example.myshopify.com"
                      onChange={(value) => setConfig((current) => ({ ...current, shopify_store_domain: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Embedded app URL"
                      value={config.shopify_embedded_app_url || ""}
                      placeholder="https://your-app-domain"
                      onChange={(value) => setConfig((current) => ({ ...current, shopify_embedded_app_url: value }))}
                      autoComplete="off"
                    />
                  </>
                )}
                <TextField
                  label="PrintConnect theme template"
                  value={config.printconnect_theme_template_name ?? "personalised-print"}
                  placeholder="personalised-print"
                  onChange={(value) =>
                    setConfig((current) => ({ ...current, printconnect_theme_template_name: value }))
                  }
                  autoComplete="off"
                />
                <Checkbox
                  label="Submit eligible paid orders to Tradeprint automatically"
                  checked={config.tradeprint_order_submission_enabled !== false}
                  onChange={(value) =>
                    setConfig((current) => ({ ...current, tradeprint_order_submission_enabled: value }))
                  }
                  helpText="Turn this off to hold eligible Tradeprint orders in manual review during incidents or launch checks."
                />
                <Checkbox
                  label="Require launch certification before automatic Tradeprint submission"
                  checked={Boolean(config.tradeprint_require_launch_certification)}
                  onChange={(value) =>
                    setConfig((current) => ({ ...current, tradeprint_require_launch_certification: value }))
                  }
                  helpText="Products without recorded attributes, quantities, prices, preflight, sandbox order, webhook, and simulator evidence are held for manual review."
                />
                <Checkbox
                  label="Enable Tradeprint launch workstream"
                  checked={config.enable_tradeprint !== false}
                  onChange={(value) =>
                    setConfig((current) => ({ ...current, enable_tradeprint: value }))
                  }
                  helpText="Core PrintConnect supplier bridge for automatic paid supplier submission."
                />
                <Checkbox
                  label="Enable Impact Health & Safety Signs"
                  checked={config.enable_impact_signs !== false}
                  onChange={(value) =>
                    setConfig((current) => ({ ...current, enable_impact_signs: value }))
                  }
                  helpText="Launch-scope Impact sign catalogue, product publishing, storefront configuration, and local fulfilment."
                />
                <Checkbox
                  label="Enable PenCarrie/apparel"
                  checked={Boolean(config.enable_pencarrie)}
                  onChange={(value) =>
                    setConfig((current) => ({ ...current, enable_pencarrie: value }))
                  }
                  helpText="Keep off until apparel is separately reviewed and approved for launch."
                />
                <Button
                  variant="primary"
                  loading={saving}
                  onClick={async () => {
                    setSaving(true);
                    const result = await apiFetch<{ saved: boolean }>("/shopify/admin/setup/config", {
                      method: "PUT",
                      body: JSON.stringify(setupConfigForSave(config, data.distribution_mode))
                    });
                    setSaving(false);
                    setMessage(result.error || "Setup saved");
                    void reload();
                  }}
                >
                  Save setup
                </Button>
                {customDistribution ? <Button
                  disabled={!connectShop}
                  onClick={async () => {
                    if (!isCustomDistributionMode(data.distribution_mode)) {
                      setMessage("Custom Shopify connection is unavailable in public distribution");
                      return;
                    }
                    setSaving(true);
                    const configToSave = setupConfigForSave(config, data.distribution_mode);
                    const saveResult = await apiFetch<{ saved: boolean }>("/shopify/admin/setup/config", {
                      method: "PUT",
                      body: JSON.stringify(configToSave)
                    });
                    setSaving(false);
                    if (saveResult.error) {
                      setMessage(`Shopify connection could not start: ${saveResult.error}`);
                      return;
                    }
                    setConfig((current) => ({ ...current, ...configToSave }));
                    const installUrl = `/api/shopify/auth/install?shop=${encodeURIComponent(connectShop)}`;
                    const opened = window.open(installUrl, "_top");
                    if (!opened) {
                      setMessage(`Shopify connection could not start: open ${installUrl}`);
                      return;
                    }
                  }}
                >
                  Connect Shopify Admin API
                </Button> : null}
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Add the Print configurator
              </Text>
              <Text as="p">
                Add the app block to the Default product template in an Online Store 2.0 theme.
              </Text>
              <List type="number">
                <List.Item>Choose and sync the product you want to sell.</List.Item>
                <List.Item>Open the Theme Editor using the button below.</List.Item>
                <List.Item>Preview the Print configurator on Default product.</List.Item>
                <List.Item>Move the block to another position if desired.</List.Item>
                <List.Item>Save the theme change.</List.Item>
                <List.Item>
                  Open a configured product and verify its options, cart, and draft checkout.
                </List.Item>
              </List>
              {themeEditorUrl ? (
                <>
                  <Button
                    url={themeEditorUrl}
                    external
                    accessibilityLabel="Open Theme Editor for the Default product template (opens in a new tab)"
                  >
                    Open Theme Editor
                  </Button>
                  <Banner tone="info">
                    This link opens a preview for {context.shop.shop_domain}. It does not publish the
                    block until you select Save in the Theme Editor.
                  </Banner>
                </>
              ) : (
                <Banner tone="warning">
                  The Theme Editor link is unavailable because the authenticated store domain or
                  configured app client ID could not be verified. Use the manual steps below.
                </Banner>
              )}
              <Text as="h3" variant="headingSm">
                Manual setup
              </Text>
              <Text as="p">
                Online Store &gt; Themes &gt; Customize &gt; Products &gt; Default product &gt; Add
                block &gt; Apps &gt; Print configurator &gt; Save.
              </Text>
              <Text as="p" tone="subdued">
                The theme must support Online Store 2.0 app blocks. Storefront requests use the
                signed app proxy at <code>/apps/print-connector</code>.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
        )}
    </BlockStack>
  );
  if (embedded) return content;
  return (
    <Page title="Setup" subtitle="Merchant onboarding for the print connector">
      {content}
    </Page>
  );
}

type ConfigurationTemplateDraft = {
  id?: string;
  name: string;
  description: string;
  optionValues: Record<string, string>;
  addOns: string[];
  quantity: string;
  serviceLevel: string;
  isPublished: boolean;
  sortOrder: string;
};

function emptyConfigurationTemplateDraft(): ConfigurationTemplateDraft {
  return {
    name: "",
    description: "",
    optionValues: {},
    addOns: [],
    quantity: "",
    serviceLevel: "",
    isPublished: false,
    sortOrder: "0"
  };
}

function ConfigurationTemplateManager({
  product,
  productId,
  title
}: {
  product: AdminProduct;
  productId: string;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<ConfigurationTemplate[]>([]);
  const [draft, setDraft] = useState<ConfigurationTemplateDraft>(
    emptyConfigurationTemplateDraft
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    loading: boolean;
    result?: ConfigurationTemplatePreview | null;
    error?: string | null;
  }>({ loading: false });
  const rules = visibleProductOptionRules(product.selection?.parity_config);
  const services = productEnabledServiceLevels(product);
  const addOns = cleanStringValues(
    product.selection?.parity_config?.add_ons
  );
  const supplier = productSupplierTab(product);
  const supportedSupplier =
    supplier === "tradeprint" ||
    supplier === "impactconnect" ||
    supplier === "pencarrie";

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setMessage(null);
    const result = await apiFetch<{
      templates: ConfigurationTemplate[];
      limit: number;
    }>(
      `/shopify/admin/configuration-templates?product_id=${encodeURIComponent(productId)}`
    );
    setLoading(false);
    if (!result.data) {
      setMessage(
        result.error || "Configuration templates could not be loaded."
      );
      return;
    }
    setTemplates(result.data.templates);
  }, [productId]);

  const beginEdit = (template: ConfigurationTemplate) => {
    setDraft({
      id: template.id,
      name: template.name,
      description: template.description || "",
      optionValues: { ...template.production_data },
      addOns: [...template.add_ons],
      quantity: String(template.quantity),
      serviceLevel: template.service_level,
      isPublished: template.is_published,
      sortOrder: String(template.sort_order)
    });
    setPreview({ loading: false });
    setMessage(null);
  };

  const updateDraft = (
    updater: (current: ConfigurationTemplateDraft) => ConfigurationTemplateDraft
  ) => {
    setDraft(updater);
    setPreview({ loading: false });
  };

  const buildPayload = () => {
    const normalizedName = draft.name.replace(/\s+/g, " ").trim();
    const quantity = Number(draft.quantity);
    const sortOrder = Number(draft.sortOrder);
    const productionData = Object.fromEntries(
      rules
        .map((rule) => [rule.attribute, draft.optionValues[rule.attribute] || ""])
        .filter(([, value]) => Boolean(value))
    );
    if (!normalizedName) {
      return { error: "Enter a customer-facing template name." };
    }
    if (
      rules.some((rule) => !draft.optionValues[rule.attribute]) ||
      !Object.keys(productionData).length
    ) {
      return { error: "Choose every print option before checking the template." };
    }
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100000) {
      return { error: "Enter a template quantity from 1 to 100,000." };
    }
    if (!services.includes(draft.serviceLevel)) {
      return { error: "Choose an available delivery service." };
    }
    if (!Number.isInteger(sortOrder) || sortOrder < 0 || sortOrder > 1000) {
      return { error: "Display order must be a whole number from 0 to 1,000." };
    }
    const canonicalHandle = String(
      product.selection?.canonical_handle || ""
    ).trim();
    const productPath = /^[a-z0-9][a-z0-9_-]*$/.test(canonicalHandle)
      ? `/products/${canonicalHandle}`
      : null;
    return {
      payload: {
        name: normalizedName,
        description: draft.description,
        product_id: productId,
        product_name: title,
        product_path: productPath,
        supplier_key: supplier,
        configurator_mode:
          supplier === "impactconnect" ? "impact_sign" : "dynamic_print",
        production_data: productionData,
        add_ons: draft.addOns,
        quantity,
        service_level: draft.serviceLevel,
        is_published: draft.isPublished,
        sort_order: sortOrder
      }
    };
  };

  const previewCombination = async () => {
    const built = buildPayload();
    if (!built.payload) {
      setPreview({ loading: false, error: built.error });
      return;
    }
    setPreview({ loading: true });
    const result = await apiFetch<{ preview: ConfigurationTemplatePreview }>(
      "/shopify/admin/configuration-templates/validate",
      {
        method: "POST",
        body: JSON.stringify(built.payload)
      }
    );
    if (!result.data) {
      setPreview({
        loading: false,
        error:
          result.error ||
          "Current availability could not be checked. Try again shortly."
      });
      return;
    }
    setPreview({ loading: false, result: result.data.preview });
  };

  const save = async () => {
    const built = buildPayload();
    if (!built.payload) {
      setMessage(built.error || "Complete the template before saving.");
      return;
    }
    setSaving(true);
    setMessage(null);
    const result = await apiFetch<{ template: ConfigurationTemplate }>(
      draft.id
        ? `/shopify/admin/configuration-templates/${encodeURIComponent(draft.id)}`
        : "/shopify/admin/configuration-templates",
      {
        method: draft.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(built.payload)
      }
    );
    setSaving(false);
    if (!result.data) {
      setMessage(result.error || "The template could not be saved.");
      return;
    }
    setDraft(emptyConfigurationTemplateDraft());
    setPreview({ loading: false });
    await load();
    setMessage(
      result.data.template.is_published
        ? "Template saved and published."
        : "Template saved as a draft."
    );
  };

  const remove = async (template: ConfigurationTemplate) => {
    if (!window.confirm(`Delete the template "${template.name}"?`)) return;
    setSaving(true);
    setMessage(null);
    const result = await apiFetch<{ deleted: boolean }>(
      `/shopify/admin/configuration-templates/${encodeURIComponent(template.id)}`,
      { method: "DELETE" }
    );
    setSaving(false);
    if (!result.data) {
      setMessage(result.error || "The template could not be deleted.");
      return;
    }
    if (draft.id === template.id) {
      setDraft(emptyConfigurationTemplateDraft());
      setPreview({ loading: false });
    }
    await load();
    setMessage("Template deleted.");
  };

  if (!supportedSupplier) return null;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          <BlockStack gap="100">
            <Text as="h4" variant="headingSm">
              Customer starting templates
            </Text>
            <Text as="p" tone="subdued">
              Offer ready-made option combinations. Availability and price are
              checked before publication and again when a customer chooses one.
            </Text>
          </BlockStack>
          <Button
            onClick={() => {
              const next = !open;
              setOpen(next);
              if (next && !templates.length) void load();
            }}
            ariaExpanded={open}
          >
            {open ? "Close templates" : "Manage templates"}
          </Button>
        </InlineStack>
        {open ? (
          <BlockStack gap="300">
            {loading ? <LoadingState label="Loading templates" /> : null}
            <DismissibleNotice message={message} />
            {templates.length ? (
              <DataTable
                columnContentTypes={["text", "text", "numeric", "text"]}
                headings={["Template", "Status", "Order", "Actions"]}
                rows={templates.map((template) => [
                  <BlockStack key={template.id} gap="050">
                    <Text as="span" fontWeight="semibold">
                      {template.name}
                    </Text>
                    <Text as="span" tone="subdued">
                      Qty {template.quantity} / {template.service_level}
                    </Text>
                  </BlockStack>,
                  template.is_published ? "Published" : "Draft",
                  template.sort_order,
                  <InlineStack key={`${template.id}-actions`} gap="100">
                    <Button
                      size="micro"
                      disabled={saving}
                      onClick={() => beginEdit(template)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="micro"
                      disabled={saving}
                      onClick={() => void remove(template)}
                    >
                      Delete
                    </Button>
                  </InlineStack>
                ])}
              />
            ) : !loading ? (
              <Text as="p" tone="subdued">
                No templates have been created for this product.
              </Text>
            ) : null}
            <Text as="h5" variant="headingSm">
              {draft.id ? "Edit template" : "New template"}
            </Text>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
              <TextField
                label="Template name"
                value={draft.name}
                maxLength={80}
                autoComplete="off"
                disabled={saving}
                onChange={(value) => updateDraft((current) => ({ ...current, name: value }))}
              />
              <TextField
                label="Short description"
                value={draft.description}
                maxLength={240}
                autoComplete="off"
                disabled={saving}
                onChange={(value) =>
                  updateDraft((current) => ({ ...current, description: value }))
                }
              />
              {rules.map((rule) => (
                <Select
                  key={rule.key}
                  label={rule.label}
                  options={[
                    { label: "Choose an option", value: "" },
                    ...rule.values.map((value) => ({ label: value, value }))
                  ]}
                  value={draft.optionValues[rule.attribute] || ""}
                  disabled={saving}
                  onChange={(value) =>
                    updateDraft((current) => ({
                      ...current,
                      optionValues: {
                        ...current.optionValues,
                        [rule.attribute]: value
                      }
                    }))
                  }
                />
              ))}
              <TextField
                label="Quantity"
                type="number"
                value={draft.quantity}
                min={1}
                max={100000}
                autoComplete="off"
                disabled={saving}
                onChange={(value) =>
                  updateDraft((current) => ({ ...current, quantity: value }))
                }
              />
              <Select
                label="Delivery service"
                options={[
                  { label: "Choose a service", value: "" },
                  ...services.map((service) => ({ label: service, value: service }))
                ]}
                value={draft.serviceLevel}
                disabled={saving}
                onChange={(value) =>
                  updateDraft((current) => ({ ...current, serviceLevel: value }))
                }
              />
              <TextField
                label="Display order"
                type="number"
                value={draft.sortOrder}
                min={0}
                max={1000}
                autoComplete="off"
                helpText="Lower numbers appear first."
                disabled={saving}
                onChange={(value) =>
                  updateDraft((current) => ({ ...current, sortOrder: value }))
                }
              />
            </InlineGrid>
            {addOns.length ? (
              <BlockStack gap="100">
                <Text as="p" fontWeight="semibold">
                  Add-ons
                </Text>
                {addOns.map((addOn) => (
                  <Checkbox
                    key={addOn}
                    label={addOn}
                    checked={draft.addOns.includes(addOn)}
                    disabled={saving}
                    onChange={(checked) =>
                      updateDraft((current) => ({
                        ...current,
                        addOns: checked
                          ? [...current.addOns, addOn]
                          : current.addOns.filter((item) => item !== addOn)
                      }))
                    }
                  />
                ))}
              </BlockStack>
            ) : null}
            {preview.error ? (
              <Banner tone="critical">
                Combination check failed: {preview.error}
              </Banner>
            ) : null}
            {preview.result ? (
              <Banner tone="success">
                This exact option, quantity and delivery combination is currently
                available
                {preview.result.currency
                  ? ` at ${formatCommercialMoney(
                      preview.result.sell_price,
                      preview.result.currency
                    )}`
                  : ""}
                . The storefront will check it again when a customer uses the
                template.
              </Banner>
            ) : null}
            <Checkbox
              label="Publish this template to the storefront"
              checked={draft.isPublished}
              disabled={saving}
              onChange={(checked) =>
                updateDraft((current) => ({ ...current, isPublished: checked }))
              }
            />
            <InlineStack gap="200">
              <Button
                loading={preview.loading}
                disabled={saving || loading || preview.loading || !rules.length}
                onClick={() => void previewCombination()}
              >
                Check combination
              </Button>
              <Button
                variant="primary"
                loading={saving}
                disabled={saving || loading || !rules.length}
                onClick={() => void save()}
              >
                {draft.id ? "Update template" : "Create template"}
              </Button>
              {draft.id ? (
                <Button
                  disabled={saving}
                  onClick={() => {
                    setDraft(emptyConfigurationTemplateDraft());
                    setPreview({ loading: false });
                  }}
                >
                  Cancel editing
                </Button>
              ) : null}
            </InlineStack>
          </BlockStack>
        ) : null}
      </BlockStack>
    </Card>
  );
}

function ProductsView() {
  const initialProductParams = new URL(window.location.href).searchParams;
  const requestedSupplierTab = initialProductParams.get("supplier");
  const requestedSupplierIndex = productSupplierTabs.findIndex(
    (tab) => tab.id === requestedSupplierTab
  );
  const [message, setMessage] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedProductTab, setSelectedProductTab] = useState(
    requestedSupplierIndex >= 0 ? requestedSupplierIndex : 0
  );
  const [updatingProduct, setUpdatingProduct] = useState<string | null>(null);
  const [productFilter, setProductFilter] = useState(
    initialProductParams.get("search") || ""
  );
  const [marginDrafts, setMarginDrafts] = useState<Record<string, string>>({});
  const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({});
  const [lightboxImage, setLightboxImage] = useState<{ src: string; title: string } | null>(null);
  const lightboxTriggerRef = useRef<HTMLButtonElement | null>(null);
  const lightboxPanelRef = useRef<HTMLDivElement | null>(null);
  const [deliveryDrafts, setDeliveryDrafts] = useState<Record<string, Record<string, string>>>({});
  const [collectionDrafts, setCollectionDrafts] = useState<Record<string, string[]>>({});
  const [newCollectionDrafts, setNewCollectionDrafts] = useState<Record<string, string>>({});
  const [impactTemplateDrafts, setImpactTemplateDrafts] = useState<Record<string, string>>({});
  const [impactAssignmentDrafts, setImpactAssignmentDrafts] = useState<Record<string, ImpactAssignmentDraft>>({});
  const [channelDrafts, setChannelDrafts] = useState<Record<string, Record<string, string>>>({});
  const [selectionFilter, setSelectionFilter] = useState("all");
  const [syncStatusFilter, setSyncStatusFilter] = useState("all");
  const [readinessFilter, setReadinessFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [signCategoryFilter, setSignCategoryFilter] = useState("all");
  const [productSort, setProductSort] = useState("attention");
  const [productSortDirection, setProductSortDirection] = useState("asc");
  const [productOffset, setProductOffset] = useState(0);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [creatingInHouse, setCreatingInHouse] = useState(false);
  const activeProductTab = productSupplierTabs[selectedProductTab]?.id || "tradeprint";
  const debouncedProductFilter = useDebouncedValue(productFilter, 250);
  const productPageLimit = 50;
  const selectProductTab = (selectedTab: number) => {
    setSelectedProductTab(selectedTab);
    setProductFilter("");
    setSelectionFilter("all");
    setSyncStatusFilter("all");
    setReadinessFilter("all");
    setSourceFilter("all");
    setSignCategoryFilter("all");
    setProductSort("attention");
    setProductSortDirection("asc");
  };
  const productsPath = useMemo(() => {
    const params = new URLSearchParams({
      supplier: activeProductTab,
      limit: String(productPageLimit),
      offset: String(productOffset),
      sort: productSort,
      direction: productSortDirection
    });
    if (debouncedProductFilter.trim()) params.set("search", debouncedProductFilter.trim());
    if (selectionFilter !== "all") params.set("selection", selectionFilter);
    params.set("sync_status", syncStatusFilter);
    if (readinessFilter !== "all") params.set("readiness", readinessFilter);
    if (sourceFilter !== "all") params.set("source", sourceFilter);
    if (activeProductTab === "impactconnect" && signCategoryFilter !== "all") params.set("sign_category", signCategoryFilter);
    return `/shopify/admin/products?${params.toString()}`;
  }, [
    activeProductTab,
    debouncedProductFilter,
    productOffset,
    productSort,
    productSortDirection,
    readinessFilter,
    selectionFilter,
    signCategoryFilter,
    sourceFilter,
    syncStatusFilter
  ]);
  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
    window.requestAnimationFrame(() => lightboxTriggerRef.current?.focus());
  }, []);
  useEffect(() => {
    if (!lightboxImage) return;
    const focusFrame = window.requestAnimationFrame(() => {
      const firstControl =
        lightboxPanelRef.current?.querySelector<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || lightboxPanelRef.current;
      firstControl?.focus();
    });
    const handleDialogKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeLightbox();
        return;
      }
      if (event.key !== "Tab" || !lightboxPanelRef.current) return;
      const controls = Array.from(
        lightboxPanelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!controls.length) {
        event.preventDefault();
        lightboxPanelRef.current.focus();
        return;
      }
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleDialogKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleDialogKeyDown);
    };
  }, [closeLightbox, lightboxImage]);
  const { data, setData, loading, error, reload } = useApiData<{
    products: AdminProduct[];
    catalog_quality?: {
      total: number;
      ready: number;
      needs_review: number;
      blocked: number;
      average_score: number;
    };
    product_groups?: {
      total_groups: number;
      ungrouped_products: number;
      duplicate_canonical_handles: string[];
      groups: Array<{
        group_id: string;
        title: string;
        canonical_handle?: string | null;
        product_count: number;
        ready: number;
        needs_review: number;
        blocked: number;
        primary_product_id?: string | null;
        warnings: string[];
      }>;
    };
    catalog_sources?: {
      supplier_product_count: number;
      supplier_catalog_product_count?: number;
      supplier_import_runs?: Record<string, SupplierImportRun | null>;
      imported_selection_count: number;
      total_merged: number;
      total_filtered?: number;
      selected_filtered?: number;
      selections_missing_from_supplier: number;
      missing_from_supplier_sample: string[];
      filter_options?: {
        sources?: Array<{ label: string; value: string }>;
        sign_categories?: Array<{ label: string; value: string }>;
      };
      impact_settings?: {
        available_sizes?: ImpactSizeOption[];
        available_substrates?: string[];
        option_templates?: ImpactOptionTemplate[];
        global_margin_percent?: number | string;
        quantity_margin_compression?: Array<Record<string, number>>;
        logo_customisation_enabled?: boolean;
        logo_customisation_label?: string;
        logo_customisation_price?: number;
        anti_graffiti_overlaminate_enabled?: boolean;
        anti_graffiti_overlaminate_label?: string;
        anti_graffiti_overlaminate_price_per_sqm?: number;
        anti_graffiti_overlaminate_minimum_price?: number;
        minimum_pricing_enabled?: boolean;
      };
      pagination?: {
        offset: number;
        limit: number;
        returned: number;
        total: number;
        has_previous: boolean;
        has_next: boolean;
      } | null;
    };
    pagination?: {
      offset: number;
      limit: number;
      returned: number;
      total: number;
      has_previous: boolean;
      has_next: boolean;
    } | null;
    global_margin_percent: number;
    warning?: string | null;
    shopify_collections?: ShopifyCollection[];
    shopify_collections_warning?: string | null;
  }>(
    productsPath,
    { products: [], global_margin_percent: 0 }
  );
  useEffect(() => {
    setProductOffset(0);
  }, [
    activeProductTab,
    debouncedProductFilter,
    productSort,
    productSortDirection,
    readinessFilter,
    selectionFilter,
    signCategoryFilter,
    sourceFilter,
    syncStatusFilter
  ]);
  const [inHouseDraft, setInHouseDraft] = useState({
    title: "",
    product_id: "",
    category: "Custom Print",
    collections: "",
    margin_percent: "0",
    canonical_handle: ""
  });
  const [inHouseStep, setInHouseStep] = useState(0);
  const [inHouseStepErrors, setInHouseStepErrors] = useState<string[]>([]);
  const [inHouseOptionDrafts, setInHouseOptionDrafts] = useState<InHouseOptionDraft[]>(
    initialInHouseOptionDrafts
  );
  const [inHousePriceDrafts, setInHousePriceDrafts] = useState<InHousePriceDraft[]>(
    initialInHousePriceDrafts
  );
  const inHouseOptionChoices = useMemo(
    () =>
      inHouseOptionDrafts.map((draft) => ({
        id: draft.id,
        name: draft.name.trim(),
        values: cleanStringValues(draft.values.split(","))
      })),
    [inHouseOptionDrafts]
  );
  const inHouseOptionSchema = useMemo(
    () => inHouseOptionSchemaFromDrafts(inHouseOptionDrafts),
    [inHouseOptionDrafts]
  );
  const inHousePriceRows = useMemo(
    () => inHousePriceRowsFromDrafts(inHousePriceDrafts),
    [inHousePriceDrafts]
  );
  const inHouseFromPrice = useMemo(() => {
    const prices = inHousePriceRows.flatMap((row) =>
      ["Saver", "Standard", "Express"]
        .map((service) => Number(row[service]))
        .filter((price) => Number.isFinite(price) && price > 0)
    );
    return prices.length ? Math.min(...prices) : null;
  }, [inHousePriceRows]);
  const pagination = data.pagination || data.catalog_sources?.pagination || null;
  const impactAvailableSizeOptions = parseImpactSizes(
    data.catalog_sources?.impact_settings?.available_sizes || DEFAULT_IMPACT_SIZES
  );
  const impactAvailableSubstrateOptions = parseImpactSubstrates(
    data.catalog_sources?.impact_settings?.available_substrates || DEFAULT_IMPACT_SUBSTRATES
  );
  const impactOptionTemplates = parseImpactOptionTemplates(
    data.catalog_sources?.impact_settings?.option_templates || []
  );
  const impactImportRun = data.catalog_sources?.supplier_import_runs?.impactconnect;
  const impactImportSummary = impactImportRun?.source_summary;
  const impactCatalogQuality = impactImportSummary?.catalog_quality;
  const impactLogoSettings = data.catalog_sources?.impact_settings || {};
  const totalMatchingProducts = pagination?.total ?? data.catalog_sources?.total_filtered ?? data.products.length;
  const pageStart = totalMatchingProducts ? (pagination?.offset ?? 0) + 1 : 0;
  const pageEnd = Math.min((pagination?.offset ?? 0) + data.products.length, totalMatchingProducts);
  const productCounts = useMemo(() => {
    const selected = data.catalog_sources?.selected_filtered ?? data.products.filter((product) => product.selection?.included).length;
    const ready = data.catalog_quality?.ready ?? data.products.filter((product) => product.catalog_validation?.status === "ready").length;
    const blocked = data.catalog_quality?.blocked ?? data.products.filter((product) => product.catalog_validation?.status === "blocked").length;
    return {
      selected,
      ready,
      blocked,
      needsAttention: data.catalog_quality
        ? data.catalog_quality.needs_review + data.catalog_quality.blocked
        : Math.max(data.products.length - ready, 0),
      averageScore: data.catalog_quality?.average_score ?? 0
    };
  }, [data.catalog_quality, data.catalog_sources?.selected_filtered, data.products]);

  const filteredProducts = useMemo(() => data.products, [data.products]);

  const signCategoryOptions = useMemo(() => {
    const options = data.catalog_sources?.filter_options?.sign_categories || [];
    const seen = new Set<string>();
    return [
      { label: "All sign categories", value: "all" },
      ...options
        .filter((option) => {
          const value = String(option.value || option.label || "").trim();
          if (!value || seen.has(value)) return false;
          seen.add(value);
          return true;
        })
        .map((option) => ({ label: option.label, value: option.value }))
    ];
  }, [data.catalog_sources?.filter_options?.sign_categories]);

  const syncStatusOptions = [
    { label: "All", value: "all" },
    { label: "Unsynced", value: "unsynced" },
    { label: "Synced", value: "synced" }
  ];

  const sourceOptions = useMemo(() => {
    const configuredSources = data.catalog_sources?.filter_options?.sources || [];
    const sources = configuredSources.length
      ? configuredSources
      : [...new Set(data.products.map((product) => String(product.catalog_source || "imported_selection")))]
          .sort()
          .map((source) => ({ label: productSourceLabel(source), value: source }));
    return [
      { label: "All sources", value: "all" },
      ...sources.map((source) => ({
        label: source.label || productSourceLabel(source.value),
        value: source.value
      }))
    ];
  }, [data.catalog_sources?.filter_options?.sources, data.products]);

  const filteredProductIds = useMemo(
    () =>
      filteredProducts
        .map((product) => String(product.id || product.selection?.product_id || ""))
        .filter(Boolean),
    [filteredProducts]
  );

  const availableCollections = useMemo(() => {
    const names = new Set<string>();
    (data.shopify_collections || []).forEach((collection) => {
      const clean = String(collection.title || "").trim();
      if (clean) names.add(clean);
    });
    data.products.forEach((product) => {
      (product.selection?.collections || []).forEach((collection) => {
        const clean = String(collection || "").trim();
        if (clean) names.add(clean);
      });
    });
    return [...names].sort((first, second) => first.localeCompare(second));
  }, [data.products, data.shopify_collections]);

  const productSelectionPayload = (
    product: AdminProduct,
    overrides: {
      included?: boolean;
      margin_percent?: number;
      collections?: string[];
      parity_config?: Record<string, unknown>;
      product_group_title?: string;
    } = {}
  ) => {
    const selection = product.selection;
    return {
      included: overrides.included ?? Boolean(selection?.included),
      margin_percent: overrides.margin_percent ?? selection?.margin_percent ?? data.global_margin_percent ?? 0,
      category: selection?.category || null,
      collections: overrides.collections ?? selection?.collections ?? [],
      product_type: selection?.product_type || "simple",
      variant_margins: selection?.variant_margins || {},
      parity_config: overrides.parity_config ?? selection?.parity_config ?? {},
      product_group_id: selection?.product_group_id || null,
      product_group_title: overrides.product_group_title ?? selection?.product_group_title ?? null,
      product_group_role: selection?.product_group_role || "primary",
      canonical_handle: selection?.canonical_handle || null
    };
  };

  const impactConfigForProduct = (product: AdminProduct) => {
    const config = product.selection?.parity_config || {};
    const impact = config.impact && typeof config.impact === "object" ? (config.impact as Record<string, unknown>) : {};
    const logoSetting = impact.logo_customisation_enabled;
    const antiGraffitiSetting = impact.anti_graffiti_overlaminate_enabled;
    const minimumPricingSetting = impact.minimum_pricing_enabled;
    return {
      sizes: parseImpactSizes(impact.sizes),
      substrates: parseImpactSubstrates(impact.substrates || impact.materials),
      minimumPricingEnabled:
        minimumPricingSetting === undefined || minimumPricingSetting === null
          ? ""
          : truthyConfigValue(minimumPricingSetting)
            ? "enabled"
            : "disabled",
      logoCustomisationEnabled:
        logoSetting === undefined || logoSetting === null
          ? truthyConfigValue(impactLogoSettings.logo_customisation_enabled)
          : truthyConfigValue(logoSetting),
      antiGraffitiOverlaminateEnabled:
        antiGraffitiSetting === undefined || antiGraffitiSetting === null
          ? impactLogoSettings.anti_graffiti_overlaminate_enabled === undefined
            ? true
            : truthyConfigValue(impactLogoSettings.anti_graffiti_overlaminate_enabled)
          : truthyConfigValue(antiGraffitiSetting)
    };
  };

  const impactTemplateLabel = (template: ImpactOptionTemplate) => {
    const sizes = parseImpactSizes(template.sizes);
    const substrates = parseImpactSubstrates(template.substrates);
    const parts = [
      sizes.length ? `${sizes.length} sizes` : "",
      substrates.length ? `${substrates.length} substrates` : ""
    ].filter(Boolean);
    return parts.length ? `${template.name} (${parts.join(", ")})` : template.name;
  };

  const applyImpactOptionTemplate = async (product: AdminProduct, templateName: string) => {
    const template = impactOptionTemplates.find((item) => item.name === templateName);
    const productId = String(product.id || product.selection?.product_id || "");
    if (!template) {
      setMessage("Choose an Impact option template to apply");
      return;
    }
    await saveImpactAssignments(product, {
      sizes: parseImpactSizes(template.sizes),
      substrates: parseImpactSubstrates(template.substrates)
    });
    setImpactTemplateDrafts((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
  };

  const saveImpactAssignments = async (
    product: AdminProduct,
    assignments: ImpactAssignmentDraft
  ) => {
    const productId = String(product.id || product.selection?.product_id || "");
    const currentDraft = impactAssignmentDrafts[productId] || {};
    const nextDraft: ImpactAssignmentDraft = { ...currentDraft, ...assignments };
    setImpactAssignmentDrafts((current) => ({
      ...current,
      [productId]: { ...(current[productId] || {}), ...assignments }
    }));
    const currentConfig = product.selection?.parity_config || {};
    const currentImpact = currentConfig.impact && typeof currentConfig.impact === "object" ? (currentConfig.impact as Record<string, unknown>) : {};
    const nextImpact: Record<string, unknown> = {
      ...currentImpact,
      ...(nextDraft.sizes !== undefined ? { sizes: nextDraft.sizes } : {}),
      ...(nextDraft.substrates !== undefined ? { substrates: nextDraft.substrates } : {}),
      ...(nextDraft.logoCustomisationEnabled !== undefined
        ? { logo_customisation_enabled: nextDraft.logoCustomisationEnabled }
        : {}),
      ...(nextDraft.antiGraffitiOverlaminateEnabled !== undefined
        ? { anti_graffiti_overlaminate_enabled: nextDraft.antiGraffitiOverlaminateEnabled }
        : {})
    };
    if (nextDraft.minimumPricingEnabled !== undefined) {
      if (nextDraft.minimumPricingEnabled) {
        nextImpact.minimum_pricing_enabled = nextDraft.minimumPricingEnabled === "enabled";
      } else {
        delete nextImpact.minimum_pricing_enabled;
      }
    }
    await saveProductSelection(product, {
      parity_config: {
        ...currentConfig,
        impact: nextImpact,
        delivery: {
          enabled_service_levels: ["Standard"]
        }
      }
    });
  };

  const saveProductSelection = async (
    product: AdminProduct,
    overrides: {
      included?: boolean;
      margin_percent?: number;
      collections?: string[];
      parity_config?: Record<string, unknown>;
      product_group_title?: string;
    } = {}
  ) => {
    const selection = product.selection;
    const productId = String(product.id || selection?.product_id || "");
    setUpdatingProduct(productId);
    const result = await apiFetch<NonNullable<AdminProduct["selection"]>>(`/shopify/admin/products/${encodeURIComponent(productId)}`, {
      method: "PUT",
      body: JSON.stringify(productSelectionPayload(product, overrides))
    });
    setUpdatingProduct(null);
    if (result.data) {
      setData((current) => ({
        ...current,
        products: current.products.map((item) => {
          const itemId = String(item.id || item.selection?.product_id || "");
          const savedTitle = String(result.data?.product_group_title || "").trim();
          return itemId === productId
            ? {
                ...item,
                ...(savedTitle ? { name: savedTitle, title: savedTitle } : {}),
                selection: result.data || item.selection
              }
            : item;
        })
      }));
    }
    setMessage(result.error || `Updated ${product.name || productId}`);
    void reload();
    return Boolean(result.data && !result.error);
  };

  const setFilteredProductSelection = async (included: boolean) => {
    if (!filteredProductIds.length) {
      setMessage("No products match the current filters");
      return;
    }
    setBulkUpdating(true);
    try {
      const updates = filteredProducts
        .map((product) => {
          const productId = String(product.id || product.selection?.product_id || "");
          return productId
            ? {
                product_id: productId,
                ...productSelectionPayload(product, { included })
              }
            : null;
        })
        .filter(Boolean);
      const result = await apiFetch<{ updated: number }>("/shopify/admin/products", {
        method: "PUT",
        body: JSON.stringify({ updates })
      });
      setMessage(result.error || `${included ? "Selected" : "Deselected"} ${result.data?.updated || filteredProductIds.length} filtered products`);
      void reload();
    } finally {
      setBulkUpdating(false);
    }
  };

  const saveProductCollections = async (product: AdminProduct, collections: string[]) => {
    const selection = product.selection;
    const productId = String(product.id || selection?.product_id || "");
    const cleanCollections = cleanCollectionList(collections);
    await saveProductSelection(product, { collections: cleanCollections });
    setCollectionDrafts((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
    setNewCollectionDrafts((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
  };

  const saveProductMargin = async (product: AdminProduct, rawMargin: string) => {
    const selection = product.selection;
    const productId = String(product.id || selection?.product_id || "");
    const margin = Number(rawMargin);
    if (!Number.isFinite(margin) || margin < 0 || margin > 500) {
      setMessage("Margin must be a number between 0 and 500");
      return;
    }
    await saveProductSelection(product, { margin_percent: margin });
    setMarginDrafts((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
  };

  const saveProductTitle = async (product: AdminProduct, rawTitle: string) => {
    const selection = product.selection;
    const productId = String(product.id || selection?.product_id || "");
    const title = rawTitle.replace(/\s+/g, " ").trim();
    if (!title) {
      setMessage("Product title is required");
      return;
    }
    if (title.length > 255) {
      setMessage("Product title must be 255 characters or fewer");
      return;
    }
    await saveProductSelection(product, { product_group_title: title });
    setTitleDrafts((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
  };

  const saveProductDeliveryPadding = async (product: AdminProduct, drafts: Record<string, string>) => {
    const selection = product.selection;
    const productId = String(product.id || selection?.product_id || "");
    const servicePadding: Record<string, number> = {};
    for (const service of ["Saver", "Standard", "Express"]) {
      const rawValue = String(drafts[service] ?? "").trim();
      if (!rawValue) continue;
      const value = Number(rawValue);
      if (!Number.isFinite(value) || value < 0 || value > 30) {
        setMessage("Delivery padding must be between 0 and 30 days");
        return;
      }
      servicePadding[service] = value;
    }
    const currentConfig = selection?.parity_config || {};
    const currentDelivery =
      currentConfig.delivery && typeof currentConfig.delivery === "object"
        ? (currentConfig.delivery as Record<string, unknown>)
        : {};
    const parityConfig = {
      ...currentConfig,
      delivery: {
        ...currentDelivery,
        service_level_padding_days: servicePadding
      }
    };
    await saveProductSelection(product, { parity_config: parityConfig });
    setDeliveryDrafts((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
  };

  const saveProductChannelSettings = async (product: AdminProduct, drafts: Record<string, string>) => {
    const selection = product.selection;
    const productId = String(product.id || selection?.product_id || "");
    const isImpact = productSupplierTab(product) === "impactconnect";
    const currentConfig = selection?.parity_config || {};
    const currentChannelMetadata =
      currentConfig.channel_metadata && typeof currentConfig.channel_metadata === "object"
        ? (currentConfig.channel_metadata as Record<string, Record<string, unknown>>)
        : {};
    const currentCommerce =
      currentConfig.commerce && typeof currentConfig.commerce === "object"
        ? (currentConfig.commerce as Record<string, unknown>)
        : {};
    const currentContent =
      currentConfig.content && typeof currentConfig.content === "object"
        ? (currentConfig.content as Record<string, unknown>)
        : {};
    const currentFulfillment =
      currentConfig.fulfillment && typeof currentConfig.fulfillment === "object"
        ? (currentConfig.fulfillment as Record<string, unknown>)
        : {};
    const currentMedia =
      currentContent.media && typeof currentContent.media === "object"
        ? (currentContent.media as Record<string, unknown>)
        : {};
    const clean = (key: string) => String(drafts[key] ?? nestedStringValue(currentConfig, CHANNEL_FIELD_PATHS[key]) ?? "").trim();
    const taxableRaw = clean("taxable").toLowerCase();
    const taxable = taxableRaw === "true" || taxableRaw === "yes" ? true : taxableRaw === "false" || taxableRaw === "no" ? false : null;
    const parityConfig = {
      ...currentConfig,
      commerce: {
        ...currentCommerce,
        taxable
      },
      content: {
        ...currentContent,
        media: {
          ...currentMedia,
          image_url: clean("image_url"),
          asset_policy: clean("asset_policy")
        }
      },
      fulfillment: {
        ...currentFulfillment,
        route: clean("fulfillment_route") || (isImpact ? "local_fulfillment" : "supplier")
      },
      channel_metadata: {
        ...currentChannelMetadata,
        google_merchant: {
          ...(currentChannelMetadata.google_merchant || {}),
          google_product_category: clean("google_product_category"),
          identifier_policy: clean("identifier_policy")
        },
        amazon: {
          ...(currentChannelMetadata.amazon || {}),
          marketplace_category: clean("amazon_category")
        },
        etsy: {
          ...(currentChannelMetadata.etsy || {}),
          marketplace_category: clean("etsy_category")
        },
        ebay: {
          ...(currentChannelMetadata.ebay || {}),
          marketplace_category: clean("ebay_category")
        },
        meta: {
          ...(currentChannelMetadata.meta || {}),
          marketplace_category: clean("meta_category")
        },
        tiktok: {
          ...(currentChannelMetadata.tiktok || {}),
          marketplace_category: clean("tiktok_category")
        },
        shopify: {
          ...(currentChannelMetadata.shopify || {}),
          shopify_taxonomy_category_id: clean("shopify_taxonomy_category_id")
        }
      }
    };
    await saveProductSelection(product, { parity_config: parityConfig });
    setChannelDrafts((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
  };

  const importCreatePriceCsv = async (file: File | null) => {
    if (!file) return;
    const rows = parseInHousePriceCsv(await file.text());
    if (!rows.length) {
      setMessage("Price CSV did not contain any valid rows. Use Quantity, Saver, Standard, Express, and Option: columns.");
      return;
    }
    setInHousePriceDrafts(inHousePriceDraftsFromRows(rows));
    setInHouseStepErrors([]);
    setMessage(`Loaded ${rows.length} price rows from CSV`);
  };

  const updateInHouseOption = (
    optionId: string,
    patch: Partial<Pick<InHouseOptionDraft, "name" | "values">>
  ) => {
    const currentOption = inHouseOptionDrafts.find((option) => option.id === optionId);
    const previousName = currentOption?.name.trim() || "";
    const nextName = patch.name?.trim() || previousName;
    setInHouseOptionDrafts((current) =>
      current.map((option) => (option.id === optionId ? { ...option, ...patch } : option))
    );
    if (patch.name !== undefined && previousName && nextName !== previousName) {
      setInHousePriceDrafts((current) =>
        current.map((row) => {
          if (!(previousName in row.optionValues)) return row;
          const optionValues = { ...row.optionValues };
          const selectedValue = optionValues[previousName];
          delete optionValues[previousName];
          if (nextName) optionValues[nextName] = selectedValue;
          return { ...row, optionValues };
        })
      );
    }
    setInHouseStepErrors([]);
  };

  const removeInHouseOption = (optionId: string) => {
    const removedName =
      inHouseOptionDrafts.find((option) => option.id === optionId)?.name.trim() || "";
    setInHouseOptionDrafts((current) =>
      current.filter((option) => option.id !== optionId)
    );
    if (removedName) {
      setInHousePriceDrafts((current) =>
        current.map((row) => {
          const optionValues = { ...row.optionValues };
          delete optionValues[removedName];
          return { ...row, optionValues };
        })
      );
    }
    setInHouseStepErrors([]);
  };

  const validateInHouseStep = (step: number): string[] => {
    const errors: string[] = [];
    if (step === 0) {
      if (!inHouseDraft.title.trim()) errors.push("Enter a product title.");
      const margin = Number(inHouseDraft.margin_percent);
      if (!Number.isFinite(margin) || margin < 0 || margin > 500) {
        errors.push("Margin must be between 0% and 500%.");
      }
    }
    if (step === 1) {
      if (!inHouseOptionDrafts.length) {
        errors.push("Add at least one print option.");
      }
      const names = new Set<string>();
      inHouseOptionDrafts.forEach((option, index) => {
        const name = option.name.trim();
        const values = cleanStringValues(option.values.split(","));
        if (!name) errors.push(`Option ${index + 1} needs a name.`);
        if (!values.length) errors.push(`${name || `Option ${index + 1}`} needs at least one value.`);
        const key = name.toLowerCase();
        if (key && names.has(key)) errors.push(`Option name “${name}” is used more than once.`);
        if (key) names.add(key);
      });
    }
    if (step === 2) {
      if (!inHousePriceDrafts.length) {
        errors.push("Add at least one quantity and price row.");
      }
      const optionValues = new Map(
        inHouseOptionChoices.map((option) => [option.name, new Set(option.values)])
      );
      const rowKeys = new Set<string>();
      inHousePriceDrafts.forEach((row, index) => {
        const quantity = Number(row.quantity);
        if (!Number.isInteger(quantity) || quantity <= 0 || quantity > 1000000) {
          errors.push(`Price row ${index + 1} needs a whole quantity between 1 and 1,000,000.`);
        }
        const prices = [row.saver, row.standard, row.express];
        if (!prices.some((value) => Number(value) > 0)) {
          errors.push(`Price row ${index + 1} needs at least one positive delivery price.`);
        }
        prices.forEach((value) => {
          if (value.trim() && (!Number.isFinite(Number(value)) || Number(value) <= 0)) {
            errors.push(`Price row ${index + 1} contains an invalid price.`);
          }
        });
        Object.entries(row.optionValues).forEach(([name, value]) => {
          if (!value) return;
          if (!optionValues.get(name)?.has(value)) {
            errors.push(`Price row ${index + 1} has an unavailable ${name} value.`);
          }
        });
        const selection = Object.entries(row.optionValues)
          .filter(([, value]) => value)
          .sort(([first], [second]) => first.localeCompare(second));
        const rowKey = JSON.stringify([quantity, selection]);
        if (rowKeys.has(rowKey)) {
          errors.push(`Price row ${index + 1} duplicates another quantity and option combination.`);
        }
        rowKeys.add(rowKey);
      });
    }
    return [...new Set(errors)];
  };

  const advanceInHouseStep = () => {
    const errors = validateInHouseStep(inHouseStep);
    setInHouseStepErrors(errors);
    if (!errors.length) {
      setInHouseStep((current) =>
        Math.min(current + 1, IN_HOUSE_CREATION_STEPS.length - 1)
      );
    }
  };

  const uploadProductPriceCsv = async (product: AdminProduct, file: File | null) => {
    if (!file) return;
    const productId = String(product.id || product.selection?.product_id || "");
    const selection = product.selection;
    if (!productId || !selection) {
      setMessage("Product selection could not be found for this price upload");
      return;
    }
    const rows = parseInHousePriceCsv(await file.text());
    if (!rows.length) {
      setMessage("Price CSV did not contain any valid rows. Use Quantity, Saver, Standard, Express, and Option: columns.");
      return;
    }
    const currentConfig = selection.parity_config || {};
    const currentCustomPrint =
      currentConfig.custom_print && typeof currentConfig.custom_print === "object"
        ? (currentConfig.custom_print as Record<string, unknown>)
        : {};
    await saveProductSelection(product, {
      parity_config: {
        ...currentConfig,
        custom_print: {
          ...currentCustomPrint,
          source: "in_house",
          pricing_mode: "manual_grid",
          price_rows: rows
        }
      }
    });
    setMessage(`Updated ${rows.length} in-house price rows for ${product.name || product.title || productId}`);
  };

  const createInHouseProduct = async () => {
    const title = inHouseDraft.title.trim();
    const stageErrors = [0, 1, 2].map((step) => validateInHouseStep(step));
    const firstInvalidStep = stageErrors.findIndex((errors) => errors.length);
    if (firstInvalidStep >= 0) {
      setInHouseStep(firstInvalidStep);
      setInHouseStepErrors(stageErrors[firstInvalidStep]);
      return;
    }
    setCreatingInHouse(true);
    try {
      const result = await apiFetch<{ product_id: string }>("/shopify/admin/products/in-house", {
        method: "POST",
        body: JSON.stringify({
          title,
          product_id: inHouseDraft.product_id.trim() || null,
          category: inHouseDraft.category.trim() || null,
          collections: cleanCollectionList(inHouseDraft.collections.split(",")),
          margin_percent: Number(inHouseDraft.margin_percent || 0),
          canonical_handle: inHouseDraft.canonical_handle.trim() || null,
          option_schema: inHouseOptionSchema,
          price_rows: inHousePriceRows,
          include_in_sync: true
        })
      });
      if (result.error) {
        setMessage(result.error);
      } else {
        setMessage(`Created in-house print product ${result.data?.product_id || title}`);
        setInHouseDraft({
          title: "",
          product_id: "",
          category: "Custom Print",
          collections: "",
          margin_percent: "0",
          canonical_handle: ""
        });
        setInHouseOptionDrafts(initialInHouseOptionDrafts());
        setInHousePriceDrafts(initialInHousePriceDrafts());
        setInHouseStepErrors([]);
        setInHouseStep(0);
        void reload();
      }
    } finally {
      setCreatingInHouse(false);
    }
  };
  const importTradeprintCatalog = async () => {
    setImporting(true);
    try {
      const result = await apiFetch<{ imported: number }>("/shopify/admin/catalog/import-products-v2", {
        method: "POST",
        body: JSON.stringify({})
      });
      setMessage(result.error || `Imported ${result.data?.imported || 0} Tradeprint products`);
      void reload();
    } finally {
      setImporting(false);
    }
  };

  const importImpactCatalog = async () => {
    setImporting(true);
    try {
      const result = await apiFetch<SupplierImportResult>("/shopify/admin/suppliers/impactconnect/catalog/import", {
        method: "POST",
        body: JSON.stringify({})
      });
      setMessage(supplierImportMessage("impactconnect", "Impact sign", result));
      void reload();
    } finally {
      setImporting(false);
    }
  };

  const syncProductsForSupplier = async (supplierKey: string, productIds: string[]) => {
    const selectedIds = productIds.filter((productId, index, ids) => productId && ids.indexOf(productId) === index);
    if (!selectedIds.length) {
      setMessage("No products are selected for sync");
      return;
    }
    setSyncing(true);
    try {
      if (supplierKey === "tradeprint") {
        const result = await apiFetch<{
          job_id: string;
          status_url: string;
        }>("/shopify/products/sync", {
          method: "POST",
          body: JSON.stringify({ tradeprint_product_ids: selectedIds, publish_status: "DRAFT", dry_run: false })
        });
        if (result.error) {
          setMessage(result.error);
        } else {
          const statusUrl = result.data?.status_url ? ` (${result.data.status_url})` : "";
          setMessage(`Queued product sync job ${result.data?.job_id || ""}${statusUrl}`);
        }
      } else {
        const result = await apiFetch<{ queued?: boolean; job_id?: string; queue?: string; status_url?: string; published?: number }>(
          `/shopify/admin/suppliers/${supplierKey}/products/publish`,
          {
            method: "POST",
            body: JSON.stringify({
              supplier_product_ids: selectedIds,
              publish_status: "DRAFT",
              dry_run: false
            })
          }
        );
        const statusUrl = result.data?.status_url ? ` (${result.data.status_url})` : "";
        setMessage(
          result.error ||
            (result.data?.queued
              ? `Queued ${selectedIds.length} supplier products on ${result.data.queue || "product-sync"}${statusUrl}`
              : `Published ${result.data?.published || 0} supplier products`)
        );
      }
      void reload();
    } finally {
      setSyncing(false);
    }
  };

  const refreshAllSyncedProducts = async () => {
    setSyncing(true);
    try {
      const result = await apiFetch<{
        queued?: boolean;
        selected?: number;
        queued_selected?: number;
        failed_supplier_count?: number;
        partial?: boolean;
        jobs?: Array<{
          supplier_key?: string;
          selected?: number;
          job_id?: string;
          status_url?: string;
          skipped?: boolean;
          error?: string;
        }>;
      }>("/shopify/admin/sync-synced-products", {
        method: "POST",
        body: JSON.stringify({ publish_status: "DRAFT", dry_run: false })
      });
      if (result.error) {
        setMessage(result.error);
        return;
      }
      const jobs = result.data?.jobs || [];
      const queuedJobs = jobs.filter((job) => job.job_id);
      const skippedJobs = jobs.filter((job) => job.skipped || job.error);
      if (queuedJobs.length) {
        const queuedSelected =
          result.data?.queued_selected ??
          queuedJobs.reduce((total, job) => total + (job.selected || 0), 0);
        const suppliers = queuedJobs
          .map((job) => `${job.supplier_key || "supplier"}: ${job.selected || 0}`)
          .join(", ");
        const skipped = skippedJobs.length
          ? `; ${skippedJobs.length} supplier group${skippedJobs.length === 1 ? "" : "s"} skipped`
          : "";
        setMessage(`Queued refresh for ${queuedSelected} synced products (${suppliers})${skipped}`);
      } else {
        setMessage(skippedJobs[0]?.error || "No synced products are available to refresh");
      }
      void reload();
    } finally {
      setSyncing(false);
    }
  };

  const currentPage = Math.floor((pagination?.offset || 0) / productPageLimit) + 1;
  const totalPages = Math.max(Math.ceil((totalMatchingProducts || 0) / productPageLimit), 1);
  const paginationControls = pagination ? (
    <div className="admin-pagination">
      <Text as="p" tone="subdued">
        Showing {pageStart}-{pageEnd} of {totalMatchingProducts} · Page {currentPage} of {totalPages}
      </Text>
      <InlineStack gap="200">
        <Button
          disabled={!pagination.has_previous || loading}
          onClick={() => setProductOffset(Math.max((pagination.offset || 0) - productPageLimit, 0))}
        >
          Previous
        </Button>
        <Button
          disabled={!pagination.has_next || loading}
          onClick={() => setProductOffset((pagination.offset || 0) + productPageLimit)}
        >
          Next
        </Button>
      </InlineStack>
    </div>
  ) : null;

  return (
    <Page
      title="Products"
      primaryAction={{
        content: syncing ? "Syncing selected products" : "Sync selected products",
        disabled: syncing || importing,
        onAction: async () => {
          const activeSupplier = productSupplierTabs[selectedProductTab]?.id || "tradeprint";
          const selectedIds = data.products
            .filter((product) => productSupplierTab(product) === activeSupplier && Boolean(product.selection?.included))
            .map((product) => String(product.supplier_product_id || product.id || product.selection?.product_id || ""))
            .filter((productId, index, ids) => productId && ids.indexOf(productId) === index);
          await syncProductsForSupplier(activeSupplier, selectedIds);
        }
      }}
    >
      <BlockStack gap="400">
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        <DismissibleNotice message={error ? `Products could not be loaded: ${error}` : null} />
        {loading && !data.products.length ? <LoadingState label="Loading products" /> : null}
        <DismissibleNotice tone="info" message={data.warning} />
        <DismissibleNotice message={data.shopify_collections_warning} />
        <InlineGrid columns={{ xs: 1, md: 4 }} gap="300">
          <MetricCard label="Selected for sync" value={String(productCounts.selected)} trend="draft" />
          <MetricCard label="Product groups" value={String(data.product_groups?.total_groups ?? 0)} trend={data.product_groups?.ungrouped_products ? "review" : "ready"} />
          <MetricCard label="Catalogue score" value={`${productCounts.averageScore}/100`} trend="quality" />
          <MetricCard label="Needs attention" value={String(productCounts.needsAttention)} trend={productCounts.blocked ? "blocked" : "review"} />
        </InlineGrid>
        {data.product_groups?.ungrouped_products ? (
          <Banner tone="warning">
            {data.product_groups.ungrouped_products} products are not assigned to a canonical product group.
          </Banner>
        ) : null}
        {data.product_groups?.duplicate_canonical_handles.length ? (
          <Banner tone="warning">
            Duplicate canonical handles across product groups: {data.product_groups.duplicate_canonical_handles.join(", ")}
          </Banner>
        ) : null}
        {data.catalog_sources ? (
          <Banner tone={data.catalog_sources.selections_missing_from_supplier ? "warning" : "info"}>
            Tradeprint product list: {data.catalog_sources.supplier_product_count}. Supplier catalogue products:{" "}
            {data.catalog_sources.supplier_catalog_product_count ?? 0}. Imported Products v2 selections:{" "}
            {data.catalog_sources.imported_selection_count}. Surfaced here: {data.catalog_sources.total_merged}.
            {data.catalog_sources.selections_missing_from_supplier
              ? ` ${data.catalog_sources.selections_missing_from_supplier} imported selections were not returned by the supplier product list and are still shown.`
              : " All imported selections are represented."}
          </Banner>
        ) : null}
        {data.catalog_sources?.supplier_import_runs?.impactconnect ? (
          <Banner tone={(data.catalog_sources.supplier_import_runs.impactconnect.imported_count ?? 0) > 0 ? "info" : "warning"}>
            Impact last import: raw{" "}
            {data.catalog_sources.supplier_import_runs.impactconnect.source_summary?.raw_product_count ?? "unknown"} · imported{" "}
            {data.catalog_sources.supplier_import_runs.impactconnect.imported_count ?? 0} · variants{" "}
            {data.catalog_sources.supplier_import_runs.impactconnect.variant_count ?? 0} · archived{" "}
            {data.catalog_sources.supplier_import_runs.impactconnect.archived_count ?? 0} · store{" "}
            {data.catalog_sources.supplier_import_runs.impactconnect.source_summary?.store_key || "unknown"}
            {data.catalog_sources.supplier_import_runs.impactconnect.source_summary?.source
              ? ` · source ${data.catalog_sources.supplier_import_runs.impactconnect.source_summary.source}`
              : ""}
          </Banner>
        ) : null}
        {activeProductTab === "impactconnect" && impactCatalogQuality ? (
          <Banner
            tone={
              impactCatalogQuality.status === "ready"
                ? "success"
                : impactCatalogQuality.status === "blocked"
                  ? "critical"
                  : "warning"
            }
          >
            Impact catalogue quality: {impactCatalogQuality.status === "ready" ? "ready" : "review required"} | duplicate
            IDs {impactImportSummary?.duplicate_supplier_product_id_count ?? 0} | missing images{" "}
            {impactCatalogQuality.missing_image?.count ?? 0} | missing SEO{" "}
            {impactCatalogQuality.missing_seo?.count ?? 0} | missing categories{" "}
            {impactCatalogQuality.missing_category?.count ?? 0} | incomplete material pricing{" "}
            {impactCatalogQuality.incomplete_material_pricing?.count ?? 0} | skipped{" "}
            {impactImportSummary?.skipped_count ?? 0}
          </Banner>
        ) : null}
        <InlineStack align="end" gap="200">
          <Button loading={syncing} disabled={syncing || importing} onClick={() => void refreshAllSyncedProducts()}>
            Refresh all Synced Products
          </Button>
        </InlineStack>
        <Tabs tabs={productSupplierTabs} selected={selectedProductTab} onSelect={selectProductTab} fitted />
        {activeProductTab === "tradeprint" ? (
          <Card>
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Tradeprint products
                </Text>
                <Text as="p" tone="subdued">
                  Import the Tradeprint catalogue and manage selected supplier products for Shopify sync.
                </Text>
              </BlockStack>
              <Button loading={importing} disabled={syncing || importing} onClick={() => void importTradeprintCatalog()}>
                Import Tradeprint catalogue
              </Button>
            </InlineStack>
          </Card>
        ) : null}
        {activeProductTab === "impactconnect" ? (
          <Card>
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Impact signs
                </Text>
                <Text as="p" tone="subdued">
                  Import the static Impact health and safety sign catalogue, then select products for Shopify publish.
                </Text>
              </BlockStack>
              <Button loading={importing} disabled={syncing || importing} onClick={() => void importImpactCatalog()}>
                Import Impact signs
              </Button>
            </InlineStack>
          </Card>
        ) : null}
        {activeProductTab === "pencarrie" ? (
          <Card>
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                PenCarrie products
              </Text>
              <Text as="p" tone="subdued">
                PenCarrie product import controls are managed from Setup &gt; Suppliers.
              </Text>
            </BlockStack>
          </Card>
        ) : null}
        {activeProductTab === "in_house" ? (
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Add in-house print product
                </Text>
                <Text as="p" tone="subdued">
                  Build a locally fulfilled print product in four guided steps. Titles, descriptions, SEO and media can be completed in Shopify after creation.
                </Text>
              </BlockStack>

              <div className="admin-creation-steps" role="list" aria-label="Product creation progress">
                {IN_HOUSE_CREATION_STEPS.map((step, index) => (
                  <div
                    key={step.label}
                    className={`admin-creation-step${index === inHouseStep ? " admin-creation-step--current" : ""}${index < inHouseStep ? " admin-creation-step--complete" : ""}`}
                    role="listitem"
                    aria-current={index === inHouseStep ? "step" : undefined}
                  >
                    <span>{index + 1}</span>
                    <BlockStack gap="100">
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        {step.shortLabel}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {index < inHouseStep ? "Complete" : index === inHouseStep ? "Current step" : "Not started"}
                      </Text>
                    </BlockStack>
                  </div>
                ))}
              </div>

              {inHouseStepErrors.length ? (
                <Banner tone="warning">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">
                      Complete this step before continuing:
                    </Text>
                    <List>
                      {inHouseStepErrors.map((error) => (
                        <List.Item key={error}>{error}</List.Item>
                      ))}
                    </List>
                  </BlockStack>
                </Banner>
              ) : null}

              <div className="admin-creation-panel">
                <BlockStack gap="400">
                  <Text as="h3" variant="headingMd">
                    {IN_HOUSE_CREATION_STEPS[inHouseStep].label}
                  </Text>

                  {inHouseStep === 0 ? (
                    <FormLayout>
                      <TextField
                        label="Product title"
                        value={inHouseDraft.title}
                        autoComplete="off"
                        placeholder="e.g. Foamex signs"
                        helpText="This becomes the initial Shopify product title and can be edited later in Shopify."
                        onChange={(value) => {
                          setInHouseDraft((current) => ({ ...current, title: value }));
                          setInHouseStepErrors([]);
                        }}
                      />
                      <FormLayout.Group>
                        <TextField
                          label="Product category"
                          value={inHouseDraft.category}
                          autoComplete="off"
                          placeholder="Custom Print"
                          onChange={(value) => {
                            setInHouseDraft((current) => ({ ...current, category: value }));
                            setInHouseStepErrors([]);
                          }}
                        />
                        <TextField
                          label="Collections"
                          value={inHouseDraft.collections}
                          autoComplete="off"
                          placeholder="Custom Print, Signs"
                          helpText="Separate collection names with commas."
                          onChange={(value) => {
                            setInHouseDraft((current) => ({ ...current, collections: value }));
                            setInHouseStepErrors([]);
                          }}
                        />
                      </FormLayout.Group>
                      <TextField
                        label="Selling margin"
                        type="number"
                        suffix="%"
                        min={0}
                        max={500}
                        value={inHouseDraft.margin_percent}
                        autoComplete="off"
                        helpText="Applied to the in-house prices entered in step 3."
                        onChange={(value) => {
                          setInHouseDraft((current) => ({ ...current, margin_percent: value }));
                          setInHouseStepErrors([]);
                        }}
                      />
                      <details className="admin-accordion">
                        <summary>Advanced identifiers</summary>
                        <div className="admin-accordion__body">
                          <FormLayout>
                            <TextField
                              label="Internal product ID"
                              value={inHouseDraft.product_id}
                              autoComplete="off"
                              placeholder="Generated automatically when blank"
                              onChange={(value) =>
                                setInHouseDraft((current) => ({ ...current, product_id: value }))
                              }
                            />
                            <TextField
                              label="URL handle"
                              value={inHouseDraft.canonical_handle}
                              autoComplete="off"
                              placeholder="Generated automatically when blank"
                              onChange={(value) =>
                                setInHouseDraft((current) => ({ ...current, canonical_handle: value }))
                              }
                            />
                          </FormLayout>
                        </div>
                      </details>
                    </FormLayout>
                  ) : null}

                  {inHouseStep === 1 ? (
                    <BlockStack gap="300">
                      <Text as="p" tone="subdued">
                        Add the choices customers should make in sequence. Values are displayed in the order entered.
                      </Text>
                      {inHouseOptionDrafts.map((option, index) => (
                        <Box
                          key={option.id}
                          padding="300"
                          background="bg-surface-secondary"
                          borderRadius="200"
                        >
                          <BlockStack gap="300">
                            <InlineStack align="space-between" blockAlign="center">
                              <Text as="h4" variant="headingSm">
                                Option {index + 1}
                              </Text>
                              <Button
                                variant="plain"
                                tone="critical"
                                onClick={() => removeInHouseOption(option.id)}
                              >
                                Remove option
                              </Button>
                            </InlineStack>
                            <FormLayout.Group>
                              <TextField
                                label="Option name"
                                value={option.name}
                                autoComplete="off"
                                placeholder="e.g. Size"
                                onChange={(value) =>
                                  updateInHouseOption(option.id, { name: value })
                                }
                              />
                              <TextField
                                label="Available values"
                                value={option.values}
                                autoComplete="off"
                                placeholder="A6, A5, A4"
                                helpText="Separate values with commas."
                                onChange={(value) =>
                                  updateInHouseOption(option.id, { values: value })
                                }
                              />
                            </FormLayout.Group>
                          </BlockStack>
                        </Box>
                      ))}
                      <Button
                        disabled={inHouseOptionDrafts.length >= 10}
                        onClick={() => {
                          setInHouseOptionDrafts((current) => [
                            ...current,
                            {
                              id: nextInHouseDraftId("option"),
                              name: "",
                              values: ""
                            }
                          ]);
                          setInHouseStepErrors([]);
                        }}
                      >
                        Add print option
                      </Button>
                    </BlockStack>
                  ) : null}

                  {inHouseStep === 2 ? (
                    <BlockStack gap="400">
                      <InlineStack align="space-between" blockAlign="center" gap="300">
                        <Text as="p" tone="subdued">
                          Add a quantity and at least one delivery price. Leave option choices blank when a row applies to every value.
                        </Text>
                        <InlineStack gap="200">
                          <Button
                            url={csvDataUrl(
                              inHouseSampleCsv(inHouseOptionSchemaText(inHouseOptionDrafts))
                            )}
                            download="printconnect-in-house-price-list-sample.csv"
                          >
                            Download CSV template
                          </Button>
                          <label className="admin-file-action">
                            <span>Import price CSV</span>
                            <input
                              type="file"
                              accept=".csv,text/csv"
                              disabled={creatingInHouse}
                              onChange={(event) => {
                                const file = event.currentTarget.files?.[0] || null;
                                void importCreatePriceCsv(file);
                                event.currentTarget.value = "";
                              }}
                            />
                          </label>
                        </InlineStack>
                      </InlineStack>
                      {inHousePriceDrafts.map((priceRow, index) => (
                        <Box
                          key={priceRow.id}
                          padding="300"
                          background="bg-surface-secondary"
                          borderRadius="200"
                        >
                          <BlockStack gap="300">
                            <InlineStack align="space-between" blockAlign="center">
                              <Text as="h4" variant="headingSm">
                                Price row {index + 1}
                              </Text>
                              <Button
                                variant="plain"
                                tone="critical"
                                onClick={() => {
                                  setInHousePriceDrafts((current) =>
                                    current.filter((row) => row.id !== priceRow.id)
                                  );
                                  setInHouseStepErrors([]);
                                }}
                              >
                                Remove row
                              </Button>
                            </InlineStack>
                            {inHouseOptionChoices.length ? (
                              <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                                {inHouseOptionChoices
                                  .filter((option) => option.name && option.values.length)
                                  .map((option) => (
                                    <Select
                                      key={`${priceRow.id}-${option.id}`}
                                      label={option.name}
                                      options={[
                                        {
                                          label: `All ${option.name.toLowerCase()} values`,
                                          value: ""
                                        },
                                        ...option.values.map((value) => ({
                                          label: value,
                                          value
                                        }))
                                      ]}
                                      value={priceRow.optionValues[option.name] || ""}
                                      onChange={(value) => {
                                        setInHousePriceDrafts((current) =>
                                          current.map((row) =>
                                            row.id === priceRow.id
                                              ? {
                                                  ...row,
                                                  optionValues: {
                                                    ...row.optionValues,
                                                    [option.name]: value
                                                  }
                                                }
                                              : row
                                          )
                                        );
                                        setInHouseStepErrors([]);
                                      }}
                                    />
                                  ))}
                              </InlineGrid>
                            ) : null}
                            <TextField
                              label="Quantity"
                              type="number"
                              min={1}
                              max={1000000}
                              step={1}
                              value={priceRow.quantity}
                              autoComplete="off"
                              onChange={(value) => {
                                setInHousePriceDrafts((current) =>
                                  current.map((row) =>
                                    row.id === priceRow.id ? { ...row, quantity: value } : row
                                  )
                                );
                                setInHouseStepErrors([]);
                              }}
                            />
                            <InlineGrid columns={{ xs: 1, md: 3 }} gap="200">
                              {(
                                [
                                  ["saver", "Saver price"],
                                  ["standard", "Standard price"],
                                  ["express", "Express price"]
                                ] as const
                              ).map(([field, label]) => (
                                <TextField
                                  key={`${priceRow.id}-${field}`}
                                  label={label}
                                  type="number"
                                  min={0.01}
                                  step={0.01}
                                  prefix="£"
                                  value={priceRow[field]}
                                  autoComplete="off"
                                  onChange={(value) => {
                                    setInHousePriceDrafts((current) =>
                                      current.map((row) =>
                                        row.id === priceRow.id ? { ...row, [field]: value } : row
                                      )
                                    );
                                    setInHouseStepErrors([]);
                                  }}
                                />
                              ))}
                            </InlineGrid>
                          </BlockStack>
                        </Box>
                      ))}
                      <Button
                        disabled={inHousePriceDrafts.length >= 500}
                        onClick={() => {
                          setInHousePriceDrafts((current) => [
                            ...current,
                            emptyInHousePriceDraft()
                          ]);
                          setInHouseStepErrors([]);
                        }}
                      >
                        Add price row
                      </Button>
                    </BlockStack>
                  ) : null}

                  {inHouseStep === 3 ? (
                    <BlockStack gap="300">
                      <InlineGrid columns={{ xs: 1, md: 4 }} gap="300">
                        <MetricCard
                          label="Product"
                          value={inHouseDraft.title.trim() || "Not entered"}
                          trend={inHouseDraft.category.trim() || "Uncategorised"}
                        />
                        <MetricCard
                          label="Options"
                          value={String(inHouseOptionSchema.length)}
                          trend={`${inHouseOptionChoices.reduce((count, option) => count + option.values.length, 0)} values`}
                        />
                        <MetricCard
                          label="Price rows"
                          value={String(inHousePriceRows.length)}
                          trend={inHouseFromPrice == null ? "No valid price" : `From £${inHouseFromPrice.toFixed(2)}`}
                        />
                        <MetricCard
                          label="Margin"
                          value={`${Number(inHouseDraft.margin_percent || 0)}%`}
                          trend="Applied at quote"
                        />
                      </InlineGrid>
                      <Card>
                        <BlockStack gap="200">
                          <Text as="h4" variant="headingSm">
                            Customer choices
                          </Text>
                          <List>
                            {inHouseOptionChoices.map((option) => (
                              <List.Item key={option.id}>
                                {option.name}: {option.values.join(", ")}
                              </List.Item>
                            ))}
                          </List>
                        </BlockStack>
                      </Card>
                      <Banner tone="info">
                        The product will be selected for Shopify sync and routed to local fulfilment. Complete its description, SEO and media in Shopify after the first sync.
                      </Banner>
                    </BlockStack>
                  ) : null}
                </BlockStack>
              </div>

              <InlineStack align="space-between" blockAlign="center" gap="300">
                <Button
                  disabled={inHouseStep === 0 || creatingInHouse}
                  onClick={() => {
                    setInHouseStepErrors([]);
                    setInHouseStep((current) => Math.max(0, current - 1));
                  }}
                >
                  Back
                </Button>
                {inHouseStep < IN_HOUSE_CREATION_STEPS.length - 1 ? (
                  <Button
                    variant="primary"
                    disabled={creatingInHouse}
                    onClick={advanceInHouseStep}
                  >
                    Next: {IN_HOUSE_CREATION_STEPS[inHouseStep + 1].shortLabel}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    loading={creatingInHouse}
                    disabled={syncing || importing || creatingInHouse}
                    onClick={() => void createInHouseProduct()}
                  >
                    Create in-house product
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
          </Card>
        ) : null}
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Product catalogue
                </Text>
                <Text as="p" tone="subdued">
                  Showing {pageStart}-{pageEnd} of {totalMatchingProducts} matching products · {productCounts.selected} selected across matching products
                </Text>
              </BlockStack>
              <InlineStack gap="200">
                <Button
                  disabled={!filteredProductIds.length || syncing || importing || bulkUpdating}
                  loading={bulkUpdating}
                  onClick={() => void setFilteredProductSelection(true)}
                >
                  Select all filtered
                </Button>
                <Button
                  disabled={!filteredProductIds.length || syncing || importing || bulkUpdating}
                  loading={bulkUpdating}
                  onClick={() => void setFilteredProductSelection(false)}
                >
                  Deselect all filtered
                </Button>
                <Button
                  disabled={
                    !productFilter &&
                    selectionFilter === "all" &&
                    syncStatusFilter === "all" &&
                    readinessFilter === "all" &&
                    sourceFilter === "all" &&
                    (activeProductTab !== "impactconnect" || signCategoryFilter === "all")
                  }
                  onClick={() => {
                    setProductFilter("");
                    setSelectionFilter("all");
                    setSyncStatusFilter("all");
                    setReadinessFilter("all");
                    setSourceFilter("all");
                    setSignCategoryFilter("all");
                  }}
                >
                  Clear filters
                </Button>
              </InlineStack>
            </InlineStack>
            {paginationControls}
            <InlineGrid columns={{ xs: 1, md: activeProductTab === "impactconnect" ? 6 : 5 }} gap="200">
              <TextField
                label="Search"
                value={productFilter}
                onChange={setProductFilter}
                autoComplete="off"
                placeholder="Flyers, synced, PRD..."
                clearButton
                onClearButtonClick={() => setProductFilter("")}
              />
              <Select
                label="Selection"
                options={[
                  { label: "All products", value: "all" },
                  { label: "Selected for sync", value: "selected" },
                  { label: "Not selected", value: "not_selected" }
                ]}
                value={selectionFilter}
                onChange={setSelectionFilter}
              />
              <Select
                label="Sync status"
                options={syncStatusOptions}
                value={syncStatusFilter}
                onChange={setSyncStatusFilter}
              />
              <Select
                label="Readiness"
                options={[
                  { label: "All readiness", value: "all" },
                  { label: "Ready", value: "ready" },
                  { label: "Needs review", value: "needs_review" },
                  { label: "Blocked", value: "blocked" }
                ]}
                value={readinessFilter}
                onChange={setReadinessFilter}
              />
              <Select
                label="Source"
                options={sourceOptions}
                value={sourceFilter}
                onChange={setSourceFilter}
              />
              {activeProductTab === "impactconnect" ? (
                <Select
                  label="Sign category"
                  options={signCategoryOptions}
                  value={signCategoryFilter}
                  onChange={setSignCategoryFilter}
                />
              ) : null}
            </InlineGrid>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
              <Select
                label="Sort products by"
                options={[
                  { label: "Needs attention", value: "attention" },
                  { label: "Title", value: "title" },
                  { label: "Supplier", value: "supplier" },
                  { label: "Readiness", value: "readiness" },
                  { label: "Sync status", value: "sync_status" },
                  { label: "Last updated", value: "updated" }
                ]}
                value={productSort}
                onChange={setProductSort}
              />
              <Select
                label="Sort direction"
                options={[
                  { label: "Ascending", value: "asc" },
                  { label: "Descending", value: "desc" }
                ]}
                value={productSortDirection}
                onChange={setProductSortDirection}
              />
            </InlineGrid>
            <BlockStack gap="200">
              {!loading && !filteredProducts.length ? <Text as="p" tone="subdued">No products match this filter.</Text> : null}
              {filteredProducts.map((product) => {
                const selection = product.selection;
                const productId = String(product.id || selection?.product_id || "");
                const title = product.name || product.title || productId || "Untitled product";
                const savedSyncTitle = selection?.product_group_title || title;
                const titleDraft = titleDrafts[productId] ?? savedSyncTitle;
                const titleChanged = titleDraft.replace(/\s+/g, " ").trim() !== savedSyncTitle;
                const previewImageUrl = String(
                  product.normalized_payload?.image_url ||
                    product.normalized_payload?.image ||
                    (product as AdminProduct & { image_url?: string }).image_url ||
                    ""
                );
                const readiness = readinessForProduct(product);
                const included = Boolean(selection?.included);
                const savedMargin = selection?.margin_percent ?? data.global_margin_percent ?? 0;
                const marginDraft = marginDrafts[productId] ?? String(savedMargin);
                const marginChanged = Number(marginDraft) !== Number(savedMargin);
                const productDelivery = selection?.parity_config?.delivery as
                  | { service_level_padding_days?: Record<string, number> }
                  | undefined;
                const savedDeliveryPadding = productDelivery?.service_level_padding_days || {};
                const savedCollections = selection?.collections || [];
                const collectionDraft = collectionDrafts[productId] || savedCollections;
                const collectionChanged =
                  collectionDraft.length !== savedCollections.length ||
                  collectionDraft.some((collection, index) => collection !== savedCollections[index]);
                const collectionOptions = cleanCollectionList([...availableCollections, ...collectionDraft]);
                const newCollectionDraft = newCollectionDrafts[productId] || "";
                const deliveryDraft = deliveryDrafts[productId] || {
                  Saver: String(savedDeliveryPadding.Saver ?? ""),
                  Standard: String(savedDeliveryPadding.Standard ?? ""),
                  Express: String(savedDeliveryPadding.Express ?? "")
                };
                const deliveryChanged = ["Saver", "Standard", "Express"].some(
                  (service) => deliveryDraft[service] !== String(savedDeliveryPadding[service] ?? "")
                );
                const channelDraft = channelDrafts[productId] || {};
                const channelValue = (key: string) =>
                  channelDraft[key] ?? nestedStringValue(selection?.parity_config, CHANNEL_FIELD_PATHS[key]);
                const customPrint =
                  selection?.parity_config?.custom_print && typeof selection.parity_config.custom_print === "object"
                    ? (selection.parity_config.custom_print as Record<string, unknown>)
                    : {};
                const isInHouseProduct = productSupplierTab(product) === "in_house" || customPrint.source === "in_house";
                const isImpactProduct = productSupplierTab(product) === "impactconnect";
                const savedImpactAssignment = impactConfigForProduct(product);
                const impactAssignmentDraft = impactAssignmentDrafts[productId] || {};
                const impactAssignment = {
                  ...savedImpactAssignment,
                  ...impactAssignmentDraft
                };
                const selectedImpactSizes = impactAssignment.sizes.map((size) => size.label);
                const selectedImpactSubstrates = impactAssignment.substrates;
                const impactTemplateDraft = impactTemplateDrafts[productId] || "";
                const impactTemplateOptions = [
                  { label: "Choose option template", value: "" },
                  ...impactOptionTemplates.map((template) => ({
                    label: impactTemplateLabel(template),
                    value: template.name
                  }))
                ];
                const impactLogoPrice = Number(impactLogoSettings.logo_customisation_price || 0);
                const impactLogoLabel = String(impactLogoSettings.logo_customisation_label || "Add customised logo");
                const impactAntiGraffitiLabel = String(
                  impactLogoSettings.anti_graffiti_overlaminate_label || "Anti-graffiti overlaminate"
                );
                const impactAntiGraffitiPricePerSqm = Number(
                  impactLogoSettings.anti_graffiti_overlaminate_price_per_sqm ?? 18
                );
                const impactAntiGraffitiMinimumPrice = Number(
                  impactLogoSettings.anti_graffiti_overlaminate_minimum_price ?? 3
                );
                const currentPriceRows = Array.isArray(customPrint.price_rows) ? customPrint.price_rows.length : 0;
                const priceSampleOptionSchema =
                  optionSchemaTextFromRules(selection?.parity_config?.rules) ||
                  inHouseOptionSchemaText(inHouseOptionDrafts);
                const detailsId = `product-details-${productId || title}`.replace(/[^a-zA-Z0-9_-]/g, "-");
                return (
                  <details key={productId || title} id={detailsId} className="admin-product-row">
                    <summary className="admin-product-row__summary">
                      <InlineStack blockAlign="center" gap="300" wrap={false}>
                        <InlineStack gap="300" blockAlign="center" wrap={false}>
                          {isImpactProduct ? (
                            <div className="admin-product-preview">
                              {previewImageUrl ? (
                                <span className="admin-product-preview__image">
                                  <img src={previewImageUrl} alt="" loading="lazy" />
                                  <span className="admin-product-preview__zoom">
                                    <img src={previewImageUrl} alt="" loading="lazy" />
                                  </span>
                                </span>
                              ) : (
                                <span>No image</span>
                              )}
                            </div>
                          ) : null}
                          <Text as="h3" variant="headingSm">
                            {title}
                          </Text>
                        </InlineStack>
                      </InlineStack>
                    </summary>
                    <div className="admin-product-row__body">
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          {previewImageUrl ? (
                            <Button
                              id={`${detailsId}-image-preview-button`}
                              accessibilityLabel={`View larger preview for ${title}`}
                              onClick={() => {
                                lightboxTriggerRef.current =
                                  document.getElementById(
                                    `${detailsId}-image-preview-button`
                                  ) as HTMLButtonElement | null;
                                setLightboxImage({ src: previewImageUrl, title });
                              }}
                            >
                              View image
                            </Button>
                          ) : null}
                          <Checkbox
                            label="Add to Sync Queue"
                            checked={included}
                            disabled={syncing || importing || updatingProduct === productId}
                            onChange={(checked) => void saveProductSelection(product, { included: checked })}
                          />
                          <Button
                            loading={syncing}
                            disabled={syncing || importing || updatingProduct === productId || !productId}
                            onClick={() =>
                              void syncProductsForSupplier(productSupplierTab(product), [
                                String(product.supplier_product_id || product.id || selection?.product_id || "")
                              ])
                            }
                          >
                            Sync this product
                          </Button>
                        </InlineStack>
                        <BlockStack gap="200">
                          <InlineStack gap="200" blockAlign="center">
                            <Text as="h4" variant="headingSm">
                              Product identity
                            </Text>
                            <StatusBadge status={readiness.status} />
                            <Text as="span" tone="subdued">
                              {readiness.score}/100
                            </Text>
                          </InlineStack>
                          <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                            <Text as="p">
                              <strong>Product ID:</strong> {productId || "Missing"}
                            </Text>
                            <Text as="p">
                              <strong>Canonical handle:</strong> {selection?.canonical_handle || "Not set"}
                            </Text>
                            <Text as="p">
                              <strong>Source:</strong>{" "}
                              {productSourceLabel(product.catalog_source)}
                            </Text>
                            <Text as="p">
                              <strong>Sync status:</strong> {selection?.sync_status || "never_synced"}
                            </Text>
                            <Text as="p">
                              <strong>Quality gaps:</strong>{" "}
                              {readiness.missing.length ? formatGaps(readiness.missing) : "Ready"}
                            </Text>
                            <Text as="p">
                              <strong>Collections:</strong>{" "}
                              {selection?.collections?.length ? selection.collections.join(", ") : "None"}
                            </Text>
                          </InlineGrid>
                          {isImpactProduct ? (
                            <InlineStack gap="200" blockAlign="end">
                              <TextField
                                label="Sync title"
                                value={titleDraft}
                                autoComplete="off"
                                disabled={syncing || importing || updatingProduct === productId}
                                onChange={(value) =>
                                  setTitleDrafts((current) => ({ ...current, [productId]: value }))
                                }
                              />
                              <Button
                                disabled={!titleChanged || syncing || importing || updatingProduct === productId}
                                onClick={() => void saveProductTitle(product, titleDraft)}
                              >
                                Save title
                              </Button>
                            </InlineStack>
                          ) : null}
                        </BlockStack>
                        <BlockStack gap="200">
                          <Text as="h4" variant="headingSm">
                            Sync and pricing
                          </Text>
                          <InlineStack gap="200" blockAlign="end">
                            <TextField
                              label="Margin"
                              type="number"
                              suffix="%"
                              value={marginDraft}
                              min={0}
                              max={500}
                              autoComplete="off"
                              disabled={syncing || importing || updatingProduct === productId}
                              onChange={(value) =>
                                setMarginDrafts((current) => ({ ...current, [productId]: value }))
                              }
                            />
                            <Button
                              disabled={!marginChanged || syncing || importing || updatingProduct === productId}
                              onClick={() => void saveProductMargin(product, marginDraft)}
                            >
                              Save margin
                            </Button>
                          </InlineStack>
                        </BlockStack>
                        <ConfigurationTemplateManager
                          product={product}
                          productId={productId}
                          title={title}
                        />
                            {isImpactProduct ? (
                              <BlockStack gap="200">
                                <Text as="h4" variant="headingSm">
                                  Impact sign options
                                </Text>
                                <InlineStack gap="200" blockAlign="end">
                                  <Select
                                    label="Option template"
                                    value={impactTemplateDraft}
                                    disabled={syncing || importing || updatingProduct === productId || impactTemplateOptions.length <= 1}
                                    options={impactTemplateOptions}
                                    onChange={(value) =>
                                      setImpactTemplateDrafts((current) => ({ ...current, [productId]: value }))
                                    }
                                  />
                                  <Button
                                    disabled={!impactTemplateDraft || syncing || importing || updatingProduct === productId}
                                    onClick={() => void applyImpactOptionTemplate(product, impactTemplateDraft)}
                                  >
                                    Apply template
                                  </Button>
                                </InlineStack>
                                <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                                  <MultiSelectDropdown
                                    label="Assign sizes"
                                    summary={
                                      selectedImpactSizes.length
                                        ? `${selectedImpactSizes.length} sizes selected`
                                        : "Select sizes"
                                    }
                                    options={impactAvailableSizeOptions.map((size) => ({
                                      label: size.label,
                                      value: size.label,
                                      description: `${size.width_mm} x ${size.height_mm}mm`
                                    }))}
                                    selectedValues={selectedImpactSizes}
                                    filterPlaceholder="Filter sizes"
                                    disabled={syncing || importing || updatingProduct === productId}
                                    onChange={(values) => {
                                      const next = values
                                        .map((value) =>
                                          impactAvailableSizeOptions.find((size) => size.label === value) ||
                                          impactAssignment.sizes.find((size) => size.label === value)
                                        )
                                        .filter((size): size is ImpactSizeOption => Boolean(size));
                                      void saveImpactAssignments(product, { sizes: next });
                                    }}
                                  />
                                  <MultiSelectDropdown
                                    label="Assign substrates"
                                    summary={
                                      selectedImpactSubstrates.length
                                        ? `${selectedImpactSubstrates.length} substrates selected`
                                        : "Select substrates"
                                    }
                                    options={impactAvailableSubstrateOptions.map((substrate) => ({
                                      label: substrate,
                                      value: substrate
                                    }))}
                                    selectedValues={selectedImpactSubstrates}
                                    filterPlaceholder="Filter substrates"
                                    disabled={syncing || importing || updatingProduct === productId}
                                    onChange={(values) => void saveImpactAssignments(product, { substrates: values })}
                                  />
                                </InlineGrid>
                                <Select
                                  label="Minimum pricing"
                                  value={impactAssignment.minimumPricingEnabled}
                                  disabled={syncing || importing || updatingProduct === productId}
                                  helpText="Controls whether this product applies material minimum_price as a per-order-line floor."
                                  options={[
                                    {
                                      label: `Inherit global (${truthyConfigValue(impactLogoSettings.minimum_pricing_enabled ?? true) ? "enabled" : "disabled"})`,
                                      value: ""
                                    },
                                    { label: "Enabled", value: "enabled" },
                                    { label: "Disabled", value: "disabled" }
                                  ]}
                                  onChange={(value) =>
                                    void saveImpactAssignments(product, {
                                      minimumPricingEnabled: value
                                    })
                                  }
                                />
                                <Checkbox
                                  label={
                                    impactLogoPrice > 0
                                      ? `${impactLogoLabel} (£${impactLogoPrice.toFixed(2)} fixed add-on)`
                                      : `${impactLogoLabel} (set a global fixed price in Impact settings)`
                                  }
                                  checked={impactAssignment.logoCustomisationEnabled && impactLogoPrice > 0}
                                  disabled={syncing || importing || updatingProduct === productId || impactLogoPrice <= 0}
                                  onChange={(checked) =>
                                    void saveImpactAssignments(product, { logoCustomisationEnabled: checked })
                                  }
                                />
                                <Checkbox
                                  label={
                                    impactAntiGraffitiPricePerSqm > 0
                                      ? `${impactAntiGraffitiLabel} (£${impactAntiGraffitiPricePerSqm.toFixed(2)}/sqm, £${impactAntiGraffitiMinimumPrice.toFixed(2)} minimum)`
                                      : `${impactAntiGraffitiLabel} (set a global sqm price in Impact settings)`
                                  }
                                  checked={impactAssignment.antiGraffitiOverlaminateEnabled && impactAntiGraffitiPricePerSqm > 0}
                                  disabled={syncing || importing || updatingProduct === productId || impactAntiGraffitiPricePerSqm <= 0}
                                  onChange={(checked) =>
                                    void saveImpactAssignments(product, {
                                      antiGraffitiOverlaminateEnabled: checked
                                    })
                                  }
                                />
                                <Text as="p" tone="subdued">
                                  Pricing is recalculated from size area, substrate costs, ink costs, and the global Impact margin unless this product margin is set.
                                </Text>
                              </BlockStack>
                            ) : null}
                            <Text as="h4" variant="headingSm">
                              Collections
                            </Text>
                            <BlockStack gap="200">
                              <details className="admin-collection-picker">
                                <summary>
                                  {collectionDraft.length
                                    ? `${collectionDraft.length} selected: ${collectionDraft.join(", ")}`
                                    : "Select collections"}
                                </summary>
                                <div className="admin-collection-picker__body">
                                  <BlockStack gap="200">
                                    {collectionOptions.length ? (
                                      <BlockStack gap="100">
                                        {collectionOptions.map((collection) => {
                                          const selected = collectionDraft.includes(collection);
                                          return (
                                            <Checkbox
                                              key={collection}
                                              label={collection}
                                              checked={selected}
                                              disabled={syncing || importing || updatingProduct === productId}
                                              onChange={(checked) =>
                                                setCollectionDrafts((current) => {
                                                  const existing = current[productId] || savedCollections;
                                                  return {
                                                    ...current,
                                                    [productId]: checked
                                                      ? cleanCollectionList([...existing, collection])
                                                      : existing.filter((item) => item !== collection)
                                                  };
                                                })
                                              }
                                            />
                                          );
                                        })}
                                      </BlockStack>
                                    ) : (
                                      <Text as="p" tone="subdued">
                                        No collections found yet.
                                      </Text>
                                    )}
                                    <InlineStack gap="200" blockAlign="end">
                                      <TextField
                                        label="Add collection"
                                        value={newCollectionDraft}
                                        placeholder="e.g. Flyers"
                                        autoComplete="off"
                                        disabled={syncing || importing || updatingProduct === productId}
                                        onChange={(value) =>
                                          setNewCollectionDrafts((current) => ({ ...current, [productId]: value }))
                                        }
                                      />
                                      <Button
                                        disabled={!newCollectionDraft.trim() || syncing || importing || updatingProduct === productId}
                                        onClick={() => {
                                          const clean = newCollectionDraft.trim();
                                          setCollectionDrafts((current) => {
                                            const existing = current[productId] || savedCollections;
                                            return {
                                              ...current,
                                              [productId]: cleanCollectionList([...existing, clean])
                                            };
                                          });
                                          setNewCollectionDrafts((current) => ({ ...current, [productId]: "" }));
                                        }}
                                      >
                                        Add
                                      </Button>
                                    </InlineStack>
                                    <Button
                                      disabled={!collectionChanged || syncing || importing || updatingProduct === productId}
                                      onClick={() => void saveProductCollections(product, collectionDraft)}
                                    >
                                      Save collections
                                    </Button>
                                  </BlockStack>
                                </div>
                              </details>
                            </BlockStack>
                            {isInHouseProduct ? (
                              <BlockStack gap="200">
                                <Text as="h4" variant="headingSm">
                                  In-house price list
                                </Text>
                                <Text as="p" tone="subdued">
                                  {currentPriceRows ? `${currentPriceRows} price rows currently loaded.` : "No price rows are currently loaded."}
                                </Text>
                                <InlineStack gap="300" blockAlign="center">
                                  <Button
                                    url={csvDataUrl(inHouseSampleCsv(priceSampleOptionSchema))}
                                    download={`${productId || "printconnect"}-price-list-sample.csv`}
                                  >
                                    Download sample CSV
                                  </Button>
                                  <label className="admin-file-action">
                                    <span>Reupload price CSV</span>
                                    <input
                                      type="file"
                                      accept=".csv,text/csv"
                                      disabled={syncing || importing || updatingProduct === productId}
                                      onChange={(event) => {
                                        const file = event.currentTarget.files?.[0] || null;
                                        void uploadProductPriceCsv(product, file);
                                        event.currentTarget.value = "";
                                      }}
                                    />
                                  </label>
                                </InlineStack>
                              </BlockStack>
                            ) : null}
                            <Text as="h4" variant="headingSm">
                              {isImpactProduct ? "Impact Shopify channel details" : "Channel blocker details"}
                            </Text>
                            <BlockStack gap="200">
                              <Text as="p" tone="subdued">
                                {isImpactProduct
                                  ? "Impact signs publish to Shopify as locally fulfilled printed products with one standard delivery option."
                                  : "Options are print-focused defaults; Shopify taxonomy, Etsy taxonomy, and Amazon product types can be API-backed when those integrations are connected."}
                              </Text>
                              {isImpactProduct ? (
                                <>
                                  <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                                    <TextField
                                      label="Shopify taxonomy category ID"
                                      value={channelValue("shopify_taxonomy_category_id")}
                                      autoComplete="off"
                                      disabled={syncing || importing || updatingProduct === productId}
                                      onChange={(value) =>
                                        setChannelDrafts((current) => ({
                                          ...current,
                                          [productId]: { ...(current[productId] || {}), shopify_taxonomy_category_id: value }
                                        }))
                                      }
                                    />
                                    <Select
                                      label="Taxable"
                                      options={[
                                        { label: "Not set", value: "" },
                                        { label: "Taxable", value: "true" },
                                        { label: "Not taxable", value: "false" }
                                      ]}
                                      value={channelValue("taxable")}
                                      disabled={syncing || importing || updatingProduct === productId}
                                      onChange={(value) =>
                                        setChannelDrafts((current) => ({
                                          ...current,
                                          [productId]: { ...(current[productId] || {}), taxable: value }
                                        }))
                                      }
                                    />
                                    <TextField
                                      label="Channel image URL"
                                      value={channelValue("image_url")}
                                      autoComplete="off"
                                      disabled={syncing || importing || updatingProduct === productId}
                                      onChange={(value) =>
                                        setChannelDrafts((current) => ({
                                          ...current,
                                          [productId]: { ...(current[productId] || {}), image_url: value }
                                        }))
                                      }
                                    />
                                    <Select
                                      label="Image asset policy"
                                      options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.asset_policy, channelValue("asset_policy"))}
                                      value={channelValue("asset_policy")}
                                      disabled={syncing || importing || updatingProduct === productId}
                                      onChange={(value) =>
                                        setChannelDrafts((current) => ({
                                          ...current,
                                          [productId]: { ...(current[productId] || {}), asset_policy: value }
                                        }))
                                      }
                                    />
                                  </InlineGrid>
                                  <Select
                                    label="Fulfilment route"
                                    options={[
                                      { label: "Likely local fulfilment", value: "local_fulfillment" },
                                      { label: "Manual review before fulfilment", value: "manual_review" }
                                    ]}
                                    value={channelValue("fulfillment_route") || "local_fulfillment"}
                                    disabled={syncing || importing || updatingProduct === productId}
                                    onChange={(value) =>
                                      setChannelDrafts((current) => ({
                                        ...current,
                                        [productId]: { ...(current[productId] || {}), fulfillment_route: value }
                                      }))
                                    }
                                  />
                                </>
                              ) : (
                                <>
                                  <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                                    <Select label="Amazon category" options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.amazon_category, channelValue("amazon_category"))} value={channelValue("amazon_category")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), amazon_category: value } }))} />
                                    <Select label="Etsy category" options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.etsy_category, channelValue("etsy_category"))} value={channelValue("etsy_category")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), etsy_category: value } }))} />
                                    <Select label="Google product category" options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.google_product_category, channelValue("google_product_category"))} value={channelValue("google_product_category")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), google_product_category: value } }))} />
                                    <Select label="GTIN/MPN identifier policy" options={[{ label: "Not set", value: "" }, { label: "GTIN or MPN available", value: "provided" }, { label: "Custom product, no identifier", value: "custom_product" }, { label: "Exempt", value: "exempt" }]} value={channelValue("identifier_policy")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), identifier_policy: value } }))} />
                                    <Select label="eBay category" options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.ebay_category, channelValue("ebay_category"))} value={channelValue("ebay_category")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), ebay_category: value } }))} />
                                    <Select label="Meta category" options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.meta_category, channelValue("meta_category"))} value={channelValue("meta_category")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), meta_category: value } }))} />
                                    <Select label="TikTok category" options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.tiktok_category, channelValue("tiktok_category"))} value={channelValue("tiktok_category")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), tiktok_category: value } }))} />
                                    <TextField label="Shopify taxonomy category ID" value={channelValue("shopify_taxonomy_category_id")} autoComplete="off" disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), shopify_taxonomy_category_id: value } }))} />
                                    <Select label="Taxable" options={[{ label: "Not set", value: "" }, { label: "Taxable", value: "true" }, { label: "Not taxable", value: "false" }]} value={channelValue("taxable")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), taxable: value } }))} />
                                    <TextField label="Channel image URL" value={channelValue("image_url")} autoComplete="off" disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), image_url: value } }))} />
                                  </InlineGrid>
                                  <Select label="Fulfilment route" options={[{ label: "Supplier submission", value: "supplier" }, { label: "Manual review before fulfilment", value: "manual_review" }, { label: "Likely local fulfilment", value: "local_fulfillment" }]} value={channelValue("fulfillment_route") || "supplier"} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), fulfillment_route: value } }))} />
                                  <Select label="Image asset policy" options={optionsWithCurrent(CHANNEL_CATEGORY_OPTIONS.asset_policy, channelValue("asset_policy"))} value={channelValue("asset_policy")} disabled={syncing || importing || updatingProduct === productId} onChange={(value) => setChannelDrafts((current) => ({ ...current, [productId]: { ...(current[productId] || {}), asset_policy: value } }))} />
                                </>
                              )}
                              <Button disabled={syncing || importing || updatingProduct === productId} onClick={() => void saveProductChannelSettings(product, channelDraft)}>
                                Save channel details
                              </Button>
                            </BlockStack>
                            {!isImpactProduct ? (
                              <>
                                <Text as="h4" variant="headingSm">
                                  Delivery padding overrides
                                </Text>
                                <InlineGrid columns={{ xs: 1, md: 3 }} gap="200">
                                  {["Saver", "Standard", "Express"].map((service) => (
                                    <TextField
                                      key={service}
                                      label={service}
                                      type="number"
                                      suffix="days"
                                      value={deliveryDraft[service]}
                                      min={0}
                                      max={30}
                                      placeholder="Use global"
                                      autoComplete="off"
                                      disabled={syncing || importing || updatingProduct === productId}
                                      onChange={(value) =>
                                        setDeliveryDrafts((current) => ({
                                          ...current,
                                          [productId]: {
                                            ...deliveryDraft,
                                            [service]: value
                                          }
                                        }))
                                      }
                                    />
                                  ))}
                                </InlineGrid>
                                <InlineStack gap="200">
                                  <Button
                                    disabled={!deliveryChanged || syncing || importing || updatingProduct === productId}
                                    onClick={() => void saveProductDeliveryPadding(product, deliveryDraft)}
                                  >
                                    Save delivery overrides
                                  </Button>
                                  <Text as="span" tone="subdued">
                                    Leave blank to inherit global padding.
                                  </Text>
                                </InlineStack>
                              </>
                            ) : (
                              <Text as="p" tone="subdued">
                                Impact signs use a single standard delivery option.
                              </Text>
                            )}
                        </BlockStack>
                      </div>
                    </details>
                );
              })}
            </BlockStack>
            {paginationControls}
          </BlockStack>
        </Card>
        <Card>
          <details className="admin-accordion">
            <summary>Product group health</summary>
            <div className="admin-accordion__body">
              <DataTable
                columnContentTypes={["text", "numeric", "numeric", "numeric", "numeric", "text"]}
                headings={["Group", "Products", "Ready", "Review", "Blocked", "Warnings"]}
                rows={
                  data.product_groups?.groups.length
                    ? data.product_groups.groups.slice(0, 12).map((group) => [
                        group.title,
                        group.product_count,
                        group.ready,
                        group.needs_review,
                        group.blocked,
                        group.warnings.length ? formatGaps(group.warnings) : "No group warnings"
                      ])
                  : [["No product groups reported", "", "", "", "", ""]]
                }
              />
            </div>
          </details>
        </Card>
        {lightboxImage ? (
          <div
            className="admin-lightbox"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-lightbox-title"
            onClick={closeLightbox}
          >
            <div
              ref={lightboxPanelRef}
              className="admin-lightbox__panel"
              tabIndex={-1}
              onClick={(event) => event.stopPropagation()}
            >
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <Text id="admin-lightbox-title" as="h2" variant="headingMd">
                  {lightboxImage.title}
                </Text>
                <Button onClick={closeLightbox}>Close</Button>
              </InlineStack>
              <img src={lightboxImage.src} alt={lightboxImage.title} />
            </div>
          </div>
        ) : null}
      </BlockStack>
    </Page>
  );
}

type ExportChannelKey = "google_merchant" | "meta" | "tiktok" | "ebay" | "etsy";

interface ExportChannelConfig {
  key: ExportChannelKey;
  label: string;
  purpose: string;
  formats: Array<{ label: string; value: ChannelExportFormat }>;
  setupGuidance: string;
  checklist: string[];
}

const exportChannelConfigs: ExportChannelConfig[] = [
  {
    key: "google_merchant",
    label: "Google Merchant Centre",
    purpose: "Prepare products for Google Shopping ads and free product listings.",
    formats: [
      { label: "XML", value: "xml" },
      { label: "CSV", value: "csv" }
    ],
    setupGuidance:
      "Connect or configure Merchant Centre separately, then confirm your website, shipping, tax and diagnostics settings before publication.",
    checklist: [
      "Resolve missing identifiers, images, prices and availability before upload.",
      "Review Google diagnostics after the feed is processed.",
      "Use Shopify's Google & YouTube sales channel if you prefer a managed Shopify connection."
    ]
  },
  {
    key: "meta",
    label: "Meta (Facebook & Instagram)",
    purpose: "Prepare a catalogue file for Facebook Shops, Instagram Shopping and Meta ads.",
    formats: [{ label: "CSV", value: "csv" }],
    setupGuidance:
      "Connect the relevant Meta Business account and catalogue separately in Shopify or Commerce Manager, then review Meta's diagnostics.",
    checklist: [
      "Confirm your Facebook Page, Instagram account and domain are connected to the correct business.",
      "Review image, availability and pricing issues before upload.",
      "Publication and advert setup take place in Meta or its Shopify sales channel."
    ]
  },
  {
    key: "tiktok",
    label: "TikTok",
    purpose: "Prepare catalogue data for TikTok Shop or catalogue advertising.",
    formats: [{ label: "CSV", value: "csv" }],
    setupGuidance:
      "Connect TikTok Shop or TikTok for Business separately and confirm the current feed template for your market before upload.",
    checklist: [
      "Check category, stock, image and price requirements for the target market.",
      "Map any TikTok-specific categories or attributes in the destination account.",
      "Publication, account approval and campaign setup remain in TikTok or its Shopify sales channel."
    ]
  },
  {
    key: "ebay",
    label: "eBay",
    purpose: "Prepare listing data for review and use with an eBay bulk-listing workflow.",
    formats: [{ label: "CSV", value: "csv" }],
    setupGuidance:
      "Confirm the current Seller Hub template or approved integration for your eBay market and map any required listing fields before upload.",
    checklist: [
      "Review eBay category, condition, item-specific and delivery requirements.",
      "Check fees, returns and marketplace policies in the destination account.",
      "The export does not create, revise or publish eBay listings."
    ]
  },
  {
    key: "etsy",
    label: "Etsy",
    purpose: "Prepare product data for review and mapping into an Etsy listing workflow.",
    formats: [{ label: "CSV", value: "csv" }],
    setupGuidance:
      "Confirm Etsy's current supported import or integration route before use; listing categories, production partners and policies may require manual setup.",
    checklist: [
      "Review titles, imagery, materials, production details and delivery profiles.",
      "Map any fields required by your chosen Etsy integration.",
      "The export is a preparation file and does not create or publish Etsy listings."
    ]
  }
];

const privateFeedMarkers = [
  "supplier_product_id",
  "supplier_key",
  "product_group_id",
  "normalized_payload",
  "raw_payload",
  "wholesale_price",
  "cost_price"
];

function safeFeedFilename(filename: string, channel: ExportChannelKey, format: ChannelExportFormat) {
  const candidate = filename.split(/[\\/]/).pop()?.replace(/[^a-zA-Z0-9._-]+/g, "-") || "";
  const fallback = `printconnect-${channel}-feed.${format}`;
  if (!candidate || candidate === "." || candidate === "..") return fallback;
  return candidate.toLowerCase().endsWith(`.${format}`) ? candidate : `${candidate}.${format}`;
}

function ChannelsView() {
  const { data, loading, error, reload } = useApiData<ChannelReadinessResponse>(
    "/shopify/admin/channels/readiness",
    { channels: [], summary: [], products: [], recommendations: [] }
  );
  const [selectedChannel, setSelectedChannel] = useState<ExportChannelKey>("google_merchant");
  const [selectedFormat, setSelectedFormat] = useState<ChannelExportFormat>("xml");
  const [feedPreview, setFeedPreview] = useState<ChannelFeedPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportResult, setExportResult] = useState<ChannelFeedExport | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [productStatus, setProductStatus] = useState("all");
  const [productSort, setProductSort] = useState("attention");
  const selectedConfig =
    exportChannelConfigs.find((channel) => channel.key === selectedChannel) || exportChannelConfigs[0];
  const selectedSummary = data.summary.find((channel) => channel.key === selectedChannel);
  const launchSummaries = exportChannelConfigs.map((config) => ({
    config,
    summary: data.summary.find((channel) => channel.key === config.key)
  }));
  const previewIssues = useMemo(() => {
    const issueCounts = new Map<string, number>();
    if (feedPreview?.channel.key !== selectedChannel) return [];
    feedPreview.blocked.forEach((item) => {
      item.gaps.forEach((gap) => issueCounts.set(gap, (issueCounts.get(gap) || 0) + 1));
    });
    return Array.from(issueCounts.entries())
      .map(([issue, count]) => ({ issue, count }))
      .sort((left, right) => right.count - left.count || left.issue.localeCompare(right.issue));
  }, [feedPreview, selectedChannel]);
  const visibleProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();
    const attentionOrder: Record<string, number> = {
      blocked: 0,
      needs_review: 1,
      ready: 2,
      not_assessed: 3
    };
    return data.products
      .filter((product) => {
        const readiness = product.channels[selectedChannel];
        const status = readiness?.status || "not_assessed";
        if (productStatus !== "all" && status !== productStatus) return false;
        if (!query) return true;
        return [
          product.title,
          product.product_id,
          product.supplier_key,
          ...(readiness?.gaps || [])
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .sort((left, right) => {
        const leftReadiness = left.channels[selectedChannel];
        const rightReadiness = right.channels[selectedChannel];
        if (productSort === "title") {
          return left.title.localeCompare(right.title);
        }
        if (productSort === "supplier") {
          return (
            left.supplier_key.localeCompare(right.supplier_key) ||
            left.title.localeCompare(right.title)
          );
        }
        if (productSort === "catalog") {
          return (
            Number(left.signals.catalog_score || 0) -
              Number(right.signals.catalog_score || 0) ||
            left.title.localeCompare(right.title)
          );
        }
        return (
          (attentionOrder[leftReadiness?.status || "not_assessed"] ?? 4) -
            (attentionOrder[rightReadiness?.status || "not_assessed"] ?? 4) ||
          (rightReadiness?.gaps.length || 0) - (leftReadiness?.gaps.length || 0) ||
          left.title.localeCompare(right.title)
        );
      });
  }, [data.products, productSearch, productSort, productStatus, selectedChannel]);

  const changeChannel = (value: string) => {
    const nextConfig = exportChannelConfigs.find((channel) => channel.key === value);
    if (!nextConfig) return;
    setSelectedChannel(nextConfig.key);
    setSelectedFormat(nextConfig.formats[0].value);
    setFeedPreview(null);
    setPreviewError(null);
    setExportError(null);
    setExportResult(null);
  };

  const loadFeedPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setFeedPreview(null);
    const result = await apiFetch<ChannelFeedPreview>(
      `/shopify/admin/channels/feed-preview?channel=${encodeURIComponent(selectedChannel)}`
    );
    setPreviewLoading(false);
    if (result.error || !result.data) {
      setPreviewError(result.error || "The feed preview returned no data.");
      return;
    }
    setFeedPreview(result.data);
  };

  const downloadFeed = async () => {
    setExportLoading(true);
    setExportError(null);
    setExportResult(null);
    const result = await apiFetch<ChannelFeedExport>(
      `/shopify/admin/channels/feed-export?channel=${encodeURIComponent(selectedChannel)}&format=${encodeURIComponent(selectedFormat)}`
    );
    setExportLoading(false);
    if (result.error || !result.data) {
      setExportError(result.error || "The channel feed export returned no data.");
      return;
    }

    const exported = result.data;
    if (exported.channel !== selectedChannel || exported.format !== selectedFormat) {
      setExportError("The generated file did not match the selected channel and format. No file was downloaded.");
      return;
    }
    if (!exported.content || exported.eligible_count < 1) {
      setExportError("No eligible products were available for this export. Review the feed issues and try again.");
      return;
    }
    const lowerContent = exported.content.toLowerCase();
    if (privateFeedMarkers.some((marker) => lowerContent.includes(marker))) {
      setExportError("The export was blocked because it contained internal-only fields. No file was downloaded.");
      return;
    }

    const filename = safeFeedFilename(exported.filename, selectedChannel, selectedFormat);
    const mimeType = selectedFormat === "xml" ? "application/xml;charset=utf-8" : "text/csv;charset=utf-8";
    const objectUrl = URL.createObjectURL(new Blob([exported.content], { type: mimeType }));
    const downloadLink = document.createElement("a");
    downloadLink.href = objectUrl;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    setExportResult({ ...exported, filename });
  };

  return (
    <Page
      title="Channels"
      subtitle="Review catalogue readiness and prepare safe exports for external sales and advertising channels"
      primaryAction={{ content: "Refresh readiness", onAction: reload }}
    >
      <BlockStack gap="400">
        <Banner tone="info">
          Feed exports are downloadable catalogue files only. They do not connect accounts, store credentials, schedule
          updates or publish products. Complete account and sales-channel setup separately in Shopify and each destination.
        </Banner>
        <DismissibleNotice
          message={error ? `Channel readiness could not be loaded: ${error}` : null}
        />
        {loading ? <LoadingState label="Loading channel readiness" /> : null}

        <InlineGrid columns={{ xs: 1, md: 2, lg: 5 }} gap="300">
          {launchSummaries.map(({ config, summary }) => (
            <Card key={config.key}>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="start" gap="200">
                  <Text as="h2" variant="headingSm">
                    {config.label}
                  </Text>
                  <StatusBadge
                    status={
                      summary?.total
                        ? summary.blocked
                          ? "Blocked"
                          : summary.needs_review
                            ? "Review"
                            : "Ready"
                        : "Not started"
                    }
                  />
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  {config.purpose}
                </Text>
                <InlineStack gap="300">
                  <Text as="span" variant="bodySm">
                    {summary?.ready || 0} ready
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    {summary?.needs_review || 0} review
                  </Text>
                  <Text as="span" variant="bodySm" tone="critical">
                    {summary?.blocked || 0} blocked
                  </Text>
                </InlineStack>
              </BlockStack>
            </Card>
          ))}
        </InlineGrid>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Choose a destination
            </Text>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
              <Select
                label="Sales or advertising channel"
                options={exportChannelConfigs.map((channel) => ({ label: channel.label, value: channel.key }))}
                value={selectedChannel}
                onChange={changeChannel}
              />
              <InlineGrid columns={3} gap="200">
                <MetricCard label="Ready" value={String(selectedSummary?.ready || 0)} trend="eligible" />
                <MetricCard label="Needs review" value={String(selectedSummary?.needs_review || 0)} trend="review" />
                <MetricCard label="Blocked" value={String(selectedSummary?.blocked || 0)} trend="excluded" />
              </InlineGrid>
            </InlineGrid>
            <div className="admin-channel-guidance">
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {selectedConfig.label} setup guidance
                </Text>
                <Text as="p">{selectedConfig.setupGuidance}</Text>
                <List>
                  {selectedConfig.checklist.map((item) => (
                    <List.Item key={item}>{item}</List.Item>
                  ))}
                </List>
              </BlockStack>
            </div>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Validate feed eligibility
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Preview exclusions before generating a file. Internal supplier fields are never displayed.
                </Text>
              </BlockStack>
              <Button loading={previewLoading} disabled={previewLoading} onClick={loadFeedPreview}>
                Preview {selectedConfig.label} feed
              </Button>
            </InlineStack>
            <DismissibleNotice
              tone="critical"
              message={previewError ? `Feed preview could not be loaded: ${previewError}` : null}
            />
            {previewLoading ? <LoadingState label={`Loading ${selectedConfig.label} feed preview`} /> : null}
            {!previewLoading && !feedPreview && !previewError ? (
              <Text as="p" tone="subdued">
                Generate a preview to see eligible products, exclusions and validation issues for this channel.
              </Text>
            ) : null}
            {feedPreview?.channel.key === selectedChannel ? (
              <BlockStack gap="300">
                <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                  <MetricCard label="Eligible products" value={String(feedPreview.eligible_count)} trend="feed" />
                  <MetricCard
                    label="Excluded products"
                    value={String(feedPreview.blocked_count)}
                    trend={feedPreview.blocked_count ? "review" : "ready"}
                  />
                </InlineGrid>
                {feedPreview.eligible_count === 0 ? (
                  <Banner tone="warning">
                    No products are eligible yet. Resolve the validation issues below before generating a feed.
                  </Banner>
                ) : null}
                <Text as="h3" variant="headingSm">
                  Validation issues
                </Text>
                {previewIssues.length ? (
                  <DataTable
                    columnContentTypes={["text", "numeric"]}
                    headings={["Issue", "Affected products"]}
                    rows={previewIssues.map(({ issue, count }) => [formatGaps([issue]), count])}
                  />
                ) : (
                  <Text as="p" tone="subdued">
                    No validation issues were found in this preview.
                  </Text>
                )}
                <Text as="h3" variant="headingSm">
                  Excluded products
                </Text>
                <DataTable
                  columnContentTypes={["text", "text", "text"]}
                  headings={["Product", "Status", "Required action"]}
                  rows={
                    feedPreview.blocked.length
                      ? feedPreview.blocked.slice(0, 20).map((item) => [
                          item.title,
                          <StatusBadge status={item.status} />,
                          item.gaps.length ? formatGaps(item.gaps) : "Review this product"
                        ])
                      : [["No products excluded", "Ready", "No action required"]]
                  }
                />
              </BlockStack>
            ) : null}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                Download feed
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                The export contains customer-facing catalogue data only. Review it before uploading to another service.
              </Text>
            </BlockStack>
            <InlineStack align="space-between" blockAlign="end" gap="300">
              <Box minWidth="220px">
                <Select
                  label="File format"
                  options={selectedConfig.formats}
                  value={selectedFormat}
                  onChange={(value) => {
                    if (selectedConfig.formats.some((format) => format.value === value)) {
                      setSelectedFormat(value as ChannelExportFormat);
                      setExportError(null);
                      setExportResult(null);
                    }
                  }}
                />
              </Box>
              <Button loading={exportLoading} disabled={exportLoading} variant="primary" onClick={downloadFeed}>
                Generate and download feed
              </Button>
            </InlineStack>
            {exportLoading ? <LoadingState label={`Generating ${selectedConfig.label} ${selectedFormat.toUpperCase()} feed`} /> : null}
            <DismissibleNotice tone="critical" message={exportError} />
            {exportResult ? (
              <Banner tone={exportResult.warnings.length ? "warning" : "success"}>
                <BlockStack gap="200">
                  <Text as="p">
                    Downloaded {exportResult.filename}: {exportResult.eligible_count} eligible,{" "}
                    {exportResult.excluded_count} excluded.
                  </Text>
                  {exportResult.generated_at ? (
                    <Text as="p" variant="bodySm">
                      Generated {new Date(exportResult.generated_at).toLocaleString("en-GB")}.
                    </Text>
                  ) : null}
                  {exportResult.warnings.length ? (
                    <List>
                      {exportResult.warnings.map((warning) => (
                        <List.Item key={warning}>{warning}</List.Item>
                      ))}
                    </List>
                  ) : null}
                </BlockStack>
              </Banner>
            ) : null}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  {selectedConfig.label} product readiness
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Showing {visibleProducts.length} of {data.products.length} launch-scope products
                </Text>
              </BlockStack>
              <Button
                disabled={!productSearch && productStatus === "all" && productSort === "attention"}
                onClick={() => {
                  setProductSearch("");
                  setProductStatus("all");
                  setProductSort("attention");
                }}
              >
                Clear filters
              </Button>
            </InlineStack>
            <InlineGrid columns={{ xs: 1, md: 3 }} gap="200">
              <TextField
                label="Search products"
                value={productSearch}
                onChange={setProductSearch}
                autoComplete="off"
                placeholder="Title, ID, supplier or issue"
                clearButton
                onClearButtonClick={() => setProductSearch("")}
              />
              <Select
                label="Channel status"
                options={[
                  { label: "All statuses", value: "all" },
                  { label: "Blocked", value: "blocked" },
                  { label: "Needs review", value: "needs_review" },
                  { label: "Ready", value: "ready" },
                  { label: "Not assessed", value: "not_assessed" }
                ]}
                value={productStatus}
                onChange={setProductStatus}
              />
              <Select
                label="Sort products by"
                options={[
                  { label: "Needs attention", value: "attention" },
                  { label: "Title", value: "title" },
                  { label: "Supplier", value: "supplier" },
                  { label: "Lowest catalogue score", value: "catalog" }
                ]}
                value={productSort}
                onChange={setProductSort}
              />
            </InlineGrid>
            <DataTable
              columnContentTypes={["text", "text", "text", "text", "text", "text", "text"]}
              headings={[
                "Product",
                "Channel status",
                "Catalogue",
                "Options",
                "Delivery",
                "Validation issues",
                "Action"
              ]}
              rows={
                visibleProducts.length
                  ? visibleProducts.map((product) => {
                      const channelReadiness = product.channels[selectedChannel];
                      return [
                        product.title,
                        channelReadiness ? <StatusBadge status={channelReadiness.status} /> : "Not assessed",
                        `${product.signals.catalog_score ?? 0}/100 ${product.signals.catalog_status || ""}`,
                        `${product.signals.option_count} fields, ${product.signals.option_value_count} values`,
                        product.signals.service_levels.length ? product.signals.service_levels.join(", ") : "Missing",
                        channelReadiness?.gaps.length
                          ? formatGaps(channelReadiness.gaps)
                          : channelReadiness
                            ? "Ready"
                            : "Channel assessment unavailable",
                        <Button
                          onClick={() =>
                            navigateToAdmin(
                              `/products?supplier=${encodeURIComponent(product.supplier_key)}` +
                                `&search=${encodeURIComponent(product.product_id)}`
                            )
                          }
                        >
                          Review product
                        </Button>
                      ];
                    })
                  : [[
                      loading
                        ? "Loading channel readiness..."
                        : data.products.length
                          ? "No products match these filters"
                          : "No launch-scope products are selected or imported yet",
                      "",
                      "",
                      "",
                      "",
                      "",
                      ""
                    ]]
              }
            />
          </BlockStack>
        </Card>

        {data.recommendations.length ? (
          <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
            {data.recommendations.map((recommendation) => (
              <Card key={recommendation.title}>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingSm">
                    {recommendation.title}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {recommendation.detail}
                  </Text>
                </BlockStack>
              </Card>
            ))}
          </InlineGrid>
        ) : null}
      </BlockStack>
    </Page>
  );
}

const settingsTabs = [
  { id: "setup", content: "Connections & launch", panelID: "settings-setup" },
  { id: "pricing", content: "Pricing", panelID: "settings-pricing" },
  { id: "delivery", content: "Delivery promises", panelID: "settings-delivery" },
  { id: "artwork", content: "Artwork", panelID: "settings-artwork" }
];

function SettingsView({ context }: { context: AdminContext }) {
  const location = useBrowserLocation();
  const requestedTab = location.searchParams.get("tab");
  const selected = Math.max(
    0,
    settingsTabs.findIndex(
      (tab) => tab.id === requestedTab || location.pathname.includes(tab.id)
    )
  );
  const [selectedTab, setSelectedTab] = useState(selected);

  useEffect(() => {
    setSelectedTab(selected);
  }, [selected]);

  return (
    <Page title="Settings" subtitle="Connections, selling rules, delivery promises, and artwork defaults">
      <BlockStack gap="400">
        <Tabs
          tabs={settingsTabs}
          selected={selectedTab}
          onSelect={(nextTab) => {
            setSelectedTab(nextTab);
            const tabId = settingsTabs[nextTab]?.id || "setup";
            window.history.replaceState(null, "", `/settings?tab=${encodeURIComponent(tabId)}`);
          }}
          fitted
        />
        {settingsTabs[selectedTab]?.id === "setup" ? (
          <SetupView context={context} embedded />
        ) : settingsTabs[selectedTab]?.id === "delivery" ? (
          <DeliveryView embedded />
        ) : settingsTabs[selectedTab]?.id === "artwork" ? (
          <ArtworkView embedded />
        ) : (
          <PricingView embedded />
        )}
      </BlockStack>
    </Page>
  );
}

function PricingView({ embedded = false }: { embedded?: boolean }) {
  const {
    data: priceLists,
    loading: priceListsLoading,
    error: priceListsError
  } = useApiData<{ price_lists: PriceListRow[] }>("/tradeprint/products-v2/price-lists?summary=true", {
    price_lists: []
  });
  const {
    data: marginData,
    loading: marginLoading,
    error: marginError,
    reload: reloadMargin
  } = useApiData<{ global_margin_percent: number }>("/shopify/admin/margins/global", {
    global_margin_percent: 0
  });
  const [marginDraft, setMarginDraft] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const margin = marginDraft ?? String(marginData.global_margin_percent);
  const importedRowCount = priceLists.price_lists.reduce((total, item) => {
    return total + Number(item.row_count || 0);
  }, 0);
  const saveMargin = async () => {
    const normalizedMargin = Number(margin);
    if (!Number.isFinite(normalizedMargin) || normalizedMargin < 0 || normalizedMargin > 500) {
      setMessage("Global margin must be between 0% and 500%.");
      return;
    }
    setSaving(true);
    const result = await apiFetch<{ global_margin_percent: number }>(
      "/shopify/admin/margins/global",
      {
        method: "PUT",
        body: JSON.stringify({ global_margin_percent: normalizedMargin })
      }
    );
    setSaving(false);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    setMarginDraft(null);
    setMessage(`Global margin saved at ${normalizedMargin}%`);
    void reloadMargin();
  };

  const content = (
    <BlockStack gap="400">
      <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
      <DismissibleNotice
        message={marginError ? `Global margin could not be loaded: ${marginError}` : null}
      />
      <DismissibleNotice
        message={priceListsError ? `Imported price lists could not be loaded: ${priceListsError}` : null}
      />
      {marginLoading || priceListsLoading ? <LoadingState label="Loading pricing settings" /> : null}
      <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
        <MetricCard
          label="Global margin"
          value={`${marginData.global_margin_percent}%`}
          trend="current fallback"
        />
        <MetricCard
          label="Optional price lists"
          value={String(priceLists.price_lists.length)}
          trend="diagnostics"
        />
        <MetricCard
          label="Optional price rows"
          value={String(importedRowCount)}
          trend="diagnostics"
        />
      </InlineGrid>
      <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
        <Card>
          <FormLayout>
            <Text as="h2" variant="headingMd">
              Default selling margin
            </Text>
            <Text as="p" tone="subdued">
              Used only when a product or variant does not have its own margin.
            </Text>
            <TextField
              label="Global margin"
              value={margin}
              onChange={setMarginDraft}
              suffix="%"
              type="number"
              min={0}
              max={500}
              step={0.1}
              autoComplete="off"
              disabled={marginLoading || saving}
            />
            <Button
              variant="primary"
              loading={saving}
              disabled={
                marginLoading ||
                saving ||
                marginDraft === null ||
                Number(margin) === marginData.global_margin_percent
              }
              onClick={saveMargin}
            >
              Save global margin
            </Button>
          </FormLayout>
        </Card>
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Optional imported price lists
            </Text>
            <DataTable
              columnContentTypes={["text", "text", "numeric", "text"]}
              headings={["Product", "Supplier product", "Price rows", "Imported"]}
              rows={
                priceLists.price_lists.length
                  ? priceLists.price_lists.map((item) => [
                      item.product_name,
                      item.product_id || "Not linked",
                      Number(item.row_count || 0),
                      item.imported_at
                        ? new Date(item.imported_at).toLocaleString("en-GB")
                        : "Unknown"
                    ])
                  : [["No imported price lists", "", 0, ""]]
              }
            />
          </BlockStack>
        </Card>
      </InlineGrid>
    </BlockStack>
  );
  if (embedded) return content;
  return (
    <Page title="Pricing" subtitle="Global margin controls and optional price-list diagnostics">
      {content}
    </Page>
  );
}

function DeliveryView({ embedded = false }: { embedded?: boolean }) {
  const deliveryServices = ["Saver", "Standard", "Express", "Same Day"];
  const { data, loading, error, reload } = useApiData<{
    delivery: {
      enabled_service_levels: string[];
      date_padding_days: number;
      service_level_padding_days: Record<string, number>;
      same_day_cutoff_hour: number;
      same_day_cutoff_timezone: string;
      local_delivery_base_price_gbp: number | null;
      local_delivery_postcode_surcharges_gbp: Record<string, number>;
    };
  }>("/shopify/admin/delivery/global", {
    delivery: {
      enabled_service_levels: ["Saver", "Standard", "Express"],
      date_padding_days: 0,
      service_level_padding_days: { Saver: 1, Standard: 1, Express: 0 },
      same_day_cutoff_hour: 11,
      same_day_cutoff_timezone: "Europe/London",
      local_delivery_base_price_gbp: null,
      local_delivery_postcode_surcharges_gbp: {}
    }
  });
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [paddingDrafts, setPaddingDrafts] = useState<Record<string, string>>({});
  const [cutoffDraft, setCutoffDraft] = useState<string | null>(null);
  const [cutoffTimezoneDraft, setCutoffTimezoneDraft] = useState<string | null>(null);
  const [enabledServiceDraft, setEnabledServiceDraft] = useState<string[] | null>(null);
  const [localDeliveryBaseDraft, setLocalDeliveryBaseDraft] = useState<string | null>(null);
  const [localDeliverySurchargesDraft, setLocalDeliverySurchargesDraft] = useState<string | null>(null);
  const delivery = data.delivery;
  const enabledServices = enabledServiceDraft ?? delivery.enabled_service_levels;
  const draftFor = (service: string) =>
    paddingDrafts[service] ?? String(delivery.service_level_padding_days?.[service] ?? delivery.date_padding_days ?? 0);
  const cutoffValue = cutoffDraft ?? String(delivery.same_day_cutoff_hour ?? 11);
  const cutoffTimezone =
    cutoffTimezoneDraft ?? delivery.same_day_cutoff_timezone ?? "Europe/London";
  const localDeliveryBase =
    localDeliveryBaseDraft ??
    (delivery.local_delivery_base_price_gbp == null
      ? ""
      : String(delivery.local_delivery_base_price_gbp));
  const localDeliverySurcharges =
    localDeliverySurchargesDraft ??
    Object.entries(delivery.local_delivery_postcode_surcharges_gbp || {})
      .map(([prefix, amount]) => `${prefix}=${Number(amount).toFixed(2)}`)
      .join("\n");

  const saveDelivery = async () => {
    const servicePadding: Record<string, number> = {};
    for (const service of deliveryServices) {
      const value = Number(draftFor(service));
      if (!Number.isFinite(value) || value < 0 || value > 30) {
        setMessage("Delivery padding must be between 0 and 30 days");
        return;
      }
      servicePadding[service] = value;
    }
    const cutoff = Number(cutoffValue);
    if (!Number.isFinite(cutoff) || cutoff < 0 || cutoff > 23) {
      setMessage("Same day cutoff must be an hour between 0 and 23");
      return;
    }
    if (!cutoffTimezone.trim()) {
      setMessage("Enter the timezone used for the same day cutoff.");
      return;
    }
    if (!enabledServices.length) {
      setMessage("Enable at least one delivery service before saving.");
      return;
    }
    const localBase = localDeliveryBase.trim() === "" ? null : Number(localDeliveryBase);
    if (localBase != null && (!Number.isFinite(localBase) || localBase < 0 || localBase > 1000)) {
      setMessage("Local delivery base price must be between £0 and £1,000, or left blank.");
      return;
    }
    const localSurcharges: Record<string, number> = {};
    for (const [index, line] of localDeliverySurcharges.split(/\r?\n/).entries()) {
      if (!line.trim()) continue;
      const parts = line.split("=");
      const prefix = String(parts[0] || "").replace(/\s+/g, "").toUpperCase();
      const amount = Number(parts[1]);
      if (parts.length !== 2 || !/^[A-Z][A-Z0-9]{0,3}$/.test(prefix) || !Number.isFinite(amount) || amount < 0 || amount > 1000) {
        setMessage(`Local delivery surcharge line ${index + 1} must look like AB=5.00.`);
        return;
      }
      localSurcharges[prefix] = amount;
    }
    setSaving(true);
    const result = await apiFetch("/shopify/admin/delivery/global", {
      method: "PUT",
      body: JSON.stringify({
        enabled_service_levels: enabledServices,
        date_padding_days: 0,
        service_level_padding_days: servicePadding,
        same_day_cutoff_hour: cutoff,
        same_day_cutoff_timezone: cutoffTimezone.trim(),
        local_delivery_base_price_gbp: localBase,
        local_delivery_postcode_surcharges_gbp: localSurcharges
      })
    });
    setSaving(false);
    setMessage(result.error || "Global delivery padding saved");
    if (!result.error) {
      setPaddingDrafts({});
      setCutoffDraft(null);
      setCutoffTimezoneDraft(null);
      setEnabledServiceDraft(null);
      setLocalDeliveryBaseDraft(null);
      setLocalDeliverySurchargesDraft(null);
      void reload();
    }
  };

  const content = (
    <BlockStack gap="400">
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        <DismissibleNotice
          message={error ? `Delivery settings could not be loaded: ${error}` : null}
        />
        <Banner tone="info">
          These global padding values are used by storefront estimated dispatch labels unless a product has delivery overrides in the Products tab. Same Day becomes unavailable when the cutoff passes in the saved timezone.
        </Banner>
        <InlineGrid columns={{ xs: 1, md: 4 }} gap="400">
          {deliveryServices.map((service) => (
            <Card key={service}>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {service}
                </Text>
                <TextField
                  label="Merchant date padding"
                  type="number"
                  value={draftFor(service)}
                  suffix="days"
                  min={0}
                  max={30}
                  autoComplete="off"
                  disabled={loading || saving}
                  onChange={(value) => setPaddingDrafts((current) => ({ ...current, [service]: value }))}
                />
                <Checkbox
                  label="Enabled on storefront"
                  checked={enabledServices.includes(service)}
                  disabled={loading || saving}
                  onChange={(checked) =>
                    setEnabledServiceDraft((current) => {
                      const active = current ?? delivery.enabled_service_levels;
                      return checked
                        ? Array.from(new Set([...active, service]))
                        : active.filter((item) => item !== service);
                    })
                  }
                />
              </BlockStack>
            </Card>
          ))}
        </InlineGrid>
        <Card>
          <FormLayout>
            <TextField
              label="Same day cutoff hour"
              type="number"
              value={cutoffValue}
              suffix=":00"
              min={0}
              max={23}
              autoComplete="off"
              disabled={loading || saving}
              onChange={setCutoffDraft}
            />
            <TextField
              label="Same day cutoff timezone"
              value={cutoffTimezone}
              helpText="Use an IANA timezone, for example Europe/London."
              autoComplete="off"
              disabled={loading || saving}
              onChange={setCutoffTimezoneDraft}
            />
            <Button variant="primary" loading={saving} disabled={loading || saving} onClick={saveDelivery}>
              Save delivery promises
            </Button>
          </FormLayout>
        </Card>
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Impact and local delivery pricing
            </Text>
            <Text as="p" tone="subdued">
              This delivery charge is added at checkout for locally fulfilled signs. Leave the base price blank until local delivery pricing is ready.
            </Text>
            <FormLayout>
              <TextField
                label="Base local delivery price"
                type="number"
                value={localDeliveryBase}
                prefix="£"
                min={0}
                max={1000}
                step={0.01}
                autoComplete="off"
                disabled={loading || saving}
                helpText="The customer’s postcode area can add a surcharge below."
                onChange={setLocalDeliveryBaseDraft}
              />
              <TextField
                label="Postcode area surcharges"
                value={localDeliverySurcharges}
                multiline={6}
                autoComplete="off"
                disabled={loading || saving}
                helpText="Enter one outward-code prefix per line, for example AB=5.00 or IV=12.50. The most specific matching prefix is used."
                onChange={setLocalDeliverySurchargesDraft}
              />
            </FormLayout>
          </BlockStack>
        </Card>
      </BlockStack>
  );
  if (embedded) return content;
  return (
    <Page title="Delivery" subtitle="Global delivery padding defaults for print products">
      {content}
    </Page>
  );
}

function ArtworkView({ embedded = false }: { embedded?: boolean }) {
  const { data, loading, error, reload } = useApiData<{
    artwork: ArtworkPolicy;
    selected_product_count: number;
  }>("/shopify/admin/artwork/global", {
    artwork: {
      required: true,
      allow_upload_later: true,
      artwork_service: "Just Print",
      allowed_mime_types: ["application/pdf"],
      max_file_size_mb: 100,
      proof_required: true
    },
    selected_product_count: 0
  });
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [draft, setDraft] = useState<ArtworkPolicy | null>(null);
  const artwork = draft || data.artwork;
  const uploadPolicy = !artwork.required ? "optional" : artwork.allow_upload_later ? "upload_later" : "required";
  const mimeTypes = (artwork.allowed_mime_types || []).join(", ");

  useEffect(() => {
    setDraft(data.artwork);
  }, [data.artwork]);

  const setArtworkDraft = (patch: Partial<ArtworkPolicy>) =>
    setDraft((current) => ({ ...(current || data.artwork), ...patch }));

  const payloadFromDraft = () => ({
    ...artwork,
    allowed_mime_types: mimeTypes
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
    max_file_size_mb: Number(artwork.max_file_size_mb) || 100
  });

  const saveArtwork = async () => {
    if (!payloadFromDraft().allowed_mime_types.length) {
      setMessage("Add at least one accepted file type or MIME type");
      return;
    }
    setSaving(true);
    const result = await apiFetch<{ artwork: ArtworkPolicy }>("/shopify/admin/artwork/global", {
      method: "PUT",
      body: JSON.stringify(payloadFromDraft())
    });
    setSaving(false);
    setMessage(result.error || "Artwork rules saved");
    if (!result.error) void reload();
  };

  const applyArtwork = async () => {
    setApplying(true);
    const result = await apiFetch<{ applied: number }>("/shopify/admin/artwork/global/apply", {
      method: "POST",
      body: JSON.stringify({})
    });
    setApplying(false);
    setMessage(result.error || `Applied artwork rules to ${result.data?.applied || 0} selected products`);
  };

  const content = (
    <Layout>
        <Layout.Section>
          <Card>
            <FormLayout>
              <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
              <DismissibleNotice
                message={error ? `Artwork rules could not be loaded: ${error}` : null}
              />
              <Banner tone="info">
                These defaults are used by storefront upload validation and can be applied to products selected for sync.
              </Banner>
              <Select
                label="Default upload policy"
                options={[
                  { label: "Required before checkout", value: "required" },
                  { label: "Upload later allowed", value: "upload_later" },
                  { label: "Optional", value: "optional" }
                ]}
                value={uploadPolicy}
                disabled={loading || saving || applying}
                onChange={(value) =>
                  setArtworkDraft({
                    required: value !== "optional",
                    allow_upload_later: value === "upload_later"
                  })
                }
              />
              <Select
                label="Artwork service"
                options={["Just Print", "File Check", "File Assist", "File Check with Proofing"]}
                value={artwork.artwork_service}
                disabled={loading || saving || applying}
                onChange={(value) => setArtworkDraft({ artwork_service: value })}
              />
              <TextField
                label="Accepted file types"
                value={mimeTypes}
                autoComplete="off"
                helpText="Use MIME types or extensions, separated by commas."
                disabled={loading || saving || applying}
                onChange={(value) => setArtworkDraft({ allowed_mime_types: value.split(",").map((item) => item.trim()) })}
              />
              <TextField
                label="Maximum file size"
                type="number"
                value={String(artwork.max_file_size_mb)}
                suffix="MB"
                min={1}
                max={2048}
                autoComplete="off"
                disabled={loading || saving || applying}
                onChange={(value) => setArtworkDraft({ max_file_size_mb: Number(value) || 1 })}
              />
              <Checkbox
                label="Require merchant approval before supplier submission"
                checked={artwork.proof_required}
                disabled={loading || saving || applying}
                onChange={(checked) => setArtworkDraft({ proof_required: checked })}
              />
              <InlineStack gap="200">
                <Button variant="primary" loading={saving} disabled={loading || saving || applying} onClick={saveArtwork}>
                  Save artwork rules
                </Button>
                <Button loading={applying} disabled={loading || saving || applying} onClick={applyArtwork}>
                  {`Apply to ${data.selected_product_count} selected products`}
                </Button>
              </InlineStack>
            </FormLayout>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Customer conversion helpers
              </Text>
              <List>
                <List.Item>Template download and artwork checklist</List.Item>
                <List.Item>Upload-later link after checkout</List.Item>
                <List.Item>Proof approval and rejected-artwork remediation</List.Item>
              </List>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
  );
  if (embedded) return content;
  return (
    <Page title="Artwork" subtitle="Global upload policy and proof workflow rules">
      {content}
    </Page>
  );
}

type TradeprintControlMode = "auto" | "manual" | "degraded" | "paused";

type TradeprintControlState = {
  enabled: boolean;
  submit_only_manual: boolean;
  degraded: boolean;
};

const tradeprintControlModes: Array<{
  key: TradeprintControlMode;
  label: string;
  description: string;
  controls: TradeprintControlState;
}> = [
  {
    key: "auto",
    label: "Enable auto",
    description: "Eligible certified orders may submit to Tradeprint automatically.",
    controls: { enabled: true, submit_only_manual: false, degraded: false }
  },
  {
    key: "manual",
    label: "Manual only",
    description: "Eligible orders remain held until an operator approves submission.",
    controls: { enabled: true, submit_only_manual: true, degraded: false }
  },
  {
    key: "degraded",
    label: "Mark degraded",
    description: "Treat Tradeprint as available but unsafe for automatic submission.",
    controls: { enabled: true, submit_only_manual: false, degraded: true }
  },
  {
    key: "paused",
    label: "Pause supplier",
    description: "Stop new Tradeprint submissions until an operator changes the mode.",
    controls: { enabled: false, submit_only_manual: false, degraded: false }
  }
];

function tradeprintControlMode(controls: TradeprintControlState): TradeprintControlMode {
  if (!controls.enabled) return "paused";
  if (controls.submit_only_manual) return "manual";
  if (controls.degraded) return "degraded";
  return "auto";
}

function TradeprintControlActions({
  controls,
  onUpdated
}: {
  controls: TradeprintControlState;
  onUpdated: () => void;
}) {
  const [pendingMode, setPendingMode] = useState<TradeprintControlMode | null>(null);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const currentMode = tradeprintControlMode(controls);
  const pending = tradeprintControlModes.find((mode) => mode.key === pendingMode);
  const beginChange = (mode: TradeprintControlMode) => {
    setPendingMode(mode);
    setReason("");
    setConfirmed(false);
    setMessage(null);
  };
  const cancelChange = () => {
    setPendingMode(null);
    setReason("");
    setConfirmed(false);
  };
  const applyChange = async () => {
    if (!pending || reason.trim().length < 8 || !confirmed) return;
    setBusy(true);
    const result = await apiFetch(
      "/shopify/admin/fulfillment/suppliers/tradeprint/controls",
      {
        method: "PUT",
        body: JSON.stringify({
          ...pending.controls,
          reason: reason.trim(),
          confirmed: true
        })
      }
    );
    setBusy(false);
    if (result.error) {
      setMessage(`Tradeprint mode could not be changed: ${result.error}`);
      return;
    }
    setMessage(`Tradeprint mode changed to ${pending.label.toLowerCase()}.`);
    cancelChange();
    onUpdated();
  };

  return (
    <BlockStack gap="300">
      <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
      <InlineStack gap="200">
        {tradeprintControlModes.map((mode) => (
          <Button
            key={mode.key}
            tone={mode.key === "paused" ? "critical" : undefined}
            disabled={busy || currentMode === mode.key}
            onClick={() => beginChange(mode.key)}
          >
            {mode.label}
          </Button>
        ))}
      </InlineStack>
      {pending ? (
        <Box
          background="bg-surface-secondary"
          borderRadius="300"
          padding="300"
        >
          <BlockStack gap="300">
            <BlockStack gap="100">
              <Text as="h3" variant="headingSm">
                Confirm {pending.label.toLowerCase()}
              </Text>
              <Text as="p" tone="subdued">
                {pending.description}
              </Text>
            </BlockStack>
            <TextField
              label="Reason for this change"
              value={reason}
              onChange={setReason}
              autoComplete="off"
              helpText="Enter at least 8 characters. The reason is stored in audit history."
              error={
                reason.length > 0 && reason.trim().length < 8
                  ? "Enter at least 8 characters."
                  : undefined
              }
            />
            <Checkbox
              label={`I confirm this will change Tradeprint order routing to ${pending.label.toLowerCase()}.`}
              checked={confirmed}
              onChange={setConfirmed}
            />
            <InlineStack gap="200">
              <Button
                tone={
                  pending.key === "auto" || pending.key === "paused"
                    ? "critical"
                    : undefined
                }
                loading={busy}
                disabled={busy || reason.trim().length < 8 || !confirmed}
                onClick={applyChange}
              >
                Apply change
              </Button>
              <Button disabled={busy} onClick={cancelChange}>
                Cancel
              </Button>
            </InlineStack>
          </BlockStack>
        </Box>
      ) : null}
    </BlockStack>
  );
}

function TradeprintControlsCard() {
  const { data, error, reload } = useApiData<FulfillmentRulesResponse>("/shopify/admin/fulfillment/rules", {
    rules: [],
    tradeprint_controls: {
      supplier_key: "tradeprint",
      enabled: true,
      submit_only_manual: false,
      degraded: false,
      reason: null,
      source_rules: []
    },
    config_submission_enabled: true
  });
  return (
    <Card>
      <BlockStack gap="300">
        <DismissibleNotice
          message={error ? `Fulfilment controls could not be loaded: ${error}` : null}
        />
        <InlineStack align="space-between" blockAlign="center" gap="300">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">Tradeprint controls</Text>
            <Text as="p" tone="subdued">
              {data.tradeprint_controls.enabled
                ? data.tradeprint_controls.submit_only_manual
                  ? "Eligible orders are held for manual approval."
                  : data.tradeprint_controls.degraded
                    ? "Supplier is degraded; orders are held for review."
                    : "Eligible orders can submit automatically."
                : "Supplier submission is disabled."}
            </Text>
            {data.tradeprint_controls.reason ? (
              <Text as="p" tone="subdued">Reason: {data.tradeprint_controls.reason}</Text>
            ) : null}
          </BlockStack>
          <StatusBadge
            status={
              data.tradeprint_controls.enabled
                ? data.tradeprint_controls.submit_only_manual || data.tradeprint_controls.degraded
                  ? "Manual review"
                  : "Auto submit"
                : "Disabled"
            }
          />
        </InlineStack>
        <TradeprintControlActions
          controls={data.tradeprint_controls}
          onUpdated={reload}
        />
      </BlockStack>
    </Card>
  );
}

function RetentionCleanupActions({
  preview,
  onUpdated
}: {
  preview: RetentionStatus;
  onUpdated: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const cancel = () => {
    setConfirming(false);
    setReason("");
    setConfirmed(false);
  };
  const runCleanup = async () => {
    if (!confirmed || reason.trim().length < 8 || !preview.total) return;
    setBusy(true);
    const result = await apiFetch<RetentionStatus>(
      "/shopify/admin/operations/retention/run",
      {
        method: "POST",
        body: JSON.stringify({
          reason: reason.trim(),
          confirmed: true,
          preview_generated_at: preview.generated_at,
          expected_counts: preview.counts
        })
      }
    );
    setBusy(false);
    if (result.error) {
      setMessage(`Retention cleanup could not run: ${result.error}`);
      return;
    }
    setMessage(
      `Retention cleanup completed: ${result.data?.total ?? 0} eligible records processed.`
    );
    cancel();
    onUpdated();
  };

  return (
    <BlockStack gap="300">
      <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
      {!confirming ? (
        <Button
          tone="critical"
          disabled={!preview.total}
          onClick={() => {
            setConfirming(true);
            setMessage(null);
          }}
        >
          Review cleanup
        </Button>
      ) : (
        <Box
          background="bg-surface-secondary"
          borderRadius="300"
          padding="300"
        >
          <BlockStack gap="300">
            <BlockStack gap="100">
              <Text as="h3" variant="headingSm">
                Confirm data retention cleanup
              </Text>
              <Text as="p" tone="subdued">
                This will permanently delete or redact {preview.total} records from the
                datasets and cutoff dates shown in this preview. The action cannot be
                undone.
              </Text>
            </BlockStack>
            <TextField
              label="Reason for cleanup"
              value={reason}
              onChange={setReason}
              autoComplete="off"
              helpText="Enter at least 8 characters. The reason is stored in audit history."
              error={
                reason.length > 0 && reason.trim().length < 8
                  ? "Enter at least 8 characters."
                  : undefined
              }
            />
            <Checkbox
              label={`I confirm the current preview contains ${preview.total} eligible records and understand cleanup cannot be undone.`}
              checked={confirmed}
              onChange={setConfirmed}
            />
            <InlineStack gap="200">
              <Button
                tone="critical"
                loading={busy}
                disabled={busy || reason.trim().length < 8 || !confirmed}
                onClick={runCleanup}
              >
                Run confirmed cleanup
              </Button>
              <Button disabled={busy} onClick={cancel}>
                Cancel
              </Button>
            </InlineStack>
          </BlockStack>
        </Box>
      )}
    </BlockStack>
  );
}

function OrdersView() {
  const [orderFilter, setOrderFilter] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [orderRouteFilter, setOrderRouteFilter] = useState("all");
  const [orderAttentionFilter, setOrderAttentionFilter] = useState("all");
  const [orderSort, setOrderSort] = useState("attention");
  const [orderSortDirection, setOrderSortDirection] = useState("asc");
  const [orderOffset, setOrderOffset] = useState(0);
  const [reviewFilter, setReviewFilter] = useState("");
  const [reviewSeverityFilter, setReviewSeverityFilter] = useState("all");
  const [reviewReasonFilter, setReviewReasonFilter] = useState("all");
  const [reviewSort, setReviewSort] = useState("priority");
  const [reviewSortDirection, setReviewSortDirection] = useState("asc");
  const [reviewOffset, setReviewOffset] = useState(0);
  const debouncedOrderFilter = useDebouncedValue(orderFilter, 250);
  const debouncedReviewFilter = useDebouncedValue(reviewFilter, 250);
  const orderPageLimit = 50;
  const reviewPageLimit = 25;
  const lifecyclePath = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(orderPageLimit),
      offset: String(orderOffset),
      status: orderStatusFilter,
      route: orderRouteFilter,
      attention: orderAttentionFilter,
      sort: orderSort,
      direction: orderSortDirection
    });
    if (debouncedOrderFilter.trim()) params.set("search", debouncedOrderFilter.trim());
    return `/shopify/admin/orders/lifecycle?${params.toString()}`;
  }, [
    debouncedOrderFilter,
    orderAttentionFilter,
    orderOffset,
    orderRouteFilter,
    orderSort,
    orderSortDirection,
    orderStatusFilter
  ]);
  const reviewPath = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(reviewPageLimit),
      offset: String(reviewOffset),
      sort: reviewSort,
      direction: reviewSortDirection
    });
    if (debouncedReviewFilter.trim()) params.set("search", debouncedReviewFilter.trim());
    if (reviewSeverityFilter !== "all") params.set("severity", reviewSeverityFilter);
    if (reviewReasonFilter !== "all") params.set("reason_code", reviewReasonFilter);
    return `/shopify/admin/reviews?${params.toString()}`;
  }, [
    debouncedReviewFilter,
    reviewOffset,
    reviewReasonFilter,
    reviewSeverityFilter,
    reviewSort,
    reviewSortDirection
  ]);
  const {
    data,
    loading,
    error,
    reload
  } = useApiData<ReviewQueueResponse>(reviewPath, {
    items: [],
    summary: { open: 0, critical: 0, high: 0, by_severity: {} },
    facets: { severities: [], reasons: [], statuses: [] },
    pagination: {
      offset: 0,
      limit: reviewPageLimit,
      returned: 0,
      total: 0,
      has_previous: false,
      has_next: false
    }
  });
  const {
    data: lifecycle,
    loading: lifecycleLoading,
    error: lifecycleError,
    reload: reloadLifecycle
  } = useApiData<OrderLifecycleResponse>(lifecyclePath, {
    items: [],
    summary: { total: 0, action_required: 0, by_status: {} }
  });
  useEffect(() => {
    setOrderOffset(0);
  }, [
    debouncedOrderFilter,
    orderAttentionFilter,
    orderRouteFilter,
    orderSort,
    orderSortDirection,
    orderStatusFilter
  ]);
  useEffect(() => {
    setReviewOffset(0);
  }, [
    debouncedReviewFilter,
    reviewReasonFilter,
    reviewSeverityFilter,
    reviewSort,
    reviewSortDirection
  ]);
  const [message, setMessage] = useState<string | null>(null);
  const [reviewActionBusy, setReviewActionBusy] = useState<string | null>(null);
  const [localFulfillmentBusy, setLocalFulfillmentBusy] = useState<string | null>(null);
  const [fulfillmentRequestBusy, setFulfillmentRequestBusy] = useState<string | null>(null);
  const [proofOrderId, setProofOrderId] = useState<string | null>(null);
  const [proofUrl, setProofUrl] = useState("");
  const [proofVersion, setProofVersion] = useState("");
  const [proofExpiryHours, setProofExpiryHours] = useState("72");
  const [proofReason, setProofReason] = useState("");
  const [proofConfirmed, setProofConfirmed] = useState(false);
  const [proofBusy, setProofBusy] = useState(false);
  const [customerUpdateOrderId, setCustomerUpdateOrderId] = useState<string | null>(null);
  const [customerUpdateAction, setCustomerUpdateAction] = useState<"publish" | "withdraw">("publish");
  const [customerUpdateMessage, setCustomerUpdateMessage] = useState("");
  const [customerUpdateReason, setCustomerUpdateReason] = useState("");
  const [customerUpdateConfirmed, setCustomerUpdateConfirmed] = useState(false);
  const [customerUpdateBusy, setCustomerUpdateBusy] = useState(false);
  const [holdOrderId, setHoldOrderId] = useState<string | null>(null);
  const [holdAction, setHoldAction] = useState<"hold" | "release">("hold");
  const [holdReason, setHoldReason] = useState("");
  const [holdConfirmed, setHoldConfirmed] = useState(false);
  const [holdBusy, setHoldBusy] = useState(false);
  const [reconciliationReview, setReconciliationReview] = useState<ReviewItem | null>(null);
  const [reconciliationReason, setReconciliationReason] = useState("");
  const [reconciliationConfirmed, setReconciliationConfirmed] = useState(false);
  const [reconciliationBusy, setReconciliationBusy] = useState(false);
  const refresh = () => {
    void reload();
    void reloadLifecycle();
  };
  const updateLocalFulfillment = async (orderId: string, status: string) => {
    setLocalFulfillmentBusy(`${orderId}:${status}`);
    try {
      const result = await apiFetch(
        `/shopify/admin/orders/${encodeURIComponent(orderId)}/local-fulfillment`,
        {
          method: "PUT",
          body: JSON.stringify({
            status,
            note: `Set local fulfilment to ${status} from admin`
          })
        }
      );
      setMessage(result.error || "Local fulfilment updated");
      refresh();
    } finally {
      setLocalFulfillmentBusy(null);
    }
  };
  const requestShopifyFulfillment = async (orderId: string) => {
    setFulfillmentRequestBusy(orderId);
    try {
      const result = await apiFetch<{
        requested: boolean;
        already_requested: boolean;
        request_status: string;
      }>(
        `/shopify/admin/orders/${encodeURIComponent(orderId)}/request-fulfillment`,
        {
          method: "POST",
          body: JSON.stringify({
            reason: "Merchant requested Tradeprint fulfilment from PrintConnect admin"
          })
        }
      );
      if (result.error) {
        const guidance: Record<string, string> = {
          fulfillment_request_action_unavailable:
            "Shopify cannot request fulfilment yet. Assign the Tradeprint items to a third-party fulfilment service in Shopify, then retry.",
          fulfillment_assignment_unavailable:
            "Shopify has not assigned these Tradeprint items to a fulfilment location yet.",
          missing_tradeprint_line_mapping:
            "The Shopify fulfilment lines no longer match this PrintConnect order. Review the order before retrying.",
          ambiguous_tradeprint_line_mapping:
            "Shopify returned an ambiguous fulfilment-line mapping. No request was sent; review the order routing.",
          ambiguous_fulfillment_order_mapping:
            "The Tradeprint items span more than one Shopify fulfilment order. No request was sent; review the order routing.",
          tradeprint_submission_required:
            "The supplier order must pass payment, artwork and submission checks before Shopify fulfilment can be requested."
        };
        setMessage(guidance[result.error] || result.error);
        return;
      }
      setMessage(
        result.data?.already_requested
          ? `Shopify fulfilment was already ${statusLabel(result.data.request_status).toLowerCase()}`
          : "Shopify fulfilment requested"
      );
      refresh();
    } finally {
      setFulfillmentRequestBusy(null);
    }
  };
  const issueSummary = (item: ReviewItem) => {
    const decision = item.context?.fulfillment_decision as
      | { action?: string; issues?: Array<{ code?: string }>; warnings?: Array<{ code?: string }> }
      | undefined;
    const issues = decision?.issues?.map((issue) => issue.code).filter(Boolean) || [];
    const warnings = decision?.warnings?.map((warning) => warning.code).filter(Boolean) || [];
    return [...issues, ...warnings].slice(0, 3).join(", ") || item.reason_code;
  };
  const isSupplierReconciliationReview = (item: ReviewItem) =>
    [
      "tradeprint_order_items_missing",
      "tradeprint_item_cancellation_failed",
      "tradeprint_refund_requires_manual_review"
    ].includes(item.reason_code);
  const supplierReconciliationOutcome = (item: ReviewItem) =>
    item.reason_code === "tradeprint_refund_requires_manual_review"
      ? "supplier_refund_reconciled"
      : "supplier_cancellation_confirmed";
  const openSupplierReconciliation = (item: ReviewItem) => {
    setReconciliationReview(item);
    setReconciliationReason("");
    setReconciliationConfirmed(false);
    setMessage(null);
  };
  const closeSupplierReconciliation = () => {
    if (reconciliationBusy) return;
    setReconciliationReview(null);
    setReconciliationReason("");
    setReconciliationConfirmed(false);
  };
  const recordSupplierReconciliation = async () => {
    if (!reconciliationReview) return;
    if (reconciliationReason.trim().length < 8) {
      setMessage("Explain how the supplier cancellation or refund was reconciled.");
      return;
    }
    if (!reconciliationConfirmed) {
      setMessage("Confirm the supplier outcome before resolving this review.");
      return;
    }
    setReconciliationBusy(true);
    try {
      const outcome = supplierReconciliationOutcome(reconciliationReview);
      const result = await apiFetch(
        `/shopify/admin/reviews/${encodeURIComponent(reconciliationReview.review_key)}/reconcile-supplier`,
        {
          method: "POST",
          body: JSON.stringify({
            outcome,
            reason: reconciliationReason.trim(),
            confirmed: true
          })
        }
      );
      if (result.error) {
        setMessage(`Supplier reconciliation could not be recorded: ${result.error}`);
        return;
      }
      setMessage(
        outcome === "supplier_cancellation_confirmed"
          ? `Order ${reconciliationReview.order_id || ""} is recorded as cancelled.`
          : `Order ${reconciliationReview.order_id || ""} is recorded as refunded.`
      );
      setReconciliationReview(null);
      setReconciliationReason("");
      setReconciliationConfirmed(false);
      refresh();
    } finally {
      setReconciliationBusy(false);
    }
  };
  const openProofPublisher = (orderId: string) => {
    setProofOrderId(orderId);
    setProofUrl("");
    setProofVersion("");
    setProofExpiryHours("72");
    setProofReason("");
    setProofConfirmed(false);
    setMessage(null);
  };
  const openCustomerUpdate = (
    item: OrderLifecycleItem,
    action: "publish" | "withdraw"
  ) => {
    if (!item.shopify_order_id) return;
    setCustomerUpdateOrderId(item.shopify_order_id);
    setCustomerUpdateAction(action);
    setCustomerUpdateMessage(
      action === "publish" ? item.customer_update_message || "" : ""
    );
    setCustomerUpdateReason("");
    setCustomerUpdateConfirmed(false);
    setMessage(null);
  };
  const closeCustomerUpdate = () => {
    if (customerUpdateBusy) return;
    setCustomerUpdateOrderId(null);
    setCustomerUpdateMessage("");
    setCustomerUpdateReason("");
    setCustomerUpdateConfirmed(false);
  };
  const submitCustomerUpdate = async () => {
    if (!customerUpdateOrderId) return;
    if (
      customerUpdateAction === "publish" &&
      customerUpdateMessage.trim().length < 8
    ) {
      setMessage("Enter a customer update of at least 8 characters.");
      return;
    }
    if (customerUpdateReason.trim().length < 8) {
      setMessage("Explain why this customer update is changing.");
      return;
    }
    if (!customerUpdateConfirmed) {
      setMessage("Confirm the customer update before continuing.");
      return;
    }
    setCustomerUpdateBusy(true);
    try {
      const result = await apiFetch(
        `/shopify/admin/orders/${encodeURIComponent(customerUpdateOrderId)}/customer-update`,
        {
          method: "POST",
          body: JSON.stringify({
            action: customerUpdateAction,
            message:
              customerUpdateAction === "publish"
                ? customerUpdateMessage.trim()
                : null,
            reason: customerUpdateReason.trim(),
            confirmed: true
          })
        }
      );
      if (result.error) {
        setMessage(`Customer update could not be changed: ${result.error}`);
        return;
      }
      setMessage(
        customerUpdateAction === "publish"
          ? `Customer update published for order ${customerUpdateOrderId}.`
          : `Customer update withdrawn for order ${customerUpdateOrderId}.`
      );
      setCustomerUpdateOrderId(null);
      setCustomerUpdateMessage("");
      setCustomerUpdateReason("");
      setCustomerUpdateConfirmed(false);
      refresh();
    } finally {
      setCustomerUpdateBusy(false);
    }
  };
  const closeProofPublisher = () => {
    if (proofBusy) return;
    setProofOrderId(null);
    setProofConfirmed(false);
  };
  const publishCustomerProof = async () => {
    if (!proofOrderId) return;
    const expiryHours = Number(proofExpiryHours);
    if (!proofUrl.trim().toLowerCase().startsWith("https://")) {
      setMessage("Enter the customer-accessible HTTPS link for the proof.");
      return;
    }
    if (!proofVersion.trim()) {
      setMessage("Enter a unique proof version.");
      return;
    }
    if (!Number.isInteger(expiryHours) || expiryHours < 1 || expiryHours > 720) {
      setMessage("Proof access must expire between 1 and 720 hours from now.");
      return;
    }
    if (proofReason.trim().length < 8) {
      setMessage("Explain why this proof is ready for customer review.");
      return;
    }
    if (!proofConfirmed) {
      setMessage("Confirm that this exact proof is ready for the customer.");
      return;
    }
    setProofBusy(true);
    try {
      const result = await apiFetch(
        `/shopify/admin/orders/${encodeURIComponent(proofOrderId)}/customer-proof`,
        {
          method: "POST",
          body: JSON.stringify({
            proof_url: proofUrl.trim(),
            version: proofVersion.trim(),
            expires_in_hours: expiryHours,
            reason: proofReason.trim(),
            confirm_publish: true
          })
        }
      );
      if (result.error) {
        setMessage(
          result.error === "Use a new proof version before publishing a replacement"
            ? result.error
            : `Customer proof could not be published: ${result.error}`
        );
        return;
      }
      setMessage(`Proof ${proofVersion.trim()} is ready for customer review.`);
      setProofOrderId(null);
      setProofConfirmed(false);
      refresh();
    } finally {
      setProofBusy(false);
    }
  };
  const openHoldEditor = (orderId: string, action: "hold" | "release") => {
    setHoldOrderId(orderId);
    setHoldAction(action);
    setHoldReason("");
    setHoldConfirmed(false);
    setMessage(null);
  };
  const closeHoldEditor = () => {
    if (holdBusy) return;
    setHoldOrderId(null);
    setHoldReason("");
    setHoldConfirmed(false);
  };
  const updateOrderHold = async () => {
    if (!holdOrderId) return;
    if (holdReason.trim().length < 8) {
      setMessage("Explain why this order hold is changing.");
      return;
    }
    if (!holdConfirmed) {
      setMessage("Confirm the order hold change before continuing.");
      return;
    }
    setHoldBusy(true);
    try {
      const result = await apiFetch(
        `/shopify/admin/orders/${encodeURIComponent(holdOrderId)}/hold`,
        {
          method: "POST",
          body: JSON.stringify({
            action: holdAction,
            reason: holdReason.trim(),
            confirmed: true
          })
        }
      );
      if (result.error) {
        setMessage(`Order hold could not be changed: ${result.error}`);
        return;
      }
      setMessage(
        holdAction === "hold"
          ? `Order ${holdOrderId} is on hold.`
          : `Order ${holdOrderId} was released from hold.`
      );
      setHoldOrderId(null);
      setHoldReason("");
      setHoldConfirmed(false);
      refresh();
    } finally {
      setHoldBusy(false);
    }
  };
  const formatReviewDate = (value?: string) => {
    if (!value) return "Unknown";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  return (
    <Page title="Orders" subtitle="Manual review, supplier submission, and fulfilment safety" primaryAction={{ content: "Refresh", onAction: refresh }}>
      <BlockStack gap="400">
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        <DismissibleNotice
          message={error ? `Review queue could not be loaded: ${error}` : null}
        />
        <DismissibleNotice
          message={lifecycleError ? `Order lifecycle could not be loaded: ${lifecycleError}` : null}
        />
        {loading || lifecycleLoading ? <LoadingState label="Loading orders and review queue" /> : null}
        <Banner tone="info">
          Orders with uncertain pricing, artwork, mapping, or supplier availability are held for manual review before Tradeprint submission.
        </Banner>
        <Card>
          <BlockStack gap="300">
            <InlineGrid columns={{ xs: 1, md: 4 }} gap="200">
              <TextField
                label="Search orders"
                value={orderFilter}
                onChange={setOrderFilter}
                autoComplete="off"
                placeholder="Order, supplier reference, tracking..."
                clearButton
                onClearButtonClick={() => setOrderFilter("")}
              />
              <Select
                label="Attention"
                value={orderAttentionFilter}
                options={[
                  { label: "All orders", value: "all" },
                  { label: "Action required", value: "required" },
                  { label: "No action required", value: "clear" }
                ]}
                onChange={setOrderAttentionFilter}
              />
              <Select
                label="Status"
                value={orderStatusFilter}
                options={[
                  { label: "All statuses", value: "all" },
                  ...(lifecycle.facets?.statuses || []).map((status) => ({
                    label: statusLabel(status),
                    value: status
                  }))
                ]}
                onChange={setOrderStatusFilter}
              />
              <Select
                label="Fulfilment route"
                value={orderRouteFilter}
                options={[
                  { label: "All routes", value: "all" },
                  ...(lifecycle.facets?.routes || []).map((route) => ({
                    label:
                      route === "tradeprint"
                        ? "Tradeprint"
                        : route === "local"
                          ? "Local fulfilment"
                          : statusLabel(route),
                    value: route
                  }))
                ]}
                onChange={setOrderRouteFilter}
              />
            </InlineGrid>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
              <Select
                label="Sort orders by"
                value={orderSort}
                options={[
                  { label: "Needs attention", value: "attention" },
                  { label: "Last updated", value: "updated" },
                  { label: "Order number", value: "order" },
                  { label: "Status", value: "status" }
                ]}
                onChange={setOrderSort}
              />
              <Select
                label="Sort direction"
                value={orderSortDirection}
                options={[
                  { label: "Ascending", value: "asc" },
                  { label: "Descending", value: "desc" }
                ]}
                onChange={setOrderSortDirection}
              />
            </InlineGrid>
            <InlineStack align="end">
              <Button
                disabled={
                  !orderFilter &&
                  orderStatusFilter === "all" &&
                  orderRouteFilter === "all" &&
                  orderAttentionFilter === "all"
                }
                onClick={() => {
                  setOrderFilter("");
                  setOrderStatusFilter("all");
                  setOrderRouteFilter("all");
                  setOrderAttentionFilter("all");
                }}
              >
                Clear filters
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
        <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
          <MetricCard label="Tracked orders" value={String(lifecycle.summary.total)} trend="live" />
          <MetricCard label="Need attention" value={String(lifecycle.summary.action_required)} trend="manual review" />
          <MetricCard label="Supplier submitted" value={String(lifecycle.summary.by_status.submitted || 0)} trend="Tradeprint" />
        </InlineGrid>
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Fulfilment lifecycle</Text>
            <DataTable
              columnContentTypes={["text", "text", "text", "text", "text", "text", "text", "text", "text"]}
              headings={["Order", "Tradeprint", "Status", "Decision", "Pricing", "Fulfilment", "Local fulfilment", "Customer proof", "Tracking"]}
              rows={
                lifecycle.items.length
                  ? lifecycle.items.map((item) => [
                      item.shopify_order_id || "Unknown",
                      item.tradeprint_order_id || "Pending",
                      <BlockStack gap="100">
                        <StatusBadge status={item.status} />
                        {item.customer_update_message ? (
                          <>
                            <Text as="span" tone="subdued">
                              {item.customer_update_message}
                            </Text>
                            {item.customer_update_published_at ? (
                              <Text as="span" tone="subdued">
                                Published {formatReviewDate(item.customer_update_published_at)}
                              </Text>
                            ) : null}
                          </>
                        ) : null}
                        {item.shopify_order_id ? (
                          <InlineStack gap="100">
                            <Button
                              size="slim"
                              disabled={customerUpdateBusy}
                              onClick={() => openCustomerUpdate(item, "publish")}
                            >
                              {item.customer_update_message
                                ? "Edit customer update"
                                : "Publish customer update"}
                            </Button>
                            {item.customer_update_message ? (
                              <Button
                                size="slim"
                                tone="critical"
                                disabled={customerUpdateBusy}
                                onClick={() => openCustomerUpdate(item, "withdraw")}
                              >
                                Withdraw
                              </Button>
                            ) : null}
                          </InlineStack>
                        ) : null}
                      </BlockStack>,
                      <BlockStack gap="100">
                        <Text as="span">
                          {item.merchant_hold_active
                            ? "Merchant hold"
                            : item.decision_reason ||
                              item.decision_action ||
                              (item.action_required ? "Action required" : "OK")}
                        </Text>
                        {item.merchant_hold_reason ? (
                          <Text as="span" tone="subdued">
                            {item.merchant_hold_reason}
                          </Text>
                        ) : null}
                        {item.merchant_hold_at ? (
                          <Text as="span" tone="subdued">
                            Held {formatReviewDate(item.merchant_hold_at)}
                          </Text>
                        ) : null}
                        {item.shopify_order_id &&
                        (item.can_place_hold || item.can_release_hold) ? (
                          <Button
                            size="slim"
                            disabled={holdBusy}
                            onClick={() =>
                              openHoldEditor(
                                item.shopify_order_id!,
                                item.can_release_hold ? "release" : "hold"
                              )
                            }
                          >
                            {item.can_release_hold ? "Release hold" : "Place on hold"}
                          </Button>
                        ) : null}
                      </BlockStack>,
                      item.pricing_status || "Not checked",
                      <BlockStack gap="100">
                        <Text as="span">
                          {item.shopify_fulfillment_status
                            ? `Fulfilment ${statusLabel(item.shopify_fulfillment_status).toLowerCase()}`
                            : "Fulfilment not created"}
                        </Text>
                        {item.shopify_fulfillment_request_status ? (
                          <Text as="span" tone="subdued">
                            Request {statusLabel(item.shopify_fulfillment_request_status).toLowerCase()}
                          </Text>
                        ) : null}
                        {item.can_request_shopify_fulfillment ? (
                          <Button
                            size="slim"
                            loading={fulfillmentRequestBusy === item.shopify_order_id}
                            disabled={!item.shopify_order_id || Boolean(fulfillmentRequestBusy)}
                            onClick={() =>
                              item.shopify_order_id &&
                              requestShopifyFulfillment(item.shopify_order_id)
                            }
                          >
                            Request Shopify fulfilment
                          </Button>
                        ) : null}
                      </BlockStack>,
                      item.local_fulfillment_item_count ? (
                        <BlockStack gap="100">
                          <Text as="span">
                            {item.local_fulfillment_status || "queued"} · {item.local_fulfillment_item_count} item
                            {item.local_fulfillment_item_count === 1 ? "" : "s"}
                          </Text>
                          {item.local_fulfillment_suppliers?.length ? (
                            <Text as="span" tone="subdued">{item.local_fulfillment_suppliers.join(", ")}</Text>
                          ) : null}
                          {item.local_fulfillment_customer_message ? (
                            <Text as="span" tone="subdued">{item.local_fulfillment_customer_message}</Text>
                          ) : null}
                          <InlineStack gap="100">
                            {["in_production", "ready", "fulfilled", "cancelled"].map((status) => (
                              <Button
                                key={status}
                                size="slim"
                                disabled={!item.shopify_order_id || Boolean(localFulfillmentBusy)}
                                loading={localFulfillmentBusy === `${item.shopify_order_id}:${status}`}
                                onClick={() => item.shopify_order_id && updateLocalFulfillment(item.shopify_order_id, status)}
                              >
                                {statusLabel(status)}
                              </Button>
                            ))}
                          </InlineStack>
                        </BlockStack>
                      ) : "Not required",
                      <BlockStack gap="100">
                        <Text as="span">
                          {item.proof_status
                            ? statusLabel(item.proof_status)
                            : "Not published"}
                        </Text>
                        {item.proof_version ? (
                          <Text as="span" tone="subdued">
                            Version {item.proof_version}
                          </Text>
                        ) : null}
                        {item.proof_expires_at ? (
                          <Text as="span" tone="subdued">
                            Expires {formatReviewDate(item.proof_expires_at)}
                          </Text>
                        ) : null}
                        {item.can_publish_proof && item.shopify_order_id ? (
                          <Button
                            size="slim"
                            disabled={proofBusy}
                            onClick={() => openProofPublisher(item.shopify_order_id!)}
                          >
                            {item.proof_version ? "Replace customer proof" : "Publish customer proof"}
                          </Button>
                        ) : null}
                      </BlockStack>,
                      item.tracking_number || "Not available"
                    ])
                  : [[lifecycleLoading ? "Loading tracked orders..." : "No tracked orders yet", "", "", "", "", "", "", "", ""]]
              }
            />
            {holdOrderId ? (
              <Card>
                <BlockStack gap="300">
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingMd">
                      {holdAction === "hold" ? "Place" : "Release"} order{" "}
                      {holdOrderId} {holdAction === "hold" ? "on hold" : "from hold"}
                    </Text>
                    <Text as="p" tone="subdued">
                      {holdAction === "hold"
                        ? "A hold blocks both automatic retries and manual supplier submission. It is available only before production starts."
                        : "Releasing the hold restores the previous workflow state. It does not automatically submit the order."}
                    </Text>
                  </BlockStack>
                  <TextField
                    label={
                      holdAction === "hold"
                        ? "Reason for placing this order on hold"
                        : "Reason for releasing this order"
                    }
                    value={holdReason}
                    maxLength={500}
                    autoComplete="off"
                    disabled={holdBusy}
                    helpText="Enter at least 8 characters. Do not include customer or artwork data; the reason is stored in order and audit history."
                    error={
                      holdReason.length > 0 && holdReason.trim().length < 8
                        ? "Enter at least 8 characters."
                        : undefined
                    }
                    onChange={setHoldReason}
                  />
                  <Checkbox
                    label={
                      holdAction === "hold"
                        ? "I confirm this order must not be submitted to its supplier until the hold is released"
                        : "I confirm the blocking issue has been resolved and this order may return to its previous workflow state"
                    }
                    checked={holdConfirmed}
                    disabled={holdBusy}
                    onChange={setHoldConfirmed}
                  />
                  <InlineStack gap="200">
                    <Button
                      variant="primary"
                      tone={holdAction === "hold" ? "critical" : undefined}
                      loading={holdBusy}
                      disabled={
                        holdBusy ||
                        holdReason.trim().length < 8 ||
                        !holdConfirmed
                      }
                      onClick={() => void updateOrderHold()}
                    >
                      {holdAction === "hold" ? "Confirm hold" : "Confirm release"}
                    </Button>
                    <Button disabled={holdBusy} onClick={closeHoldEditor}>
                      Cancel
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            ) : null}
            {customerUpdateOrderId ? (
              <Card>
                <BlockStack gap="300">
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingMd">
                      {customerUpdateAction === "publish" ? "Publish" : "Withdraw"} customer update for{" "}
                      {customerUpdateOrderId}
                    </Text>
                    <Text as="p" tone="subdued">
                      This update appears only in the authenticated Shopify customer order-status block. It does not send an email or a Shopify notification.
                    </Text>
                  </BlockStack>
                  {customerUpdateAction === "publish" ? (
                    <TextField
                      label="Customer update"
                      value={customerUpdateMessage}
                      onChange={setCustomerUpdateMessage}
                      autoComplete="off"
                      multiline={3}
                      maxLength={500}
                      showCharacterCount
                      disabled={customerUpdateBusy}
                      helpText="Use plain, helpful language. Do not include supplier names, costs, margins, credentials or internal diagnostics."
                    />
                  ) : (
                    <Banner tone="warning">
                      The current update will stop appearing in the customer order-status block. This does not recall any separate message already sent outside PrintConnect.
                    </Banner>
                  )}
                  <TextField
                    label="Internal reason"
                    value={customerUpdateReason}
                    onChange={setCustomerUpdateReason}
                    autoComplete="off"
                    maxLength={500}
                    disabled={customerUpdateBusy}
                    helpText="Recorded in order history and audit; do not include customer data."
                  />
                  <Checkbox
                    label={
                      customerUpdateAction === "publish"
                        ? "I confirm this supplier-neutral update is ready for the customer to read"
                        : "I confirm this customer update should be withdrawn"
                    }
                    checked={customerUpdateConfirmed}
                    disabled={customerUpdateBusy}
                    onChange={setCustomerUpdateConfirmed}
                  />
                  <InlineStack gap="200">
                    <Button
                      variant="primary"
                      tone={customerUpdateAction === "withdraw" ? "critical" : undefined}
                      loading={customerUpdateBusy}
                      disabled={
                        customerUpdateBusy ||
                        (customerUpdateAction === "publish" &&
                          customerUpdateMessage.trim().length < 8) ||
                        customerUpdateReason.trim().length < 8 ||
                        !customerUpdateConfirmed
                      }
                      onClick={submitCustomerUpdate}
                    >
                      {customerUpdateAction === "publish"
                        ? "Publish update"
                        : "Confirm withdrawal"}
                    </Button>
                    <Button disabled={customerUpdateBusy} onClick={closeCustomerUpdate}>
                      Cancel
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            ) : null}
            {proofOrderId ? (
              <Card>
                <BlockStack gap="300">
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingMd">
                      Publish customer proof for {proofOrderId}
                    </Text>
                    <Text as="p" tone="subdued">
                      The authenticated order owner will be able to open this proof and approve it or request changes.
                    </Text>
                  </BlockStack>
                  <FormLayout>
                    <TextField
                      label="Proof HTTPS link"
                      type="url"
                      value={proofUrl}
                      placeholder="https://..."
                      autoComplete="off"
                      disabled={proofBusy}
                      onChange={setProofUrl}
                    />
                    <TextField
                      label="Proof version"
                      value={proofVersion}
                      placeholder="proof-v1"
                      helpText="Use a new value whenever the proof file changes."
                      autoComplete="off"
                      disabled={proofBusy}
                      onChange={setProofVersion}
                    />
                    <TextField
                      label="Proof access expires after"
                      type="number"
                      value={proofExpiryHours}
                      suffix="hours"
                      min={1}
                      max={720}
                      autoComplete="off"
                      disabled={proofBusy}
                      onChange={setProofExpiryHours}
                    />
                    <TextField
                      label="Publication reason"
                      value={proofReason}
                      helpText="Recorded in order history and audit; do not include customer data."
                      autoComplete="off"
                      disabled={proofBusy}
                      onChange={setProofReason}
                    />
                    <Checkbox
                      label="I confirm this exact proof is ready for the customer to review"
                      checked={proofConfirmed}
                      disabled={proofBusy}
                      onChange={setProofConfirmed}
                    />
                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        loading={proofBusy}
                        disabled={proofBusy}
                        onClick={publishCustomerProof}
                      >
                        Publish proof
                      </Button>
                      <Button disabled={proofBusy} onClick={closeProofPublisher}>
                        Cancel
                      </Button>
                    </InlineStack>
                  </FormLayout>
                </BlockStack>
              </Card>
            ) : null}
            {lifecycle.pagination ? (
              <InlineStack align="space-between" blockAlign="center">
                <Text as="p" tone="subdued">
                  Showing{" "}
                  {lifecycle.pagination.total
                    ? `${lifecycle.pagination.offset + 1}-${Math.min(
                        lifecycle.pagination.offset + lifecycle.pagination.returned,
                        lifecycle.pagination.total
                      )}`
                    : "0"}{" "}
                  of {lifecycle.pagination.total} matching orders
                </Text>
                <InlineStack gap="200">
                  <Button
                    disabled={!lifecycle.pagination.has_previous || lifecycleLoading}
                    onClick={() =>
                      setOrderOffset((current) => Math.max(0, current - orderPageLimit))
                    }
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={!lifecycle.pagination.has_next || lifecycleLoading}
                    onClick={() => setOrderOffset((current) => current + orderPageLimit)}
                  >
                    Next
                  </Button>
                </InlineStack>
              </InlineStack>
            ) : null}
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Manual review queue</Text>
                <Text as="p" tone="subdued">
                  {data.summary.open} open · {data.summary.critical + data.summary.high} high priority
                </Text>
              </BlockStack>
              <Button
                disabled={
                  !reviewFilter &&
                  reviewSeverityFilter === "all" &&
                  reviewReasonFilter === "all"
                }
                onClick={() => {
                  setReviewFilter("");
                  setReviewSeverityFilter("all");
                  setReviewReasonFilter("all");
                }}
              >
                Clear review filters
              </Button>
            </InlineStack>
            <InlineGrid columns={{ xs: 1, md: 3 }} gap="200">
              <TextField
                label="Search reviews"
                value={reviewFilter}
                onChange={setReviewFilter}
                autoComplete="off"
                placeholder="Order, reason, review ID..."
                clearButton
                onClearButtonClick={() => setReviewFilter("")}
              />
              <Select
                label="Review severity"
                value={reviewSeverityFilter}
                options={[
                  { label: "All severities", value: "all" },
                  ...data.facets.severities.map((severity) => ({
                    label: statusLabel(severity),
                    value: severity
                  }))
                ]}
                onChange={setReviewSeverityFilter}
              />
              <Select
                label="Review reason"
                value={reviewReasonFilter}
                options={[
                  { label: "All reasons", value: "all" },
                  ...data.facets.reasons.map((reason) => ({
                    label: statusLabel(reason),
                    value: reason
                  }))
                ]}
                onChange={setReviewReasonFilter}
              />
            </InlineGrid>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
              <Select
                label="Sort reviews by"
                value={reviewSort}
                options={[
                  { label: "Priority", value: "priority" },
                  { label: "Last updated", value: "updated" },
                  { label: "Created", value: "created" },
                  { label: "Order", value: "order" },
                  { label: "Reason", value: "reason" }
                ]}
                onChange={(value) => {
                  setReviewSort(value);
                  setReviewSortDirection(
                    value === "updated" || value === "created" ? "desc" : "asc"
                  );
                }}
              />
              <Select
                label="Review sort direction"
                value={reviewSortDirection}
                options={
                  reviewSort === "priority"
                    ? [
                        { label: "Most urgent first", value: "asc" },
                        { label: "Least urgent first", value: "desc" }
                      ]
                    : [
                        { label: "Ascending", value: "asc" },
                        { label: "Descending", value: "desc" }
                      ]
                }
                onChange={setReviewSortDirection}
              />
            </InlineGrid>
          <DataTable
            columnContentTypes={["text", "text", "text", "text", "text", "text", "text"]}
            headings={["Order", "Reason", "Issues", "Severity", "Status", "Updated", "Action"]}
            rows={
              data.items.length
                ? data.items.map((item) => [
                    item.order_id || "Unknown",
                    <BlockStack gap="100">
                      <Text as="span">{statusLabel(item.reason_code)}</Text>
                      <Text as="span" tone="subdued">{item.review_key}</Text>
                    </BlockStack>,
                    issueSummary(item),
                    <StatusBadge status={item.severity} />,
                    <StatusBadge status={item.status} />,
                    formatReviewDate(item.updated_at),
                    <InlineStack gap="200">
                      {isSupplierReconciliationReview(item) ? (
                        <Button
                          loading={
                            reconciliationBusy &&
                            reconciliationReview?.review_key === item.review_key
                          }
                          disabled={
                            !item.order_id ||
                            Boolean(reviewActionBusy) ||
                            reconciliationBusy ||
                            Boolean(reconciliationReview)
                          }
                          onClick={() => openSupplierReconciliation(item)}
                        >
                          Record reconciliation
                        </Button>
                      ) : (
                        <>
                      <Button
                        loading={reviewActionBusy === `${item.review_key}:approve`}
                        disabled={!item.order_id || Boolean(reviewActionBusy)}
                        onClick={async () => {
                          setReviewActionBusy(`${item.review_key}:approve`);
                          const result = await apiFetch(`/shopify/admin/reviews/${encodeURIComponent(item.review_key)}/approve-submit`, {
                            method: "POST",
                            body: JSON.stringify({ comment: "Approved for submit-if-ready from admin" })
                          });
                          setReviewActionBusy(null);
                          const submission = (result.data as { submission?: { submitted?: boolean; reason?: string } } | null)?.submission;
                          setMessage(result.error || (submission?.submitted ? "Submission completed" : `Submit check recorded: ${submission?.reason || "not submitted"}`));
                          refresh();
                        }}
                      >
                        Approve submit
                      </Button>
                      <Button
                        loading={reviewActionBusy === `${item.review_key}:local`}
                        disabled={!item.order_id || Boolean(reviewActionBusy)}
                        onClick={async () => {
                          setReviewActionBusy(`${item.review_key}:local`);
                          try {
                            const result = await apiFetch(`/shopify/admin/reviews/${encodeURIComponent(item.review_key)}/fulfill-locally`, {
                              method: "POST",
                              body: JSON.stringify({ comment: "Selected for local fulfilment from admin" })
                            });
                            setMessage(result.error || "Order marked for local fulfilment");
                            refresh();
                          } finally {
                            setReviewActionBusy(null);
                          }
                        }}
                      >
                        Fulfil locally
                      </Button>
                      <Button
                        loading={reviewActionBusy === `${item.review_key}:retry`}
                        disabled={Boolean(reviewActionBusy)}
                        onClick={async () => {
                          setReviewActionBusy(`${item.review_key}:retry`);
                          const result = await apiFetch(`/shopify/admin/reviews/${encodeURIComponent(item.review_key)}/resend`, {
                            method: "POST",
                            body: JSON.stringify({ comment: "Retry requested from admin" })
                          });
                          setReviewActionBusy(null);
                          setMessage(result.error || "Retry action recorded");
                          refresh();
                        }}
                      >
                        Retry
                      </Button>
                      <Button
                        tone="critical"
                        loading={reviewActionBusy === `${item.review_key}:close`}
                        disabled={Boolean(reviewActionBusy)}
                        onClick={async () => {
                          setReviewActionBusy(`${item.review_key}:close`);
                          const result = await apiFetch(`/shopify/admin/reviews/${encodeURIComponent(item.review_key)}/reject-close`, {
                            method: "POST",
                            body: JSON.stringify({ comment: "Closed from admin" })
                          });
                          setReviewActionBusy(null);
                          setMessage(result.error || "Review closed");
                          refresh();
                        }}
                      >
                        Close
                      </Button>
                        </>
                      )}
                    </InlineStack>
                  ])
                : [[loading ? "Loading review queue..." : "No reviews match these filters", "", "", "", "", "", ""]]
            }
          />
            {reconciliationReview ? (
              <Box
                background="bg-surface-secondary"
                borderColor="border"
                borderWidth="025"
                borderRadius="300"
                padding="400"
              >
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    Record supplier reconciliation
                  </Text>
                  <Text as="p">
                    {reconciliationReview.reason_code === "tradeprint_refund_requires_manual_review"
                      ? `Confirm that refund action for order ${reconciliationReview.order_id || "Unknown"} has been reconciled outside PrintConnect.`
                      : `Confirm that every supplier item for order ${reconciliationReview.order_id || "Unknown"} has been cancelled.`}
                  </Text>
                  <Banner tone="warning">
                    This records an outcome already verified with the supplier. It does not contact Tradeprint or issue a Shopify refund.
                  </Banner>
                  <TextField
                    label="Reconciliation note"
                    value={reconciliationReason}
                    onChange={setReconciliationReason}
                    autoComplete="off"
                    multiline={3}
                    maxLength={500}
                    showCharacterCount
                    helpText="Describe the supplier confirmation, reference, or refund action checked."
                  />
                  <Checkbox
                    label={
                      reconciliationReview.reason_code === "tradeprint_refund_requires_manual_review"
                        ? "I confirm the supplier refund or credit has been reconciled."
                        : "I confirm every supplier item has been cancelled."
                    }
                    checked={reconciliationConfirmed}
                    onChange={setReconciliationConfirmed}
                  />
                  <InlineStack gap="200">
                    <Button
                      variant="primary"
                      loading={reconciliationBusy}
                      disabled={
                        reconciliationBusy ||
                        reconciliationReason.trim().length < 8 ||
                        !reconciliationConfirmed
                      }
                      onClick={recordSupplierReconciliation}
                    >
                      Record confirmed outcome
                    </Button>
                    <Button disabled={reconciliationBusy} onClick={closeSupplierReconciliation}>
                      Cancel
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Box>
            ) : null}
            <InlineStack align="space-between" blockAlign="center">
              <Text as="p" tone="subdued">
                Showing{" "}
                {data.pagination.total
                  ? `${data.pagination.offset + 1}-${Math.min(
                      data.pagination.offset + data.pagination.returned,
                      data.pagination.total
                    )}`
                  : "0"}{" "}
                of {data.pagination.total} matching reviews
              </Text>
              <InlineStack gap="200">
                <Button
                  disabled={!data.pagination.has_previous || loading}
                  onClick={() =>
                    setReviewOffset((current) => Math.max(0, current - reviewPageLimit))
                  }
                >
                  Previous reviews
                </Button>
                <Button
                  disabled={!data.pagination.has_next || loading}
                  onClick={() => setReviewOffset((current) => current + reviewPageLimit)}
                >
                  Next reviews
                </Button>
              </InlineStack>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function JobsView() {
  const [jobFilter, setJobFilter] = useState("");
  const [jobStatusFilter, setJobStatusFilter] = useState("all");
  const [jobTypeFilter, setJobTypeFilter] = useState("all");
  const [jobSort, setJobSort] = useState("updated");
  const [jobSortDirection, setJobSortDirection] = useState("desc");
  const [jobOffset, setJobOffset] = useState(0);
  const debouncedJobFilter = useDebouncedValue(jobFilter, 250);
  const jobPageLimit = 25;
  const jobPath = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(jobPageLimit),
      offset: String(jobOffset),
      sort: jobSort,
      direction: jobSortDirection
    });
    if (debouncedJobFilter.trim()) params.set("search", debouncedJobFilter.trim());
    if (jobStatusFilter !== "all") params.set("status", jobStatusFilter);
    if (jobTypeFilter !== "all") params.set("job_type", jobTypeFilter);
    return `/admin/job-center/sync-jobs?${params.toString()}`;
  }, [
    debouncedJobFilter,
    jobOffset,
    jobSort,
    jobSortDirection,
    jobStatusFilter,
    jobTypeFilter
  ]);
  const { data, loading, error, reload } = useApiData<SyncJobsResponse>(
    jobPath,
    {
      jobs: [],
      facets: { statuses: [], job_types: [] },
      pagination: {
        offset: 0,
        limit: jobPageLimit,
        returned: 0,
        total: 0,
        has_previous: false,
        has_next: false
      }
    }
  );
  const {
    data: drift,
    loading: driftLoading,
    error: driftError,
    reload: reloadDrift
  } = useApiData<CatalogDriftResponse>("/shopify/admin/catalog-drift", {
    events: [],
    summary: {
      total: 0,
      occurrences: 0,
      action_required: 0,
      by_severity: {},
      by_type: {}
    }
  });
  const [message, setMessage] = useState<string | null>(null);
  const [retryingJob, setRetryingJob] = useState<string | null>(null);
  const [reviewingDrift, setReviewingDrift] = useState<string | null>(null);
  useEffect(() => {
    setJobOffset(0);
  }, [
    debouncedJobFilter,
    jobSort,
    jobSortDirection,
    jobStatusFilter,
    jobTypeFilter
  ]);
  const formatJobType = (jobType: string) => jobType.replace(/_/g, " ");
  const formatJobDate = (value?: string) => {
    if (!value) return "Unknown";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  const checkpointLabel = (job: SyncJob) => {
    const checkpoint = job.checkpoint || {};
    const staleWorkerRecovery = Boolean(checkpoint.stale_worker_recovery);
    const staleAfterMinutes = checkpoint.stale_after_minutes;
    const processed = checkpoint.processed;
    const total = checkpoint.total;
    const currentProductId = checkpoint.current_product_id;
    const queue = checkpoint.queue;
    const error = checkpoint.error;
    if (error) return String(error);
    if (staleWorkerRecovery) {
      return `Stale worker recovered${staleAfterMinutes ? ` after ${staleAfterMinutes}m` : ""}`;
    }
    if (currentProductId) return `Processing ${currentProductId}`;
    if (processed !== undefined && total !== undefined) return `${processed}/${total}`;
    if (queue) return String(queue);
    return "Pending";
  };
  const retryLabel = (job: SyncJob) =>
    job.checkpoint?.stale_worker_recovery ? "Retry job" : "Retry failures";
  const failureReason = (job: SyncJob) => {
    const failure = job.failure_summary;
    if (failure?.first_error_code === "missing_supplier_cost") {
      return (
        "Live Tradeprint pricing did not return a verified supplier cost "
        + "for this product and option set."
      );
    }
    if (failure?.first_error_detail) return String(failure.first_error_detail);
    if (failure?.first_error_code) return String(failure.first_error_code);
    if (job.checkpoint?.stale_worker_recovery) {
      return "The worker stopped or did not complete before stale recovery. Product creation was not reached.";
    }
    if (job.status === "failed" && job.checkpoint?.error) return String(job.checkpoint.error);
    return job.status === "failed" ? "No item-level failure has been recorded." : "None";
  };
  const failureCount = (job: SyncJob) => Number(job.failure_summary?.failed_count || 0);
  const driftSeverityLabel = (event: CatalogDriftEvent) => {
    if (event.severity === "high") return "Blocked";
    if (event.severity === "warning") return "Needs attention";
    return "Review";
  };
  const driftGuidance = (event: CatalogDriftEvent) => {
    if (event.event_type === "selected_product_unresolvable") {
      return "Re-import the catalogue or disable this product before publishing.";
    }
    if (event.event_type === "selected_product_missing_from_live_catalog") {
      return "Confirm the supplier product still exists, then refresh or disable it.";
    }
    if (event.event_type === "live_catalog_unavailable") {
      return "Restore the supplier connection and retry the product refresh.";
    }
    if (event.event_type === "using_imported_selection_fallback") {
      return "Verify the imported selection before relying on fallback catalogue data.";
    }
    return "Review the product mapping and rerun the affected refresh.";
  };
  const checkpointEntries = (job: SyncJob) => Object.entries(job.checkpoint || {}).map(([key, value]) => [
    key,
    typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? String(value) : JSON.stringify(value)
  ]);
  const retryJob = async (job: SyncJob) => {
    setRetryingJob(job.id);
    const result = await apiFetch(`/admin/job-center/sync-jobs/${encodeURIComponent(job.id)}/retry-failures`, {
      method: "POST",
      body: JSON.stringify({})
    });
    setRetryingJob(null);
    setMessage(result.error || `Retry queued for ${job.id}`);
    void reload();
  };
  const reviewDrift = async (
    event: CatalogDriftEvent,
    action: "resolved" | "dismissed"
  ) => {
    const actionKey = `${event.id}:${action}`;
    setReviewingDrift(actionKey);
    const result = await apiFetch<{ changed: boolean }>(
      `/shopify/admin/catalog-drift/${event.id}/resolution`,
      {
        method: "POST",
        body: JSON.stringify({ action })
      }
    );
    setReviewingDrift(null);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    setMessage(
      action === "resolved"
        ? `Catalogue drift resolved for ${event.product_name}`
        : `Catalogue drift dismissed for ${event.product_name}`
    );
    await reloadDrift();
  };
  const reloadAll = useCallback(async () => {
    await Promise.all([reload(), reloadDrift()]);
  }, [reload, reloadDrift]);
  return (
    <Page title="Jobs" subtitle="Product sync, order submission, and catalog drift recovery" primaryAction={{ content: "Refresh", onAction: reloadAll }}>
      <BlockStack gap="400">
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        <DismissibleNotice
          message={error ? `Job centre could not be loaded: ${error}` : null}
        />
        <DismissibleNotice
          message={driftError ? `Catalogue drift could not be loaded: ${driftError}` : null}
        />
        {loading || driftLoading ? <LoadingState label="Loading jobs and catalogue drift" /> : null}
        {drift.summary.action_required ? (
          <Banner tone="warning">
            {drift.summary.action_required} catalogue drift event{drift.summary.action_required === 1 ? "" : "s"} {drift.summary.action_required === 1 ? "needs" : "need"} review before the affected products are published again.
          </Banner>
        ) : null}
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Catalogue drift
                </Text>
                <Text as="p" tone="subdued">
                  Supplier catalogue differences detected during product refresh.
                </Text>
              </BlockStack>
              <Text as="p" tone="subdued">
                {drift.summary.total} open issue{drift.summary.total === 1 ? "" : "s"}
                {drift.summary.occurrences > drift.summary.total
                  ? ` · ${drift.summary.occurrences} detections`
                  : ""}
              </Text>
            </InlineStack>
            {drift.events.length ? (
              <div className="admin-record-list">
                {drift.events.map((event) => (
                  <div className="admin-record" key={event.id}>
                    <InlineStack align="space-between" blockAlign="start" gap="300">
                      <BlockStack gap="100">
                        <Text as="h3" variant="headingSm">
                          {event.product_name === "catalog" ? "Supplier catalogue" : event.product_name}
                        </Text>
                        <Text as="p" tone="subdued">
                          {statusLabel(event.event_type)}
                        </Text>
                      </BlockStack>
                      <StatusBadge status={driftSeverityLabel(event)} />
                    </InlineStack>
                    <Text as="p">{driftGuidance(event)}</Text>
                    <Text as="p" tone="subdued">
                      {event.occurrence_count > 1
                        ? `Detected ${event.occurrence_count} times · first ${formatJobDate(event.created_at || undefined)} · latest ${formatJobDate(event.last_seen_at || undefined)}`
                        : `Detected ${formatJobDate(event.created_at || undefined)}`}
                    </Text>
                    <InlineStack gap="200">
                      <Button
                        loading={reviewingDrift === `${event.id}:resolved`}
                        disabled={Boolean(reviewingDrift)}
                        onClick={() => void reviewDrift(event, "resolved")}
                      >
                        Resolve
                      </Button>
                      <Button
                        loading={reviewingDrift === `${event.id}:dismissed`}
                        disabled={Boolean(reviewingDrift)}
                        onClick={() => void reviewDrift(event, "dismissed")}
                      >
                        Dismiss
                      </Button>
                    </InlineStack>
                  </div>
                ))}
              </div>
            ) : (
              <Text as="p" tone="subdued">
                No catalogue drift has been detected.
              </Text>
            )}
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Background jobs
                </Text>
                <Text as="p" tone="subdued">
                  Search product refreshes, supplier publishing and order-processing work.
                </Text>
              </BlockStack>
              <Button
                disabled={
                  !jobFilter &&
                  jobStatusFilter === "all" &&
                  jobTypeFilter === "all" &&
                  jobSort === "updated" &&
                  jobSortDirection === "desc"
                }
                onClick={() => {
                  setJobFilter("");
                  setJobStatusFilter("all");
                  setJobTypeFilter("all");
                  setJobSort("updated");
                  setJobSortDirection("desc");
                }}
              >
                Clear filters
              </Button>
            </InlineStack>
            <InlineGrid columns={{ xs: 1, md: 5 }} gap="200">
              <TextField
                label="Search jobs"
                value={jobFilter}
                onChange={setJobFilter}
                autoComplete="off"
                placeholder="Job ID, type or status"
                clearButton
                onClearButtonClick={() => setJobFilter("")}
              />
              <Select
                label="Status"
                options={[
                  { label: "All statuses", value: "all" },
                  ...data.facets.statuses.map((status) => ({
                    label: statusLabel(status),
                    value: status
                  }))
                ]}
                value={jobStatusFilter}
                onChange={setJobStatusFilter}
              />
              <Select
                label="Job type"
                options={[
                  { label: "All job types", value: "all" },
                  ...data.facets.job_types.map((jobType) => ({
                    label: statusLabel(jobType),
                    value: jobType
                  }))
                ]}
                value={jobTypeFilter}
                onChange={setJobTypeFilter}
              />
              <Select
                label="Sort jobs by"
                options={[
                  { label: "Last updated", value: "updated" },
                  { label: "Job type", value: "type" },
                  { label: "Status", value: "status" },
                  { label: "Job ID", value: "id" }
                ]}
                value={jobSort}
                onChange={setJobSort}
              />
              <Select
                label="Sort direction"
                options={[
                  { label: "Newest or Z–A", value: "desc" },
                  { label: "Oldest or A–Z", value: "asc" }
                ]}
                value={jobSortDirection}
                onChange={setJobSortDirection}
              />
            </InlineGrid>
          {data.jobs.length ? (
            <div className="admin-record-list">
              {data.jobs.map((job) => (
                <div key={job.id} className="admin-record">
                  <InlineStack align="space-between" blockAlign="start" gap="300">
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="h3" variant="headingSm">
                          {formatJobType(job.job_type)}
                        </Text>
                        <StatusBadge status={job.status} />
                      </InlineStack>
                      <Text as="p" tone="subdued">
                        {checkpointLabel(job)}
                      </Text>
                    </BlockStack>
                    <Button
                      loading={retryingJob === job.id}
                      disabled={job.status !== "failed" || Boolean(retryingJob)}
                      onClick={() => void retryJob(job)}
                    >
                      {retryLabel(job)}
                    </Button>
                  </InlineStack>
                  <div className="admin-record__meta">
                    <span>
                      <strong>Updated</strong>
                      {formatJobDate(job.updated_at)}
                    </span>
                    <span>
                      <strong>Progress</strong>
                      {checkpointLabel(job)}
                    </span>
                    <span>
                      <strong>Status</strong>
                      {job.status}
                    </span>
                    <span>
                      <strong>Failures</strong>
                      {failureCount(job) ? `${failureCount(job)} item failure${failureCount(job) === 1 ? "" : "s"}` : "No item failures"}
                    </span>
                    <span>
                      <strong>Reason</strong>
                      {failureReason(job)}
                    </span>
                  </div>
                  <details className="admin-accordion">
                    <summary>Status and failure details</summary>
                    <div className="admin-accordion__body">
                      <BlockStack gap="300">
                        <BlockStack gap="100">
                          <Text as="h4" variant="headingSm">
                            Job status
                          </Text>
                          <div className="admin-record__meta">
                            <span>
                              <strong>Job ID</strong>
                              {job.id}
                            </span>
                            <span>
                              <strong>Type</strong>
                              {job.job_type}
                            </span>
                            <span>
                              <strong>Status</strong>
                              {job.status}
                            </span>
                            <span>
                              <strong>Updated</strong>
                              {job.updated_at || "Unknown"}
                            </span>
                          </div>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text as="h4" variant="headingSm">
                            Failure information
                          </Text>
                          <div className="admin-record__meta">
                            <span>
                              <strong>Failure count</strong>
                              {failureCount(job)}
                            </span>
                            <span>
                              <strong>First failed item</strong>
                              {job.failure_summary?.first_failed_item || "None"}
                            </span>
                            <span>
                              <strong>First error code</strong>
                              {job.failure_summary?.first_error_code || "None"}
                            </span>
                            <span>
                              <strong>Failure reason</strong>
                              {failureReason(job)}
                            </span>
                          </div>
                          {job.failure_summary?.items?.length ? (
                            <BlockStack gap="200">
                              {job.failure_summary.items.map((item) => (
                                <Box key={`${job.id}-${item.item_key || item.updated_at}`} padding="300" background="bg-surface-secondary" borderRadius="200">
                                  <BlockStack gap="100">
                                    <Text as="p">
                                      <strong>{item.item_key || "Unknown item"}</strong>
                                    </Text>
                                    <Text as="p" tone="subdued">
                                      {item.error_code || "no_error_code"} · {item.updated_at || "unknown time"}
                                    </Text>
                                    <Text as="p">{item.error_detail || "No error detail recorded."}</Text>
                                  </BlockStack>
                                </Box>
                              ))}
                            </BlockStack>
                          ) : (
                            <Text as="p" tone="subdued">
                              No product-level failure rows have been recorded for this job.
                            </Text>
                          )}
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text as="h4" variant="headingSm">
                            Worker checkpoint
                          </Text>
                          {checkpointEntries(job).length ? (
                            <div className="admin-record__meta">
                              {checkpointEntries(job).map(([key, value]) => (
                                <span key={`${job.id}-${key}`}>
                                  <strong>{key}</strong>
                                  {value}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <Text as="p" tone="subdued">
                              No checkpoint recorded.
                            </Text>
                          )}
                        </BlockStack>
                      </BlockStack>
                    </div>
                  </details>
                </div>
              ))}
            </div>
          ) : (
            <Text as="p" tone="subdued">
              {loading
                ? "Loading jobs..."
                : data.pagination.total
                  ? "No jobs match these filters."
                  : "No background jobs have run yet."}
            </Text>
          )}
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <Text as="p" tone="subdued">
                Showing{" "}
                {data.pagination.total
                  ? `${data.pagination.offset + 1}-${Math.min(
                      data.pagination.offset + data.pagination.returned,
                      data.pagination.total
                    )}`
                  : "0"}{" "}
                of {data.pagination.total} matching jobs
              </Text>
              <InlineStack gap="200">
                <Button
                  disabled={!data.pagination.has_previous || loading}
                  onClick={() =>
                    setJobOffset((current) => Math.max(0, current - jobPageLimit))
                  }
                >
                  Previous
                </Button>
                <Button
                  disabled={!data.pagination.has_next || loading}
                  onClick={() => setJobOffset((current) => current + jobPageLimit)}
                >
                  Next
                </Button>
              </InlineStack>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function OperationsView() {
  const { data, loading, error, reload } = useApiData<OperationsHealth>("/shopify/admin/operations/health", {
    generated_at: "",
    store_key: "default",
    status: "healthy",
    score: 100,
    recommendations: [],
    supplier_controls: {
      tradeprint: {
        supplier_key: "tradeprint",
        enabled: true,
        submit_only_manual: false,
        degraded: false
      }
    },
    suppliers: [],
    queues: [],
    recent_jobs: [],
    manual_reviews: [],
    risks: [],
    risk_summary: {
      action_required: 0,
      items: 0,
      by_severity: {},
      by_category: {},
      margin_threshold_percent: 20,
      quote_recovery_warning_count: 5
    },
    kpis: {},
    retention: {
      store_key: "default",
      dry_run: true,
      generated_at: "",
      policy: {},
      cutoffs: {},
      counts: {},
      total: 0
    },
    audit_events: [],
    alerting: {
      configured: false,
      destination: "platform_dashboard",
      external_webhook_configured: false
    }
  });
  const [message, setMessage] = useState<string | null>(null);
  const [testAlertBusy, setTestAlertBusy] = useState(false);
  const [diagnosticsBusy, setDiagnosticsBusy] = useState(false);
  const statusMessage =
    data.status === "critical"
      ? "Operational intervention is required before scaling traffic."
      : data.status === "warning"
        ? "Some operational checks need attention."
        : "Core operations are healthy.";
  const tradeprintControls = data.supplier_controls.tradeprint;
  const supplierMode = !tradeprintControls.enabled
    ? "Paused"
    : tradeprintControls.submit_only_manual
      ? "Manual only"
      : tradeprintControls.degraded
        ? "Degraded"
        : "Auto submit";
  const totalQueued = data.queues.reduce((sum, queue) => sum + Number(queue.count || 0), 0);
  const failedJobs = data.queues.reduce(
    (sum, queue) => sum + Number(queue.failed_job_registry_count || 0),
    0
  );
  const sendTestAlert = async () => {
    setTestAlertBusy(true);
    const result = await apiFetch("/shopify/admin/operations/alerts/test", {
      method: "POST",
      body: JSON.stringify({})
    });
    setTestAlertBusy(false);
    setMessage(result.error || "Operator test alert delivered");
    void reload();
  };
  const downloadDiagnostics = async () => {
    setDiagnosticsBusy(true);
    const result = await apiFetch<{ diagnostics: Record<string, unknown> }>(
      "/shopify/admin/operations/diagnostics/export",
      { method: "POST", body: JSON.stringify({}) }
    );
    setDiagnosticsBusy(false);
    if (result.error || !result.data?.diagnostics) {
      setMessage(result.error || "Diagnostics export failed");
      return;
    }
    const blob = new Blob([JSON.stringify(result.data.diagnostics, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `printconnect-diagnostics-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setMessage("Sanitized diagnostics exported");
  };
  return (
    <Page title="Operations" subtitle="Platform health, queues, supplier controls, and operator actions" primaryAction={{ content: "Refresh", onAction: reload }}>
      <BlockStack gap="400">
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        <DismissibleNotice
          message={error ? `Operations health could not be loaded: ${error}` : null}
        />
        {loading ? <LoadingState label="Loading operational health" /> : null}
        {!loading ? <Banner tone={data.status === "critical" ? "critical" : data.status === "warning" ? "warning" : "success"}>
          {statusMessage}
        </Banner> : null}
        <InlineGrid columns={{ xs: 1, md: 4 }} gap="400">
          <MetricCard label="Health score" value={`${data.score}/100`} trend={data.status} />
          <MetricCard label="Queued jobs" value={String(totalQueued)} trend="RQ" />
          <MetricCard label="Risk items" value={String(data.risk_summary.action_required)} trend="prioritized" />
          <MetricCard label="Supplier mode" value={supplierMode} trend="Tradeprint" />
        </InlineGrid>
        <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" gap="300">
                  <Text as="h2" variant="headingMd">
                    Recommended actions
                  </Text>
                  <StatusBadge status={data.status} />
                </InlineStack>
                <List>
                  {(data.recommendations || []).map((item) => (
                    <List.Item key={item}>{item}</List.Item>
                  ))}
                </List>
              </BlockStack>
            </Box>
          </Card>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Tradeprint controls
                </Text>
                <List>
                  <List.Item>{tradeprintControls.enabled ? "Supplier submission enabled" : "Supplier submission paused"}</List.Item>
                  <List.Item>{tradeprintControls.submit_only_manual ? "Manual approval required" : "Automatic submission permitted"}</List.Item>
                  <List.Item>{tradeprintControls.degraded ? "Supplier marked degraded" : "Supplier not marked degraded"}</List.Item>
                  {tradeprintControls.reason ? <List.Item>{tradeprintControls.reason}</List.Item> : null}
                </List>
                <TradeprintControlActions
                  controls={tradeprintControls}
                  onUpdated={reload}
                />
              </BlockStack>
            </Box>
          </Card>
        </InlineGrid>
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <Text as="h2" variant="headingMd">
                  Prioritized risks
                </Text>
                <Text as="span" tone={data.risk_summary.action_required ? "critical" : "subdued"}>
                  {data.risk_summary.action_required} actions
                </Text>
              </InlineStack>
              <DataTable
                columnContentTypes={["text", "text", "numeric", "text"]}
                headings={["Risk", "Priority", "Items", "Next action"]}
                rows={
                  data.risks.length
                    ? data.risks.map((risk) => [
                        <BlockStack gap="100">
                          <Text as="span" fontWeight="semibold">{risk.title}</Text>
                          <Text as="span" tone="subdued">{risk.detail}</Text>
                        </BlockStack>,
                        <StatusBadge status={risk.severity} />,
                        risk.count,
                        <Button onClick={() => navigateToAdmin(risk.action_path)}>
                          {risk.action_label}
                        </Button>
                      ])
                    : [["No operational risks detected", "", 0, ""]]
                }
              />
            </BlockStack>
          </Box>
        </Card>
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <Text as="h2" variant="headingMd">
                  Queue health
                </Text>
                <Text as="span" tone={failedJobs ? "critical" : "subdued"}>
                  {failedJobs} failed jobs
                </Text>
              </InlineStack>
              <DataTable
                columnContentTypes={["text", "text", "numeric", "numeric", "numeric"]}
                headings={["Queue", "Status", "Queued", "Failed", "Started"]}
                rows={
                  data.queues.length
                    ? data.queues.map((queue) => [
                        queue.queue,
                        <StatusBadge status={queue.status} />,
                        queue.count ?? "-",
                        queue.failed_job_registry_count ?? "-",
                        queue.started_job_registry_count ?? "-"
                      ])
                    : [["No queues reported", "", "", "", ""]]
                }
              />
            </BlockStack>
          </Box>
        </Card>
        <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Open manual reviews
                </Text>
                <DataTable
                  columnContentTypes={["text", "text", "text"]}
                  headings={["Review", "Reason", "Severity"]}
                  rows={
                    data.manual_reviews.length
                      ? data.manual_reviews.slice(0, 6).map((review) => [
                          review.review_key,
                          review.reason_code,
                          <StatusBadge status={review.severity} />
                        ])
                      : [["No open reviews", "", ""]]
                  }
                />
              </BlockStack>
            </Box>
          </Card>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Retention
                </Text>
                <Text as="p" tone="subdued">
                  {data.retention.total} records are eligible for cleanup.
                </Text>
                <RetentionCleanupActions
                  preview={data.retention}
                  onUpdated={reload}
                />
                <InlineStack align="space-between" blockAlign="center" gap="300">
                  <Text as="p" tone={data.alerting.configured ? "subdued" : "critical"}>
                    {data.alerting.configured
                      ? "Operational alerts are retained in the global platform dashboard."
                      : "Enable the global platform dashboard to retain operational alerts."}
                  </Text>
                  <Button
                    loading={testAlertBusy}
                    disabled={testAlertBusy || !data.alerting.configured}
                    onClick={sendTestAlert}
                  >
                    Send test alert
                  </Button>
                  <Button
                    loading={diagnosticsBusy}
                    disabled={diagnosticsBusy}
                    onClick={downloadDiagnostics}
                  >
                    Export diagnostics
                  </Button>
                </InlineStack>
                <DataTable
                  columnContentTypes={["text", "numeric"]}
                  headings={["Dataset", "Eligible"]}
                  rows={
                    Object.entries(data.retention.counts).length
                      ? Object.entries(data.retention.counts).map(([key, count]) => [
                          key.replace(/_/g, " "),
                          count
                        ])
                      : [["No eligible records", "0"]]
                  }
                />
              </BlockStack>
            </Box>
          </Card>
        </InlineGrid>
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Recent audit events
              </Text>
              <DataTable
                columnContentTypes={["text", "text", "text", "text"]}
                headings={["Time", "Action", "Entity", "Actor"]}
                rows={
                  data.audit_events.length
                    ? data.audit_events.slice(0, 6).map((event) => [
                        event.created_at || "Unknown",
                        event.action,
                        `${event.entity_type}:${event.entity_id}`,
                        event.actor_id || "system"
                      ])
                    : [["No recent audit events", "", "", ""]]
                }
              />
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
}

function funnelMetricLabel(value: string): string {
  return statusLabel(value.replace(/:/g, "_"));
}

function formatFunnelRate(value: number): string {
  if (!Number.isFinite(value)) return "Not available";
  const percentage = Math.abs(value) <= 1 ? value * 100 : value;
  return `${percentage.toFixed(1)}%`;
}

function formatCommercialMoney(value: string | null | undefined, currency?: string | null): string {
  const amount = Number(value);
  if (!currency || !Number.isFinite(amount)) return "Not available";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return "Not available";
  }
}

function AnalyticsView() {
  const { data, loading, error, reload } = useApiData<Kpis>("/analytics/kpis", {});
  const {
    data: retention,
    loading: retentionLoading,
    error: retentionError,
    reload: reloadRetention
  } = useApiData<RetentionStatus>("/shopify/admin/operations/retention", {
    store_key: "default",
    dry_run: true,
    generated_at: "",
    policy: {},
    cutoffs: {},
    counts: {},
    total: 0
  });
  const [funnelWindow, setFunnelWindow] = useState("30");
  const [productPerformanceQuery, setProductPerformanceQuery] = useState("");
  const [productPerformanceSort, setProductPerformanceSort] =
    useState("paid_orders");
  const {
    data: funnel,
    loading: funnelLoading,
    error: funnelError,
    reload: reloadFunnel
  } = useApiData<AnalyticsFunnelResponse>(
    `/shopify/admin/analytics/funnel?days=${funnelWindow}`,
    {
      window_days: 30,
      generated_at: "",
      total_events: 0,
      unique_journeys: 0,
      counts: {},
      rates: {},
      by_day: [],
      product_performance: [],
      product_count: 0
    }
  );
  const [message, setMessage] = useState<string | null>(null);
  const [syncingStatus, setSyncingStatus] = useState(false);
  const counts = data.counts || {};
  const commercial = data.commercial || {};
  const totalOrders = Object.values(counts).reduce((sum, value) => sum + Number(value || 0), 0);
  const revenueOrders = Number(commercial.orders_with_revenue || 0);
  const coveredOrders = Number(commercial.orders_with_verified_cost || 0);
  const missingRevenueOrders = Number(commercial.orders_missing_revenue || 0);
  const missingCostOrders = Number(commercial.orders_missing_verified_cost || 0);
  const commercialMetrics = [
    {
      label: "Tracked revenue",
      value: formatCommercialMoney(commercial.tracked_revenue, commercial.currency),
      trend: `${revenueOrders} order${revenueOrders === 1 ? "" : "s"}`
    },
    {
      label: "Revenue with verified cost",
      value: coveredOrders
        ? formatCommercialMoney(commercial.covered_revenue, commercial.currency)
        : "Not available",
      trend: `${coveredOrders} covered`
    },
    {
      label: "Verified supplier cost",
      value: coveredOrders
        ? formatCommercialMoney(commercial.verified_supplier_cost, commercial.currency)
        : "Not available",
      trend: "covered orders only"
    },
    {
      label: "Covered gross profit",
      value: formatCommercialMoney(commercial.covered_gross_profit, commercial.currency),
      trend: "revenue minus verified cost"
    },
    {
      label: "Covered gross margin",
      value:
        commercial.covered_gross_margin_percent != null
          ? `${Number(commercial.covered_gross_margin_percent).toFixed(1)}%`
          : "Not available",
      trend: `${Number(commercial.cost_coverage_percent || 0).toFixed(1)}% order coverage`
    }
  ];
  const funnelCounts = Object.entries(funnel.counts || {});
  const funnelRates = Object.entries(funnel.rates || {});
  const funnelDayKeys = useMemo(() => {
    const keys = new Set<string>(Object.keys(funnel.counts || {}));
    funnel.by_day.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== "date") keys.add(key);
      });
    });
    return [...keys];
  }, [funnel.by_day, funnel.counts]);
  const visibleFunnelDays = funnel.by_day.slice(-14);
  const visibleProductPerformance = useMemo(() => {
    const query = productPerformanceQuery.trim().toLocaleLowerCase("en-GB");
    return [...(funnel.product_performance || [])]
      .filter((product) => {
        if (!query) return true;
        return (
          product.product_name.toLocaleLowerCase("en-GB").includes(query) ||
          product.product_id.toLocaleLowerCase("en-GB").includes(query)
        );
      })
      .sort((left, right) => {
        const metric = productPerformanceSort as
          | "paid_orders"
          | "checkouts"
          | "baskets"
          | "quotes"
          | "views";
        return (
          Number(right[metric] || 0) - Number(left[metric] || 0) ||
          left.product_name.localeCompare(right.product_name, "en-GB") ||
          left.product_id.localeCompare(right.product_id, "en-GB")
        );
      });
  }, [
    funnel.product_performance,
    productPerformanceQuery,
    productPerformanceSort
  ]);
  const metrics = useMemo(
    () => [
      { label: "Health score", value: `${data.operational_health_score ?? 100}/100`, trend: "operations" },
      { label: "Tracked orders", value: String(totalOrders), trend: "live" },
      { label: "Manual review", value: String(data.manual_review_count || 0), trend: "hold" },
      { label: "Stale orders", value: String(data.stale_order_count || 0), trend: "watch" },
      { label: "Quote mismatches", value: String(data.reconciliation_mismatch_count || 0), trend: "watch" },
      { label: "Fulfilment gaps", value: String(data.fulfillment_writeback_pending_count || 0), trend: "tracking" }
    ],
    [
      counts,
      data.fulfillment_writeback_pending_count,
      data.manual_review_count,
      data.operational_health_score,
      data.reconciliation_mismatch_count,
      data.stale_order_count,
      totalOrders
    ]
  );
  return (
    <Page
      title="Analytics"
      subtitle="Profit, operations, and product performance"
      primaryAction={{
        content: syncingStatus ? "Running status sync" : "Run status sync",
        disabled: syncingStatus,
        onAction: async () => {
          setSyncingStatus(true);
          const result = await apiFetch<{ synced?: number; mismatches?: number }>("/sync/tradeprint-statuses/run?limit=50", {
            method: "POST"
          });
          setSyncingStatus(false);
          setMessage(result.error || `Status sync complete: ${result.data?.synced ?? 0} synced, ${result.data?.mismatches ?? 0} mismatches`);
          void reload();
          void reloadRetention();
        }
      }}
    >
      <BlockStack gap="400">
        <AdminMessageBanner message={message} onDismiss={() => setMessage(null)} />
        <DismissibleNotice
          message={error ? `Analytics could not be loaded: ${error}` : null}
        />
        <DismissibleNotice
          message={retentionError ? `Retention status could not be loaded: ${retentionError}` : null}
        />
        {loading || retentionLoading ? <LoadingState label="Loading analytics" /> : null}
        {!loading ? (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Commercial performance
                  </Text>
                  <Text as="p" tone="subdued">
                    Revenue is taken from routed print lines. Profit and margin are shown only
                    where the order has a complete, verified supplier quote.
                  </Text>
                </BlockStack>
                {commercial.currency_status === "mixed" ? (
                  <Banner tone="warning">
                    Commercial totals are not combined because tracked orders use multiple
                    currencies ({(commercial.currencies || []).join(", ")}).
                  </Banner>
                ) : null}
                {commercial.currency_status === "unknown" || !revenueOrders ? (
                  <Banner tone="info">
                    No complete routed revenue with a known currency is available yet.
                  </Banner>
                ) : null}
                {commercial.currency_status === "single" && revenueOrders ? (
                  <>
                    <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                      {commercialMetrics.map((metric) => (
                        <MetricCard key={metric.label} {...metric} />
                      ))}
                    </InlineGrid>
                    {missingCostOrders || missingRevenueOrders ? (
                      <Banner tone={missingCostOrders ? "warning" : "info"}>
                        Gross profit covers {coveredOrders} of {revenueOrders} revenue-bearing
                        orders ({Number(commercial.cost_coverage_percent || 0).toFixed(1)}%).
                        {missingCostOrders
                          ? ` ${missingCostOrders} order${missingCostOrders === 1 ? "" : "s"} do not have a complete verified supplier cost.`
                          : ""}
                        {missingRevenueOrders
                          ? ` ${missingRevenueOrders} tracked order${missingRevenueOrders === 1 ? "" : "s"} do not have complete routed revenue and are excluded from the totals.`
                          : ""}
                      </Banner>
                    ) : (
                      <Banner tone="success">
                        Every revenue-bearing order in this view has complete verified cost
                        coverage.
                      </Banner>
                    )}
                  </>
                ) : null}
              </BlockStack>
            </Box>
          </Card>
        ) : null}
        <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
          {metrics.map((metric) => (
            <MetricCard key={metric.label} {...metric} />
          ))}
        </InlineGrid>
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="end" gap="300">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Storefront journey funnel
                  </Text>
                  <Text as="p" tone="subdued">
                    Aggregated journey counts only. This panel does not expose customer names,
                    contact details, order contents, IP addresses, or raw session identifiers.
                  </Text>
                </BlockStack>
                <InlineStack gap="200" blockAlign="end">
                  <Select
                    label="Funnel window"
                    options={[
                      { label: "Last 7 days", value: "7" },
                      { label: "Last 30 days", value: "30" },
                      { label: "Last 90 days", value: "90" }
                    ]}
                    value={funnelWindow}
                    onChange={setFunnelWindow}
                  />
                  <Button onClick={reloadFunnel} disabled={funnelLoading}>
                    Refresh
                  </Button>
                </InlineStack>
              </InlineStack>
              {funnelLoading ? (
                <LoadingState label={`Loading ${funnelWindow}-day storefront funnel`} />
              ) : null}
              <DismissibleNotice
                message={funnelError ? `Funnel analytics could not be loaded: ${funnelError}` : null}
                actionLabel="Retry"
                onAction={reloadFunnel}
              />
              {!funnelLoading && !funnelError && !funnel.total_events ? (
                <Banner tone="info">
                  No privacy-safe storefront journey events were recorded in this period.
                </Banner>
              ) : null}
              {!funnelLoading && !funnelError && funnel.total_events ? (
                <>
                  <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                    <MetricCard
                      label="Total events"
                      value={Number(funnel.total_events).toLocaleString("en-GB")}
                      trend={`${funnel.window_days} days`}
                    />
                    <MetricCard
                      label="Unique journeys"
                      value={Number(funnel.unique_journeys).toLocaleString("en-GB")}
                      trend="anonymous"
                    />
                    {funnelCounts.map(([key, value]) => (
                      <MetricCard
                        key={key}
                        label={funnelMetricLabel(key)}
                        value={Number(value || 0).toLocaleString("en-GB")}
                        trend="events"
                      />
                    ))}
                  </InlineGrid>
                  {funnelRates.length ? (
                    <dl className="admin-funnel-rates" aria-label="Funnel conversion rates">
                      {funnelRates.map(([key, value]) => (
                        <div key={key}>
                          <dt>{funnelMetricLabel(key)}</dt>
                          <dd>{formatFunnelRate(Number(value))}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {visibleFunnelDays.length && funnelDayKeys.length ? (
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center" gap="200">
                        <Text as="h3" variant="headingSm">
                          Daily activity
                        </Text>
                        <Text as="p" tone="subdued">
                          {funnel.by_day.length > visibleFunnelDays.length
                            ? `Latest ${visibleFunnelDays.length} of ${funnel.by_day.length} days`
                            : `${visibleFunnelDays.length} day${visibleFunnelDays.length === 1 ? "" : "s"}`}
                        </Text>
                      </InlineStack>
                      <DataTable
                        columnContentTypes={[
                          "text",
                          ...funnelDayKeys.map(() => "numeric" as const)
                        ]}
                        headings={[
                          "Date",
                          ...funnelDayKeys.map((key) => funnelMetricLabel(key))
                        ]}
                        rows={visibleFunnelDays.map((row) => [
                          row.date,
                          ...funnelDayKeys.map((key) => Number(row[key] || 0))
                        ])}
                      />
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">
                      No daily funnel rows are available for this period.
                    </Text>
                  )}
                  {funnel.generated_at ? (
                    <Text as="p" tone="subdued">
                      Aggregated {new Date(funnel.generated_at).toLocaleString("en-GB")}.
                    </Text>
                  ) : null}
                </>
              ) : null}
            </BlockStack>
          </Box>
        </Card>
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="end" gap="300">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Product performance
                  </Text>
                  <Text as="p" tone="subdued">
                    Anonymous journey counts for the selected funnel window. Paid orders are
                    counted from trusted Shopify webhooks; no customer or session details are
                    shown.
                  </Text>
                </BlockStack>
                <InlineStack gap="200" blockAlign="end">
                  <TextField
                    label="Find product"
                    value={productPerformanceQuery}
                    autoComplete="off"
                    clearButton
                    onClearButtonClick={() => setProductPerformanceQuery("")}
                    onChange={setProductPerformanceQuery}
                  />
                  <Select
                    label="Sort products by"
                    options={[
                      { label: "Paid orders", value: "paid_orders" },
                      { label: "Checkouts", value: "checkouts" },
                      { label: "Baskets", value: "baskets" },
                      { label: "Quotes", value: "quotes" },
                      { label: "Views", value: "views" }
                    ]}
                    value={productPerformanceSort}
                    onChange={setProductPerformanceSort}
                  />
                </InlineStack>
              </InlineStack>
              {funnelLoading ? (
                <LoadingState label="Loading product performance" />
              ) : null}
              {!funnelLoading && !funnelError && !funnel.product_count ? (
                <Banner tone="info">
                  No product-level activity was recorded in this period.
                </Banner>
              ) : null}
              {!funnelLoading &&
              !funnelError &&
              funnel.product_count &&
              !visibleProductPerformance.length ? (
                <Banner tone="info">No products match this search.</Banner>
              ) : null}
              {!funnelLoading && !funnelError && visibleProductPerformance.length ? (
                <>
                  <DataTable
                    columnContentTypes={[
                      "text",
                      "numeric",
                      "numeric",
                      "numeric",
                      "numeric",
                      "numeric",
                      "numeric",
                      "numeric"
                    ]}
                    headings={[
                      "Product",
                      "Views",
                      "Quotes",
                      "Baskets",
                      "Checkouts",
                      "Paid",
                      "Quote rate",
                      "Basket rate"
                    ]}
                    rows={visibleProductPerformance.map((product) => [
                      product.product_name === product.product_id
                        ? product.product_name
                        : `${product.product_name} (${product.product_id})`,
                      product.views,
                      product.quotes,
                      product.baskets,
                      product.checkouts,
                      product.paid_orders,
                      formatFunnelRate(product.quote_rate),
                      formatFunnelRate(product.basket_rate)
                    ])}
                  />
                  <Text as="p" tone="subdued">
                    Showing {visibleProductPerformance.length} of {funnel.product_count}{" "}
                    product{funnel.product_count === 1 ? "" : "s"}. The API returns at
                    most the leading 25 products for this window.
                  </Text>
                </>
              ) : null}
            </BlockStack>
          </Box>
        </Card>
        <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Stale order watchlist
                </Text>
                <List>
                  {(data.stale_orders || []).slice(0, 5).map((order) => (
                    <List.Item key={String(order.shopify_order_id || order.tradeprint_order_id)}>
                      {String(order.shopify_order_id || "Unknown order")} is {String(order.status || "unknown")}
                    </List.Item>
                  ))}
                  {data.stale_orders?.length ? null : <List.Item>No stale supplier orders detected.</List.Item>}
                </List>
              </BlockStack>
            </Box>
          </Card>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Fulfilment writeback
                </Text>
                <List>
                  {(data.fulfillment_writeback_pending || []).slice(0, 5).map((order) => (
                    <List.Item key={String(order.shopify_order_id || order.tradeprint_order_id)}>
                      {String(order.shopify_order_id || "Unknown order")} needs Shopify tracking writeback
                    </List.Item>
                  ))}
                  {data.fulfillment_writeback_pending?.length ? null : <List.Item>No tracking writeback gaps detected.</List.Item>}
                </List>
              </BlockStack>
            </Box>
          </Card>
        </InlineGrid>
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Data retention
                </Text>
                <Text as="p" tone="subdued">
                  {retention.total} expired operational records are eligible for cleanup.
                </Text>
              </BlockStack>
              <RetentionCleanupActions
                preview={retention}
                onUpdated={() => {
                  void reloadRetention();
                  void reload();
                }}
              />
              <DataTable
                columnContentTypes={["text", "numeric", "text"]}
                headings={["Dataset", "Eligible", "Retention"]}
                rows={Object.entries(retention.counts).map(([key, count]) => [
                  key.replace(/_/g, " "),
                  count,
                  `${retention.policy[`${key.replace(/s$/, "")}_retention_days`] ?? "-"} days`
                ])}
              />
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
}

function AuditView() {
  const [auditSearch, setAuditSearch] = useState("");
  const [auditAction, setAuditAction] = useState("all");
  const [auditEntityType, setAuditEntityType] = useState("all");
  const [auditActor, setAuditActor] = useState("all");
  const [auditSort, setAuditSort] = useState("created_at");
  const [auditDirection, setAuditDirection] = useState("desc");
  const [auditOffset, setAuditOffset] = useState(0);
  const debouncedAuditSearch = useDebouncedValue(auditSearch, 250);
  const auditPageLimit = 50;
  const auditPath = useMemo(() => {
    const params = new URLSearchParams({
      sort: auditSort,
      direction: auditDirection,
      limit: String(auditPageLimit),
      offset: String(auditOffset)
    });
    if (debouncedAuditSearch.trim()) params.set("search", debouncedAuditSearch.trim());
    if (auditAction !== "all") params.set("action", auditAction);
    if (auditEntityType !== "all") params.set("entity_type", auditEntityType);
    if (auditActor !== "all") params.set("actor_id", auditActor);
    return `/shopify/admin/audit?${params.toString()}`;
  }, [
    auditAction,
    auditActor,
    auditDirection,
    auditEntityType,
    auditOffset,
    auditSort,
    debouncedAuditSearch
  ]);
  const { data, loading, error, reload } = useApiData<AuditResponse>(auditPath, {
    items: [],
    facets: { actions: [], entity_types: [], actors: [] },
    pagination: {
      offset: 0,
      limit: auditPageLimit,
      returned: 0,
      total: 0,
      has_previous: false,
      has_next: false
    }
  });
  const summarizePayload = (event: AuditEvent) => {
    const payload = event.payload || {};
    const result = payload.result as { synced?: number; mismatches?: number } | undefined;
    const submission = payload.submission as { submitted?: boolean; reason?: string } | undefined;
    if (result) return `${result.synced ?? 0} synced, ${result.mismatches ?? 0} mismatches`;
    if (submission) return submission.submitted ? "submitted" : submission.reason || "not submitted";
    const keys = Object.keys(payload).slice(0, 3).map(statusLabel);
    return keys.join(", ") || "Recorded";
  };
  const formatAuditDate = (value?: string | null) => {
    if (!value) return "Unknown";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("en-GB");
  };
  const clearAuditFilters = () => {
    setAuditSearch("");
    setAuditAction("all");
    setAuditEntityType("all");
    setAuditActor("all");
    setAuditSort("created_at");
    setAuditDirection("desc");
    setAuditOffset(0);
  };
  const pageStart = data.pagination.total ? data.pagination.offset + 1 : 0;
  const pageEnd = Math.min(
    data.pagination.total,
    data.pagination.offset + data.pagination.returned
  );
  return (
    <Page title="Audit" subtitle="Recent admin actions and operational events" primaryAction={{ content: "Refresh", onAction: reload }}>
      <BlockStack gap="400">
        <DismissibleNotice
          message={error ? `Audit events could not be loaded: ${error}` : null}
        />
        {loading ? <LoadingState label="Loading audit events" /> : null}
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <Text as="p" tone="subdued">
                Showing {pageStart}-{pageEnd} of {data.pagination.total} matching events
              </Text>
              <Button
                disabled={
                  !auditSearch &&
                  auditAction === "all" &&
                  auditEntityType === "all" &&
                  auditActor === "all" &&
                  auditSort === "created_at" &&
                  auditDirection === "desc"
                }
                onClick={clearAuditFilters}
              >
                Clear filters
              </Button>
            </InlineStack>
            <InlineGrid columns={{ xs: 1, md: 3 }} gap="200">
              <TextField
                label="Search audit events"
                value={auditSearch}
                onChange={(value) => {
                  setAuditSearch(value);
                  setAuditOffset(0);
                }}
                autoComplete="off"
                placeholder="Actor, action, entity or ID"
                clearButton
                onClearButtonClick={() => {
                  setAuditSearch("");
                  setAuditOffset(0);
                }}
              />
              <Select
                label="Action"
                options={[
                  { label: "All actions", value: "all" },
                  ...data.facets.actions.map((value) => ({
                    label: statusLabel(value),
                    value
                  }))
                ]}
                value={auditAction}
                onChange={(value) => {
                  setAuditAction(value);
                  setAuditOffset(0);
                }}
              />
              <Select
                label="Entity type"
                options={[
                  { label: "All entity types", value: "all" },
                  ...data.facets.entity_types.map((value) => ({
                    label: statusLabel(value),
                    value
                  }))
                ]}
                value={auditEntityType}
                onChange={(value) => {
                  setAuditEntityType(value);
                  setAuditOffset(0);
                }}
              />
              <Select
                label="Actor"
                options={[
                  { label: "All actors", value: "all" },
                  ...data.facets.actors.map((value) => ({ label: value, value }))
                ]}
                value={auditActor}
                onChange={(value) => {
                  setAuditActor(value);
                  setAuditOffset(0);
                }}
              />
              <Select
                label="Sort audit events by"
                options={[
                  { label: "Time", value: "created_at" },
                  { label: "Action", value: "action" },
                  { label: "Actor", value: "actor" },
                  { label: "Entity type", value: "entity" }
                ]}
                value={auditSort}
                onChange={(value) => {
                  setAuditSort(value);
                  setAuditOffset(0);
                }}
              />
              <Select
                label="Sort direction"
                options={[
                  { label: "Newest or descending", value: "desc" },
                  { label: "Oldest or ascending", value: "asc" }
                ]}
                value={auditDirection}
                onChange={(value) => {
                  setAuditDirection(value);
                  setAuditOffset(0);
                }}
              />
            </InlineGrid>
            <DataTable
              columnContentTypes={["text", "text", "text", "text", "text", "text"]}
              headings={["Time", "Actor", "Action", "Entity", "Result", "Role"]}
              rows={
                data.items.length
                  ? data.items.map((event) => [
                      formatAuditDate(event.created_at),
                      event.actor_id || "System",
                      statusLabel(event.action),
                      `${statusLabel(event.entity_type)}: ${event.entity_id}`,
                      summarizePayload(event),
                      statusLabel(event.actor_role || "unknown")
                    ])
                  : [[
                      loading
                        ? "Loading audit events..."
                        : data.pagination.total
                          ? "No audit events are on this page"
                          : "No audit events match these filters",
                      "",
                      "",
                      "",
                      "",
                      ""
                    ]]
              }
            />
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <Text as="p" tone="subdued">
                Page {Math.floor(data.pagination.offset / data.pagination.limit) + 1}
              </Text>
              <InlineStack gap="200">
                <Button
                  disabled={!data.pagination.has_previous || loading}
                  onClick={() =>
                    setAuditOffset((current) => Math.max(0, current - auditPageLimit))
                  }
                >
                  Previous
                </Button>
                <Button
                  disabled={!data.pagination.has_next || loading}
                  onClick={() => setAuditOffset((current) => current + auditPageLimit)}
                >
                  Next
                </Button>
              </InlineStack>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function readinessForProduct(product: AdminProduct) {
  if (product.catalog_validation) {
    const validation = product.catalog_validation;
    const missing = validation.summary?.length
      ? validation.summary
      : [...(validation.issues || []), ...(validation.warnings || [])].map((issue) => issue.message);
    return {
      missing,
      score: validation.score,
      status: validation.status === "ready" ? "Ready" : validation.status === "blocked" ? "Blocked" : "Needs attention"
    };
  }
  const selection = product.selection;
  const config = (selection?.parity_config || {}) as Record<string, unknown>;
  const missing = [];
  if (!selection?.included) missing.push("not selected");
  if (!selection?.margin_percent) missing.push("margin");
  if (!config.artwork) missing.push("artwork policy");
  if (!config.delivery) missing.push("delivery policy");
  if (selection?.validation_issues?.length) {
    missing.push(...selection.validation_issues.map((issue) => `validation: ${issue}`));
  }
  return {
    missing,
    score: missing.length ? 50 : 100,
    status: missing.length ? "Needs attention" : "Ready"
  };
}

function formatGaps(gaps: string[]) {
  if (gaps.length <= 3) return gaps.join(", ");
  return `${gaps.slice(0, 3).join(", ")} +${gaps.length - 3} more`;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);
}
